/* do not manipulate this file manually. */
export interface GraphQLUploadType {
  type: string;
  listDepth: number;
}
export interface GraphQLUploadPlan {
  variables: Record<string, GraphQLUploadType>;
  inputs: Record<string, Record<string, GraphQLUploadType>>;
}
export interface GraphQLQuery {
  id: string;
  op: string;
  query: string;
  file?: boolean;
  uploads?: GraphQLUploadPlan;
  deprecations?: string[];
}
export const copilotChatMessageFragment = `fragment CopilotChatMessage on ChatMessage {
  id
  role
  content
  attachments
  streamObjects {
    type
    textDelta
    toolCallId
    toolName
    args
    result
    durationMs
  }
  createdAt
  context {
    docId
    searchMode
    items {
      id
      kind
      label
      subtitle
      source
      priority
      display {
        title
        subtitle
        icon
        preview
        unavailable
        unavailableReason
      }
      refs
      payload
    }
  }
  sources {
    items {
      id
      kind
      label
      display {
        title
        subtitle
        icon
        preview
        unavailable
        unavailableReason
      }
      refs
      payload
    }
  }
  messageType
  toolData {
    approvalId
    toolCall {
      name
      arguments
    }
    description
    confirmationCopy
    approved
    reason
    includesAgentMode
    includesTargetMode
    goalTurn {
      goalId
      kind
      stepLabel
    }
    toolCallId
    toolName
    success
    result
    error
    toolResults {
      toolCallId
      toolName
      displayName
      success
      result
      durationMs
    }
  }
}`;
export const copilotChatHistoryFragment = `fragment CopilotChatHistory on CopilotHistories {
  sessionId
  agentStatus
  workspaceId
  docId
  parentSessionId
  promptName
  model
  reasoningMode
  optionalModels
  action
  pinned
  title
  tokens
  messages {
    ...CopilotChatMessage
  }
  createdAt
  updatedAt
}`;
export const paginatedCopilotChatsFragment = `fragment PaginatedCopilotChats on PaginatedCopilotHistoriesType {
  pageInfo {
    hasNextPage
    hasPreviousPage
    startCursor
    endCursor
  }
  edges {
    cursor
    node {
      ...CopilotChatHistory
    }
  }
}`;
export const credentialsRequirementsFragment = `fragment CredentialsRequirements on CredentialsRequirementType {
  password {
    ...PasswordLimits
  }
}`;
export const passwordLimitsFragment = `fragment PasswordLimits on PasswordLimitsType {
  minLength
  maxLength
}`;
export const permissionGraphConnectionFieldsFragment = `fragment PermissionGraphConnectionFields on PermissionGraphConnectionType {
  rightsEpoch
  edges {
    cursor
    sortKey
    node {
      __typename
      ... on PermissionGraphNodeType {
        id
        kind
        label
        workspaceId
        metadata
      }
      ... on PermissionGraphRelationshipType {
        id
        relationshipType
        sourceType
        sourceId
        targetType
        targetId
        role
        provenance {
          sourceType
          sourceId
          viaGroupId
          viaFolderId
          inherited
          effective
        }
        updatedAt
      }
    }
  }
  pageInfo {
    hasNextPage
    endCursor
    token
    resetRequired
  }
  diagnostics {
    bounded
    maxPageSize
    maxSubtreeDepth
    projectionStatus
    resetReason
  }
}`;
export const generateUserAccessTokenMutation = {
  id: 'generateUserAccessTokenMutation' as const,
  op: 'generateUserAccessToken',
  query: `mutation generateUserAccessToken($input: GenerateAccessTokenInput!) {
  generateUserAccessToken(input: $input) {
    id
    name
    token
    createdAt
    expiresAt
  }
}`,
};

export const listUserAccessTokensQuery = {
  id: 'listUserAccessTokensQuery' as const,
  op: 'listUserAccessTokens',
  query: `query listUserAccessTokens {
  revealedAccessTokens {
    id
    name
    createdAt
    expiresAt
    token
  }
}`,
};

export const revokeUserAccessTokenMutation = {
  id: 'revokeUserAccessTokenMutation' as const,
  op: 'revokeUserAccessToken',
  query: `mutation revokeUserAccessToken($id: String!) {
  revokeUserAccessToken(id: $id)
}`,
};

export const accountDeletionPreviewQuery = {
  id: 'accountDeletionPreviewQuery' as const,
  op: 'accountDeletionPreview',
  query: `query accountDeletionPreview {
  accountDeletionPreview {
    activeSubscriptions {
      id
      plan
      periodEnd
      amount
      currency
    }
    ownedWorkspaces {
      id
      name
      memberCount
    }
    pendingInvoices {
      id
      amount
      currency
      dueDate
    }
    blockedReason
    gracePeriodDays
    scheduledFinalizeDate
  }
}`,
};

export const accountDeletionStatusQuery = {
  id: 'accountDeletionStatusQuery' as const,
  op: 'accountDeletionStatus',
  query: `query accountDeletionStatus {
  accountDeletionStatus {
    scheduled
    scheduledAt
    finalizesAt
    reactivatable
    gracePeriodDays
  }
}`,
};

export const addTenantMemberMutation = {
  id: 'addTenantMemberMutation' as const,
  op: 'addTenantMember',
  query: `mutation addTenantMember($tenantId: ID!, $userId: String!, $role: TenantRoleEnum!) {
  addTenantMember(tenantId: $tenantId, userId: $userId, role: $role) {
    id
    tenantId
    userId
    role
    createdAt
  }
}`,
};

export const adminWorkspaceStatsQuery = {
  id: 'adminWorkspaceStatsQuery' as const,
  op: 'adminWorkspaceStats',
  query: `query adminWorkspaceStats {
  adminWorkspaceStats {
    totalWorkspaces
    avgMembersPerWorkspace
    totalStorageUsed
    storageBreakdown {
      blobBytes
      documentSnapshotBytes
      documentHistoryBytes
      databaseBytes
      derivedBytes
      mailSearchIndexBytes
    }
    avgStoragePerWorkspace
    top10ByStorage {
      id
      name
      ownerName
      plan
      memberCount
      totalStorageBytes
      storageBreakdown {
        blobBytes
        documentSnapshotBytes
        documentHistoryBytes
        databaseBytes
        derivedBytes
        mailSearchIndexBytes
      }
      createdAt
    }
    top10ByMembers {
      id
      name
      ownerName
      plan
      memberCount
      totalStorageBytes
      storageBreakdown {
        blobBytes
        documentSnapshotBytes
        documentHistoryBytes
        databaseBytes
        derivedBytes
        mailSearchIndexBytes
      }
      createdAt
    }
    top10ByAIUsage {
      id
      name
      ownerName
      plan
      memberCount
      totalStorageBytes
      storageBreakdown {
        blobBytes
        documentSnapshotBytes
        documentHistoryBytes
        databaseBytes
        derivedBytes
        mailSearchIndexBytes
      }
      createdAt
    }
  }
}`,
};

export const adminAiProfitabilityQuery = {
  id: 'adminAiProfitabilityQuery' as const,
  op: 'adminAiProfitability',
  query: `query adminAiProfitability($input: AIProfitabilityQueryInput!) {
  adminAIProfitability(input: $input) {
    overview {
      providerCostMicroUsd
      providerCostMicroEur
      providerCostMicroCredits
      customerCostBasisMicroCredits
      customerChargedMicroCredits
      marginMicroCredits
      lineCount
      eurComplete
    }
    managedBackendRows {
      tenantId
      workspaceId
      managedBackendId
      provider
      model
      cacheScope
      cacheMode
      cacheWindowSeconds
      totals {
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
        marginMicroCredits
        lineCount
        eurComplete
      }
    }
    providerModelRows {
      tenantId
      workspaceId
      managedBackendId
      provider
      model
      cacheScope
      cacheMode
      cacheWindowSeconds
      totals {
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
        marginMicroCredits
        lineCount
        eurComplete
      }
    }
    tenantRows {
      tenantId
      workspaceId
      managedBackendId
      provider
      model
      cacheScope
      cacheMode
      cacheWindowSeconds
      totals {
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
        marginMicroCredits
        lineCount
        eurComplete
      }
    }
    workspaceRows {
      tenantId
      workspaceId
      managedBackendId
      provider
      model
      cacheScope
      cacheMode
      cacheWindowSeconds
      totals {
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
        marginMicroCredits
        lineCount
        eurComplete
      }
    }
    cacheWindowRows {
      tenantId
      workspaceId
      managedBackendId
      provider
      model
      cacheScope
      cacheMode
      cacheWindowSeconds
      totals {
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
        marginMicroCredits
        lineCount
        eurComplete
      }
    }
    alerts {
      kind
      severity
      scope
      cause
      provider
      model
      tenantId
      workspaceId
      cacheScope
      cacheMode
      cacheWindowSeconds
      marginMicroCredits
    }
    requestTrace {
      rows {
        id
        aiUsageLogId
        creditTransactionId
        createdAt
        tenantId
        workspaceId
        userId
        triggerId
        provider
        model
        capability
        providerUsed
        modelUsed
        routingPolicy
        managedBackendId
        costKind
        costSource
        cacheScope
        cacheMode
        cacheKey
        cacheWindowSeconds
        providerCostMicroUsd
        providerCostMicroEur
        providerCostMicroCredits
        customerCostBasisMicroCredits
        customerChargedMicroCredits
      }
      nextOffset
    }
  }
}`,
};

export const adminAssignPlanMutation = {
  id: 'adminAssignPlanMutation' as const,
  op: 'adminAssignPlan',
  query: `mutation adminAssignPlan($userId: String!, $plan: String!) {
  adminAssignPlan(userId: $userId, plan: $plan) {
    plan
    status
    recurring
    start
    createdAt
    updatedAt
    provider
  }
}`,
};

export const adminAuditLogFacetsQuery = {
  id: 'adminAuditLogFacetsQuery' as const,
  op: 'adminAuditLogFacets',
  query: `query adminAuditLogFacets {
  adminAuditLogFacets {
    actions
    targetTypes
    admins {
      id
      name
      email
    }
  }
}`,
};

export const adminAuditLogsQuery = {
  id: 'adminAuditLogsQuery' as const,
  op: 'adminAuditLogs',
  query: `query adminAuditLogs($filter: AuditLogFilterInput!) {
  adminAuditLogs(filter: $filter) {
    entries {
      id
      adminId
      action
      targetType
      targetId
      details
      createdAt
      admin {
        id
        name
        email
      }
    }
    total
  }
}`,
};

export const adminBillingStatsQuery = {
  id: 'adminBillingStatsQuery' as const,
  op: 'adminBillingStats',
  query: `query adminBillingStats {
  adminBillingStats {
    totalActive
    totalSubscriptions
    planDistribution {
      plan
      count
    }
  }
}`,
};

export const adminTenantCachePerformanceQuery = {
  id: 'adminTenantCachePerformanceQuery' as const,
  op: 'adminTenantCachePerformance',
  query: `query adminTenantCachePerformance($tenantId: String!, $input: CachePerformanceQueryInput!) {
  adminTenantCachePerformance(tenantId: $tenantId, input: $input) {
    buckets {
      bucket
      paidMicro
      savedMicro
      chargedMicro
      hitRate
      hits
      misses
      callCount
    }
    totals {
      paidMicro
      savedMicro
      chargedMicro
      uncachedHypotheticalMicro
      hitRateAvg
      callCount
    }
  }
}`,
};

export const adminSystemCachePerformanceQuery = {
  id: 'adminSystemCachePerformanceQuery' as const,
  op: 'adminSystemCachePerformance',
  query: `query adminSystemCachePerformance($input: CachePerformanceQueryInput!) {
  adminSystemCachePerformance(input: $input) {
    buckets {
      bucket
      paidMicro
      savedMicro
      chargedMicro
      hitRate
      hits
      misses
      callCount
    }
    totals {
      paidMicro
      savedMicro
      chargedMicro
      uncachedHypotheticalMicro
      hitRateAvg
      callCount
    }
  }
}`,
};

export const adminGetUserSubscriptionsQuery = {
  id: 'adminGetUserSubscriptionsQuery' as const,
  op: 'adminGetUserSubscriptions',
  query: `query adminGetUserSubscriptions($userId: String!) {
  adminGetUserSubscriptions(userId: $userId) {
    plan
    recurring
    status
    start
    end
    nextBillAt
    canceledAt
    createdAt
    provider
  }
}`,
};

export const adminListSubscriptionsQuery = {
  id: 'adminListSubscriptionsQuery' as const,
  op: 'adminListSubscriptions',
  query: `query adminListSubscriptions($skip: Int, $take: Int, $planFilter: String, $statusFilter: SubscriptionStatus) {
  adminListSubscriptions(
    skip: $skip
    take: $take
    planFilter: $planFilter
    statusFilter: $statusFilter
  ) {
    plan
    recurring
    status
    start
    end
    canceledAt
    createdAt
    userEmail
    userName
    provider
  }
}`,
};

export const adminPermissionGraphDiagnosticsQuery = {
  id: 'adminPermissionGraphDiagnosticsQuery' as const,
  op: 'adminPermissionGraphDiagnostics',
  query: `query adminPermissionGraphDiagnostics($workspaceId: String!, $sampleDocs: Int!) {
  permissionGraphProjectionDiagnostics(
    workspaceId: $workspaceId
    sampleDocs: $sampleDocs
  ) {
    rightsEpoch
    sampledDocs
    sampledPrincipals
    truncated
    findings {
      kind
      docId
      userId
      sourceId
    }
  }
}`,
};

export const adminPermissionGraphQuery = {
  id: 'adminPermissionGraphQuery' as const,
  op: 'adminPermissionGraph',
  query: `query adminPermissionGraph($workspaceId: String!, $isRoots: Boolean!, $isFolderSubtree: Boolean!, $isRelationships: Boolean!, $isResourceSubjects: Boolean!, $isSubjectResources: Boolean!, $isChanges: Boolean!, $filter: PermissionGraphFilterInput, $folderId: String!, $depth: Int!, $subjectId: String!, $subjectKind: String!, $resourceId: String!, $resourceKind: String!, $permission: String, $sinceToken: String, $first: Int, $after: String) {
  permissionGraphRoots(workspaceId: $workspaceId, first: $first, after: $after) @include(if: $isRoots) {
    ...PermissionGraphConnectionFields
  }
  permissionFolderSubtree(
    workspaceId: $workspaceId
    folderId: $folderId
    depth: $depth
    permission: $permission
    first: $first
    after: $after
  ) @include(if: $isFolderSubtree) {
    ...PermissionGraphConnectionFields
  }
  permissionGraphRelationships(
    workspaceId: $workspaceId
    filter: $filter
    first: $first
    after: $after
  ) @include(if: $isRelationships) {
    ...PermissionGraphConnectionFields
  }
  permissionResourceSubjects(
    workspaceId: $workspaceId
    resourceId: $resourceId
    resourceKind: $resourceKind
    permission: $permission
    first: $first
    after: $after
  ) @include(if: $isResourceSubjects) {
    ...PermissionGraphConnectionFields
  }
  permissionSubjectResources(
    workspaceId: $workspaceId
    subjectId: $subjectId
    subjectKind: $subjectKind
    resourceKind: $resourceKind
    permission: $permission
    first: $first
    after: $after
  ) @include(if: $isSubjectResources) {
    ...PermissionGraphConnectionFields
  }
  permissionGraphChanges(
    workspaceId: $workspaceId
    sinceToken: $sinceToken
    first: $first
    after: $after
  ) @include(if: $isChanges) {
    ...PermissionGraphConnectionFields
  }
}
${permissionGraphConnectionFieldsFragment}`,
};

export const adminPlanDistributionQuery = {
  id: 'adminPlanDistributionQuery' as const,
  op: 'adminPlanDistribution',
  query: `query adminPlanDistribution {
  adminPlanDistribution {
    plan
    count
    percentage
  }
}`,
};

export const restoreWorkspaceMutation = {
  id: 'restoreWorkspaceMutation' as const,
  op: 'restoreWorkspace',
  query: `mutation restoreWorkspace($workspaceId: String!, $targetTimestamp: DateTime!) {
  restoreWorkspace(workspaceId: $workspaceId, targetTimestamp: $targetTimestamp) {
    id
    workspaceId
    adminUserId
    targetTimestamp
    status
    phase
    startedAt
    completedAt
    durationMs
    tablesRestored
    rowsDeleted
    rowsInserted
    blobsRestored
    errorMessage
    createdAt
  }
}`,
};

export const recoveryLogQuery = {
  id: 'recoveryLogQuery' as const,
  op: 'recoveryLog',
  query: `query recoveryLog($workspaceId: String!, $logId: String!) {
  recoveryLog(workspaceId: $workspaceId, logId: $logId) {
    id
    workspaceId
    adminUserId
    targetTimestamp
    status
    phase
    startedAt
    completedAt
    durationMs
    tablesRestored
    rowsDeleted
    rowsInserted
    blobsRestored
    errorMessage
    createdAt
  }
}`,
};

export const recoveryHistoryQuery = {
  id: 'recoveryHistoryQuery' as const,
  op: 'recoveryHistory',
  query: `query recoveryHistory($workspaceId: String!) {
  recoveryHistory(workspaceId: $workspaceId) {
    id
    workspaceId
    adminUserId
    targetTimestamp
    status
    phase
    startedAt
    completedAt
    durationMs
    tablesRestored
    rowsDeleted
    rowsInserted
    blobsRestored
    errorMessage
    createdAt
  }
}`,
};

export const recoveryTimeRangeQuery = {
  id: 'recoveryTimeRangeQuery' as const,
  op: 'recoveryTimeRange',
  query: `query recoveryTimeRange($workspaceId: String!) {
  recoveryTimeRange(workspaceId: $workspaceId) {
    earliest
    latest
  }
}`,
};

export const adminServerConfigQuery = {
  id: 'adminServerConfigQuery' as const,
  op: 'adminServerConfig',
  query: `query adminServerConfig {
  serverConfig {
    version
    baseUrl
    name
    features
    type
    initialized
    credentialsRequirement {
      ...CredentialsRequirements
    }
    availableUpgrade {
      changelog
      version
      publishedAt
      url
    }
    availableUserFeatures
  }
}
${passwordLimitsFragment}
${credentialsRequirementsFragment}`,
};

export const adminSignupTrendQuery = {
  id: 'adminSignupTrendQuery' as const,
  op: 'adminSignupTrend',
  query: `query adminSignupTrend($input: SignupTrendInput!) {
  adminSignupTrend(input: $input) {
    date
    count
  }
}`,
};

export const adminStripeProductsQuery = {
  id: 'adminStripeProductsQuery' as const,
  op: 'adminStripeProducts',
  query: `query adminStripeProducts {
  adminStripeProducts {
    priceId
    productName
    unitAmount
    currency
    recurring
    active
  }
}`,
};

export const adminTenantDetailQuery = {
  id: 'adminTenantDetailQuery' as const,
  op: 'adminTenantDetail',
  query: `query adminTenantDetail($tenantId: String!) {
  adminTenantDetail(tenantId: $tenantId) {
    id
    name
    slug
    billingEmail
    ownerName
    ownerEmail
    plan
    stripePlan
    planOverride
    memberCount
    workspaceCount
    totalStorageBytes
    createdAt
    databaseType
    databaseName
    planOverrideExpiresAt
    planOverrideSetBy
    planMemberLimit
    planMonthlyCreditGrant
    creditBalance
    creditEnabled
    creditSuspended
    members {
      userId
      name
      email
      role
      accepted
      joinedAt
    }
    workspaces {
      id
      name
      memberCount
      docCount
      totalStorageBytes
      createdAt
      seatAddonQuantity
      seatAddonBilled
    }
  }
}`,
};

export const adminTenantsCountQuery = {
  id: 'adminTenantsCountQuery' as const,
  op: 'adminTenantsCount',
  query: `query adminTenantsCount($filter: ListTenantInput!) {
  adminTenantsCount(filter: $filter)
}`,
};

export const adminTenantsQuery = {
  id: 'adminTenantsQuery' as const,
  op: 'adminTenants',
  query: `query adminTenants($filter: ListTenantInput!) {
  adminTenants(filter: $filter) {
    id
    name
    slug
    billingEmail
    ownerName
    ownerEmail
    plan
    stripePlan
    planOverride
    memberCount
    workspaceCount
    totalStorageBytes
    createdAt
    databaseType
    databaseName
  }
}`,
};

export const adminTopWorkspacesByActivityQuery = {
  id: 'adminTopWorkspacesByActivityQuery' as const,
  op: 'adminTopWorkspacesByActivity',
  query: `query adminTopWorkspacesByActivity($from: DateTime!, $to: DateTime!, $kind: String!) {
  adminTopWorkspacesByActivity(from: $from, to: $to, kind: $kind) {
    workspaceId
    kind
    unit
    amount
  }
}`,
};

export const adminTopWorkspacesQuery = {
  id: 'adminTopWorkspacesQuery' as const,
  op: 'adminTopWorkspaces',
  query: `query adminTopWorkspaces($from: DateTime!, $to: DateTime!) {
  adminTopWorkspaces(from: $from, to: $to) {
    workspaceId
    creditsMicro
  }
}`,
};

export const adminUserStatsQuery = {
  id: 'adminUserStatsQuery' as const,
  op: 'adminUserStats',
  query: `query adminUserStats {
  adminUserStats {
    totalUsers
    planCounts {
      plan
      count
      percentage
    }
    signupsThisWeek
    signupsThisMonth
    activeLastSevenDays
    activeLastThirtyDays
  }
}`,
};

export const adminWebhookHealthQuery = {
  id: 'adminWebhookHealthQuery' as const,
  op: 'adminWebhookHealth',
  query: `query adminWebhookHealth {
  adminWebhookHealth {
    totalEventsReceived
    totalErrors
    lastEventAt
    lastEventType
    lastErrorAt
    lastErrorMessage
  }
}`,
};

export const adminWorkerJobPlacesQuery = {
  id: 'adminWorkerJobPlacesQuery' as const,
  op: 'adminWorkerJobPlaces',
  query: `query adminWorkerJobPlaces {
  adminWorkerJobPlaces {
    placesPerPod
    pods
    held
    workspaceCount
    workspaces {
      workspaceId
      claim
      limit
      held
      borrowed
      tiers {
        tier
        waiting
        oldestWaitMs
        underClaim
      }
    }
  }
}`,
};

export const adminWorkspaceActivityCountersQuery = {
  id: 'adminWorkspaceActivityCountersQuery' as const,
  op: 'adminWorkspaceActivityCounters',
  query: `query adminWorkspaceActivityCounters($from: DateTime!, $to: DateTime!, $workspaceId: String!) {
  adminWorkspaceActivityCounters(from: $from, to: $to, workspaceId: $workspaceId) {
    workspaceId
    bucket
    retentionDays
    incomplete
    series {
      periodStart
      kind
      unit
      amount
    }
    queues {
      kind
      unit
      amount
      queue
      appBaseId
      route
    }
    routes {
      kind
      unit
      amount
      queue
      appBaseId
      route
    }
  }
}`,
};

export const adminWorkspaceActivityQuery = {
  id: 'adminWorkspaceActivityQuery' as const,
  op: 'adminWorkspaceActivity',
  query: `query adminWorkspaceActivity($from: DateTime!, $to: DateTime!, $workspaceId: String!) {
  adminWorkspaceActivity(from: $from, to: $to, workspaceId: $workspaceId) {
    workspaceId
    creditsMicro
    sources {
      source
      retentionDays
      retentionPolicy
      incomplete
      observedAt
      quantities {
        name
        value
        unit
        dimension
      }
      costs {
        name
        value
        unit
        dimension
      }
    }
    apps {
      appBaseId
      title
      deleted
      costKind
      quantity
      unit
      creditsMicro
    }
  }
}`,
};

export const adminWorkspaceDetailQuery = {
  id: 'adminWorkspaceDetailQuery' as const,
  op: 'adminWorkspaceDetail',
  query: `query adminWorkspaceDetail($workspaceId: String!) {
  adminWorkspaceDetail(workspaceId: $workspaceId) {
    id
    name
    ownerId
    ownerName
    ownerEmail
    plan
    memberCount
    docCount
    totalStorageBytes
    aiUsageThisMonth
    createdAt
    deletionScheduledAt
    members {
      userId
      name
      email
      role
      membershipKind
      accepted
      joinedAt
    }
    quotaUsage {
      members
      membersLimit
      storageBytes
      storageBytesLimit
      aiActions
      aiActionsLimit
    }
    aiKeyStatus {
      hasServerKey
      hasWorkspaceKey
      userKeysCount
    }
  }
}`,
};

export const adminWorkspacesQuery = {
  id: 'adminWorkspacesQuery' as const,
  op: 'adminWorkspaces',
  query: `query adminWorkspaces($filter: ListWorkspaceInput!) {
  adminWorkspaces(filter: $filter) {
    id
    name
    ownerId
    ownerName
    ownerEmail
    plan
    memberCount
    docCount
    totalStorageBytes
    aiUsageThisMonth
    createdAt
    deletionScheduledAt
  }
}`,
};

export const createChangePasswordUrlMutation = {
  id: 'createChangePasswordUrlMutation' as const,
  op: 'createChangePasswordUrl',
  query: `mutation createChangePasswordUrl($userId: String!) {
  createChangePasswordUrl(userId: $userId)
}`,
};

export const appConfigQuery = {
  id: 'appConfigQuery' as const,
  op: 'appConfig',
  query: `query appConfig {
  appConfig
}`,
};

export const getPromptsQuery = {
  id: 'getPromptsQuery' as const,
  op: 'getPrompts',
  query: `query getPrompts {
  listCopilotPrompts {
    name
    model
    action
    config {
      frequencyPenalty
      presencePenalty
      temperature
      topP
    }
    messages {
      role
      content
      params
    }
  }
}`,
};

export const updatePromptMutation = {
  id: 'updatePromptMutation' as const,
  op: 'updatePrompt',
  query: `mutation updatePrompt($name: String!, $messages: [CopilotPromptMessageInput!]!) {
  updateCopilotPrompt(name: $name, messages: $messages) {
    name
    model
    action
    config {
      frequencyPenalty
      presencePenalty
      temperature
      topP
    }
    messages {
      role
      content
      params
    }
  }
}`,
};

export const createUserMutation = {
  id: 'createUserMutation' as const,
  op: 'createUser',
  query: `mutation createUser($input: CreateUserInput!) {
  createUser(input: $input) {
    id
  }
}`,
};

export const deleteUserMutation = {
  id: 'deleteUserMutation' as const,
  op: 'deleteUser',
  query: `mutation deleteUser($id: String!) {
  deleteUser(id: $id) {
    success
  }
}`,
};

export const disableUserMutation = {
  id: 'disableUserMutation' as const,
  op: 'disableUser',
  query: `mutation disableUser($id: String!) {
  banUser(id: $id) {
    email
    disabled
  }
}`,
};

export const enableUserMutation = {
  id: 'enableUserMutation' as const,
  op: 'enableUser',
  query: `mutation enableUser($id: String!) {
  enableUser(id: $id) {
    email
    disabled
  }
}`,
};

export const importUsersMutation = {
  id: 'importUsersMutation' as const,
  op: 'ImportUsers',
  query: `mutation ImportUsers($input: ImportUsersInput!) {
  importUsers(input: $input) {
    __typename
    ... on UserType {
      id
      name
      email
    }
    ... on UserImportFailedType {
      email
      error
    }
  }
}`,
};

export const listUsersQuery = {
  id: 'listUsersQuery' as const,
  op: 'listUsers',
  query: `query listUsers($filter: ListUserInput!) {
  users(filter: $filter) {
    id
    name
    email
    disabled
    features
    hasPassword
    emailVerified
    avatarUrl
    currentPlan
    totpEnabled
    lastActiveAt
    createdAt
    ownedWorkspaceCount
    memberWorkspaceCount
  }
  usersCount(filter: $filter)
}`,
};

export const sendTestEmailMutation = {
  id: 'sendTestEmailMutation' as const,
  op: 'sendTestEmail',
  query: `mutation sendTestEmail($config: JSONObject!) {
  sendTestEmail(config: $config)
}`,
};

export const updateAccountFeaturesMutation = {
  id: 'updateAccountFeaturesMutation' as const,
  op: 'updateAccountFeatures',
  query: `mutation updateAccountFeatures($userId: String!, $features: [FeatureType!]!) {
  updateUserFeatures(id: $userId, features: $features)
}`,
};

export const updateAccountMutation = {
  id: 'updateAccountMutation' as const,
  op: 'updateAccount',
  query: `mutation updateAccount($id: String!, $input: ManageUserInput!) {
  updateUser(id: $id, input: $input) {
    id
    name
    email
  }
}`,
};

export const updateAppConfigMutation = {
  id: 'updateAppConfigMutation' as const,
  op: 'updateAppConfig',
  query: `mutation updateAppConfig($updates: [UpdateAppConfigInput!]!) {
  updateAppConfig(updates: $updates)
}`,
};

export const validateConfigMutation = {
  id: 'validateConfigMutation' as const,
  op: 'validateConfig',
  query: `mutation validateConfig($updates: [UpdateAppConfigInput!]!) {
  validateAppConfig(updates: $updates) {
    module
    key
    value
    valid
    error
  }
}`,
};

export const appBaseAieoQuery = {
  id: 'appBaseAieoQuery' as const,
  op: 'appBaseAieo',
  query: `query appBaseAieo($appBaseId: ID!, $workspaceId: ID!) {
  appBaseAIEO(appBaseId: $appBaseId, workspaceId: $workspaceId) {
    appBaseId
    workspaceId
    crawlerPolicy
    crawlerPolicyDetails
    declaration
    trainingDataConsent
    attributionUrl
    llmsTextOverride
    updatedAt
  }
}`,
};

export const appBaseBandwidthBudgetQuery = {
  id: 'appBaseBandwidthBudgetQuery' as const,
  op: 'appBaseBandwidthBudget',
  query: `query appBaseBandwidthBudget($workspaceId: String!, $appBaseId: String!) {
  appBaseBandwidthBudget(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    id
    workspaceId
    appBaseId
    monthlyLimitMicro
    monthToDateUsedMicro
    alertThresholds
    behavior
    createdAt
    updatedAt
  }
}`,
};

export const appBaseDatabaseBindingHealthQuery = {
  id: 'appBaseDatabaseBindingHealthQuery' as const,
  op: 'appBaseDatabaseBindingHealth',
  query: `query appBaseDatabaseBindingHealth($workspaceId: String!, $id: String!) {
  appBaseDatabaseBindingHealth(workspaceId: $workspaceId, id: $id)
}`,
};

export const appBaseDatabaseBindingsQuery = {
  id: 'appBaseDatabaseBindingsQuery' as const,
  op: 'appBaseDatabaseBindings',
  query: `query appBaseDatabaseBindings($workspaceId: String!, $id: String!) {
  appBaseDatabaseBindings(workspaceId: $workspaceId, id: $id)
}`,
};

export const appBaseDevelopmentInspectQuery = {
  id: 'appBaseDevelopmentInspectQuery' as const,
  op: 'appBaseDevelopmentInspect',
  query: `query appBaseDevelopmentInspect($workspaceId: String!, $target: JSONObject!) {
  appBaseDevelopmentInspect(workspaceId: $workspaceId, target: $target)
}`,
};

export const appBaseInvocationStatusQuery = {
  id: 'appBaseInvocationStatusQuery' as const,
  op: 'appBaseInvocationStatus',
  query: `query appBaseInvocationStatus($workspaceId: String!, $appBaseId: String!) {
  appBaseInvocationStatus(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    planSlug
    exposure
    origin
    actions {
      actionId
      label
      operation
      allowed
      policyReason
      effectiveRatePerHour
      usedLastHour
      concurrencyLimit
      concurrencyUsed
    }
  }
}`,
};

export const appBaseOwnRowIdsQuery = {
  id: 'appBaseOwnRowIdsQuery' as const,
  op: 'appBaseOwnRowIds',
  query: `query appBaseOwnRowIds($workspaceId: String!, $appBaseId: String!, $databaseId: String!) {
  appBaseOwnRowIds(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    databaseId: $databaseId
  )
}`,
};

export const appBasePortalInvitationsQuery = {
  id: 'appBasePortalInvitationsQuery' as const,
  op: 'appBasePortalInvitations',
  query: `query appBasePortalInvitations($workspaceId: String!, $appBaseId: String!) {
  appBasePortalInvitations(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const createAppBasePortalInvitationMutation = {
  id: 'createAppBasePortalInvitationMutation' as const,
  op: 'createAppBasePortalInvitation',
  query: `mutation createAppBasePortalInvitation($workspaceId: String!, $appBaseId: String!, $email: String!, $sendEmail: Boolean) {
  createAppBasePortalInvitation(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    email: $email
    sendEmail: $sendEmail
  )
}`,
};

export const revokeAppBasePortalInvitationMutation = {
  id: 'revokeAppBasePortalInvitationMutation' as const,
  op: 'revokeAppBasePortalInvitation',
  query: `mutation revokeAppBasePortalInvitation($workspaceId: String!, $appBaseId: String!, $invitationId: String!) {
  revokeAppBasePortalInvitation(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    invitationId: $invitationId
  )
}`,
};

export const appBasePortalMembersQuery = {
  id: 'appBasePortalMembersQuery' as const,
  op: 'appBasePortalMembers',
  query: `query appBasePortalMembers($workspaceId: String!, $appBaseId: String!) {
  appBasePortalMembers(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const appBaseProductQuotaQuery = {
  id: 'appBaseProductQuotaQuery' as const,
  op: 'appBaseProductQuota',
  query: `query appBaseProductQuota($workspaceId: String!) {
  appBaseProductQuota(workspaceId: $workspaceId)
}`,
};

export const appBasePublicViewerStateQuery = {
  id: 'appBasePublicViewerStateQuery' as const,
  op: 'appBasePublicViewerState',
  query: `query appBasePublicViewerState($workspaceId: String!, $appBaseId: String!) {
  appBasePublicViewerState(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    viewConfig
    publishedVersion
  }
}`,
};

export const appBaseRowOwnershipSummaryQuery = {
  id: 'appBaseRowOwnershipSummaryQuery' as const,
  op: 'appBaseRowOwnershipSummary',
  query: `query appBaseRowOwnershipSummary($workspaceId: String!, $appBaseId: String!, $databaseId: String!) {
  appBaseRowOwnershipSummary(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    databaseId: $databaseId
  ) {
    totalRows
    rowsWithoutOwner
    moreOwners
    owners {
      type
      id
      appBaseId
      displayName
      rowCount
    }
  }
}`,
};

export const appBaseSeoCheckQuery = {
  id: 'appBaseSeoCheckQuery' as const,
  op: 'appBaseSeoCheck',
  query: `query appBaseSeoCheck($appBaseId: ID!, $workspaceId: ID!) {
  appBaseSeoCheck(appBaseId: $appBaseId, workspaceId: $workspaceId) {
    published
    apps {
      key
      title
      status
      findings {
        code
        severity
        count
      }
    }
  }
}`,
};

export const appBaseSeoQuery = {
  id: 'appBaseSeoQuery' as const,
  op: 'appBaseSeo',
  query: `query appBaseSeo($appBaseId: ID!, $workspaceId: ID!) {
  appBaseSEO(appBaseId: $appBaseId, workspaceId: $workspaceId) {
    appBaseId
    workspaceId
    titlesI18n
    descriptionsI18n
    slugsI18n
    ogImagesI18n
    indexing
    canonicalUrlOverride
    jsonLdType
    twitterCardType
    robotsDirectives
    structuredData
    updatedAt
  }
}`,
};

export const appBaseStateQuery = {
  id: 'appBaseStateQuery' as const,
  op: 'appBaseState',
  query: `query appBaseState($workspaceId: String!, $id: String!) {
  appBaseState(workspaceId: $workspaceId, id: $id)
}`,
};

export const appBaseVersionHistoryQuery = {
  id: 'appBaseVersionHistoryQuery' as const,
  op: 'appBaseVersionHistory',
  query: `query appBaseVersionHistory($workspaceId: String!, $appBaseId: String!) {
  appBaseVersionHistory(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    version
    role
    active
    current
    changeLabel
    changeSource
    baseVersion
    restoredFromVersion
    createdBy
    createdAt
    publishedAt
  }
}`,
};

export const appBaseVersionModulesQuery = {
  id: 'appBaseVersionModulesQuery' as const,
  op: 'appBaseVersionModules',
  query: `query appBaseVersionModules($workspaceId: String!, $appBaseId: String!, $version: Int!) {
  appBaseVersionModules(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    version: $version
  ) {
    mountId
    title
  }
}`,
};

export const appBasesByDocIdQuery = {
  id: 'appBasesByDocIdQuery' as const,
  op: 'appBasesByDocId',
  query: `query appBasesByDocId($workspaceId: String!, $docId: String!) {
  appBasesByDocId(workspaceId: $workspaceId, docId: $docId) {
    id
    workspaceId
    docId
    slug
    title
    isPublished
  }
}`,
};

export const appBasesQuery = {
  id: 'appBasesQuery' as const,
  op: 'appBases',
  query: `query appBases($workspaceId: String!) {
  appBases(workspaceId: $workspaceId) {
    id
    workspaceId
    docId
    slug
    title
    config
    isPublished
    publishedAt
    createdAt
    updatedAt
  }
}`,
};

export const appBaseAppsQuery = {
  id: 'appBaseAppsQuery' as const,
  op: 'appBaseApps',
  query: `query appBaseApps($workspaceId: String!) {
  appBaseApps(workspaceId: $workspaceId)
}`,
};

export const appBaseAppAccessQuery = {
  id: 'appBaseAppAccessQuery' as const,
  op: 'appBaseAppAccess',
  query: `query appBaseAppAccess($workspaceId: String!, $id: String!) {
  appBaseAppAccess(workspaceId: $workspaceId, id: $id)
}`,
};

export const appBaseAppPermissionMatrixQuery = {
  id: 'appBaseAppPermissionMatrixQuery' as const,
  op: 'appBaseAppPermissionMatrix',
  query: `query appBaseAppPermissionMatrix($workspaceId: String!, $id: String!) {
  appBaseAppPermissionMatrix(workspaceId: $workspaceId, id: $id)
}`,
};

export const appContainerPlanOverviewQuery = {
  id: 'appContainerPlanOverviewQuery' as const,
  op: 'appContainerPlanOverview',
  query: `query appContainerPlanOverview($workspaceId: String!) {
  appContainerPlanOverview(workspaceId: $workspaceId)
}`,
};

export const appContainerPublishedPlanUsageQuery = {
  id: 'appContainerPublishedPlanUsageQuery' as const,
  op: 'appContainerPublishedPlanUsage',
  query: `query appContainerPublishedPlanUsage($workspaceId: String!, $appBaseId: String!) {
  appContainerPublishedPlanUsage(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const appContainersForDeletionQuery = {
  id: 'appContainersForDeletionQuery' as const,
  op: 'appContainersForDeletion',
  query: `query appContainersForDeletion($workspaceId: String!, $docIds: [String!]! = [], $appBaseIds: [String!]! = []) {
  appContainersForDeletion(
    workspaceId: $workspaceId
    docIds: $docIds
    appBaseIds: $appBaseIds
  ) {
    appBaseId
    title
    hasMountedApps
  }
}`,
};

export const appTableChoicesQuery = {
  id: 'appTableChoicesQuery' as const,
  op: 'appTableChoices',
  query: `query appTableChoices($workspaceId: String!, $id: String!, $appId: String) {
  appTableChoices(workspaceId: $workspaceId, id: $id, appId: $appId) {
    choices {
      sourceKey
      label
      reason
    }
    retained {
      sourceKey
      label
      reason
    }
  }
}`,
};

export const applyAppBaseDevelopmentMutation = {
  id: 'applyAppBaseDevelopmentMutation' as const,
  op: 'applyAppBaseDevelopment',
  query: `mutation applyAppBaseDevelopment($workspaceId: String!, $input: JSONObject!, $change: AppBaseDraftChangeInput) {
  applyAppBaseDevelopment(
    workspaceId: $workspaceId
    input: $input
    change: $change
  )
}`,
};

export const attachExistingAppBaseDatabaseMutation = {
  id: 'attachExistingAppBaseDatabaseMutation' as const,
  op: 'attachExistingAppBaseDatabase',
  query: `mutation attachExistingAppBaseDatabase($workspaceId: String!, $id: String!, $input: AttachAppBaseDatabaseInput!) {
  attachExistingAppBaseDatabase(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const automationTrashMutation = {
  id: 'automationTrashMutation' as const,
  op: 'automationTrash',
  query: `mutation automationTrash($input: AutomationLifecycleInput!) {
  automationTrash(input: $input) {
    operationId
    state
  }
}`,
};

export const automationRestoreMutation = {
  id: 'automationRestoreMutation' as const,
  op: 'automationRestore',
  query: `mutation automationRestore($input: AutomationLifecycleInput!) {
  automationRestore(input: $input) {
    operationId
    state
  }
}`,
};

export const automationDeleteMutation = {
  id: 'automationDeleteMutation' as const,
  op: 'automationDelete',
  query: `mutation automationDelete($input: AutomationLifecycleInput!) {
  automationDelete(input: $input) {
    operationId
    state
  }
}`,
};

export const beginContentEntryChangeMutation = {
  id: 'beginContentEntryChangeMutation' as const,
  op: 'beginContentEntryChange',
  query: `mutation beginContentEntryChange($workspaceId: String!, $appBaseId: String!, $sourceKey: String!, $rowId: String!) {
  beginContentEntryChange(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
    rowId: $rowId
  ) {
    rowId
    status
    liveVersion
    hasOpenChanges
  }
}`,
};

export const deleteBlobMutation = {
  id: 'deleteBlobMutation' as const,
  op: 'deleteBlob',
  query: `mutation deleteBlob($workspaceId: String!, $key: String!, $permanently: Boolean) {
  deleteBlob(workspaceId: $workspaceId, key: $key, permanently: $permanently)
}`,
};

export const listBlobsQuery = {
  id: 'listBlobsQuery' as const,
  op: 'listBlobs',
  query: `query listBlobs($workspaceId: String!) {
  workspace(id: $workspaceId) {
    blobs {
      key
      size
      mime
      createdAt
    }
  }
}`,
};

export const releaseDeletedBlobsMutation = {
  id: 'releaseDeletedBlobsMutation' as const,
  op: 'releaseDeletedBlobs',
  query: `mutation releaseDeletedBlobs($workspaceId: String!) {
  releaseDeletedBlobs(workspaceId: $workspaceId)
}`,
};

export const setBlobMutation = {
  id: 'setBlobMutation' as const,
  op: 'setBlob',
  query: `mutation setBlob($workspaceId: String!, $blob: Upload!) {
  setBlob(workspaceId: $workspaceId, blob: $blob)
}`,
  file: true,
  uploads: {"variables":{"blob":{"type":"Upload","listDepth":0}},"inputs":{}},
};

export const blockInviteEligibleRecipientsQuery = {
  id: 'blockInviteEligibleRecipientsQuery' as const,
  op: 'blockInviteEligibleRecipients',
  query: `query blockInviteEligibleRecipients($input: BlockInviteEligibilityInput!) {
  blockInviteEligibleRecipients(input: $input) {
    users {
      id
      name
      email
      avatarUrl
      role
    }
    groups {
      id
      name
      description
      memberCount
    }
    ineligible {
      id
      type
      reason
      detail
    }
  }
}`,
};

export const brandedSubdomainRenameAvailabilityQuery = {
  id: 'brandedSubdomainRenameAvailabilityQuery' as const,
  op: 'brandedSubdomainRenameAvailability',
  query: `query brandedSubdomainRenameAvailability($workspaceId: String!) {
  brandedSubdomainRenameAvailability(workspaceId: $workspaceId) {
    canRename
    nextRenameAt
    currentLabel
  }
}`,
};

export const cancelAccountDeletionMutation = {
  id: 'cancelAccountDeletionMutation' as const,
  op: 'cancelAccountDeletion',
  query: `mutation cancelAccountDeletion {
  cancelAccountDeletion {
    scheduled
    scheduledAt
    finalizesAt
    reactivatable
    gracePeriodDays
  }
}`,
};

export const cancelCreditSubscriptionMutation = {
  id: 'cancelCreditSubscriptionMutation' as const,
  op: 'cancelCreditSubscription',
  query: `mutation cancelCreditSubscription($workspaceId: String!, $creditPackId: String!) {
  cancelCreditSubscription(workspaceId: $workspaceId, creditPackId: $creditPackId)
}`,
};

export const cancelPendingIntervalChangeMutation = {
  id: 'cancelPendingIntervalChangeMutation' as const,
  op: 'cancelPendingIntervalChange',
  query: `mutation cancelPendingIntervalChange($workspaceId: String!, $type: String!, $creditPackId: String) {
  cancelPendingIntervalChange(
    workspaceId: $workspaceId
    type: $type
    creditPackId: $creditPackId
  )
}`,
};

export const cancelSubscriptionMutation = {
  id: 'cancelSubscriptionMutation' as const,
  op: 'cancelSubscription',
  query: `mutation cancelSubscription($language: String, $plan: String!, $terminationReason: String, $terminationType: String, $workspaceId: String) {
  cancelSubscription(
    language: $language
    plan: $plan
    terminationReason: $terminationReason
    terminationType: $terminationType
    workspaceId: $workspaceId
  ) {
    id
    status
    nextBillAt
    canceledAt
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const changeEmailMutation = {
  id: 'changeEmailMutation' as const,
  op: 'changeEmail',
  query: `mutation changeEmail($token: String!, $email: String!) {
  changeEmail(token: $token, email: $email) {
    id
    email
  }
}`,
};

export const changePasswordMutation = {
  id: 'changePasswordMutation' as const,
  op: 'changePassword',
  query: `mutation changePassword($token: String!, $userId: String!, $newPassword: String!) {
  changePassword(token: $token, userId: $userId, newPassword: $newPassword)
}`,
};

export const clearAppBaseBandwidthBudgetMutation = {
  id: 'clearAppBaseBandwidthBudgetMutation' as const,
  op: 'clearAppBaseBandwidthBudget',
  query: `mutation clearAppBaseBandwidthBudget($workspaceId: String!, $appBaseId: String!) {
  clearAppBaseBandwidthBudget(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const listCommentChangesQuery = {
  id: 'listCommentChangesQuery' as const,
  op: 'listCommentChanges',
  query: `query listCommentChanges($workspaceId: String!, $docId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    commentChanges(docId: $docId, pagination: $pagination) {
      totalCount
      edges {
        cursor
        node {
          action
          id
          commentId
          item
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
}`,
};

export const createCommentMutation = {
  id: 'createCommentMutation' as const,
  op: 'createComment',
  query: `mutation createComment($input: CommentCreateInput!) {
  createComment(input: $input) {
    id
    content
    resolved
    targetBlockIds
    createdAt
    updatedAt
    user {
      id
      name
      avatarUrl
    }
    replies {
      commentId
      id
      content
      createdAt
      updatedAt
      user {
        id
        name
        avatarUrl
      }
    }
  }
}`,
};

export const deleteCommentMutation = {
  id: 'deleteCommentMutation' as const,
  op: 'deleteComment',
  query: `mutation deleteComment($workspaceId: String!, $id: String!) {
  deleteComment(workspaceId: $workspaceId, id: $id)
}`,
};

export const listCommentsQuery = {
  id: 'listCommentsQuery' as const,
  op: 'listComments',
  query: `query listComments($workspaceId: String!, $docId: String!, $pagination: PaginationInput) {
  workspace(id: $workspaceId) {
    comments(docId: $docId, pagination: $pagination) {
      totalCount
      edges {
        cursor
        node {
          id
          content
          resolved
          targetBlockIds
          createdAt
          updatedAt
          user {
            id
            name
            avatarUrl
          }
          replies {
            commentId
            id
            content
            createdAt
            updatedAt
            user {
              id
              name
              avatarUrl
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
}`,
};

export const createReplyMutation = {
  id: 'createReplyMutation' as const,
  op: 'createReply',
  query: `mutation createReply($input: ReplyCreateInput!) {
  createReply(input: $input) {
    commentId
    id
    content
    createdAt
    updatedAt
    user {
      id
      name
      avatarUrl
    }
  }
}`,
};

export const deleteReplyMutation = {
  id: 'deleteReplyMutation' as const,
  op: 'deleteReply',
  query: `mutation deleteReply($workspaceId: String!, $id: String!) {
  deleteReply(workspaceId: $workspaceId, id: $id)
}`,
};

export const updateReplyMutation = {
  id: 'updateReplyMutation' as const,
  op: 'updateReply',
  query: `mutation updateReply($input: ReplyUpdateInput!) {
  updateReply(input: $input)
}`,
};

export const resolveCommentMutation = {
  id: 'resolveCommentMutation' as const,
  op: 'resolveComment',
  query: `mutation resolveComment($input: CommentResolveInput!) {
  resolveComment(input: $input)
}`,
};

export const updateCommentMutation = {
  id: 'updateCommentMutation' as const,
  op: 'updateComment',
  query: `mutation updateComment($input: CommentUpdateInput!) {
  updateComment(input: $input)
}`,
};

export const uploadCommentAttachmentMutation = {
  id: 'uploadCommentAttachmentMutation' as const,
  op: 'uploadCommentAttachment',
  query: `mutation uploadCommentAttachment($workspaceId: String!, $docId: String!, $attachment: Upload!) {
  uploadCommentAttachment(
    workspaceId: $workspaceId
    docId: $docId
    attachment: $attachment
  )
}`,
  file: true,
  uploads: {"variables":{"attachment":{"type":"Upload","listDepth":0}},"inputs":{}},
};

export const contentEntryOpenChangesQuery = {
  id: 'contentEntryOpenChangesQuery' as const,
  op: 'contentEntryOpenChanges',
  query: `query contentEntryOpenChanges($workspaceId: String!, $appBaseId: String!, $sourceKey: String!) {
  contentEntryOpenChanges(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
  ) {
    rowId
    liveVersion
    openedAt
    openedBy
  }
}`,
};

export const contentEntryVersionsQuery = {
  id: 'contentEntryVersionsQuery' as const,
  op: 'contentEntryVersions',
  query: `query contentEntryVersions($workspaceId: String!, $appBaseId: String!, $sourceKey: String!, $rowId: String!) {
  contentEntryVersions(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
    rowId: $rowId
  ) {
    version
    live
    createdAt
    createdBy
  }
}`,
};

export const addContextBlobMutation = {
  id: 'addContextBlobMutation' as const,
  op: 'addContextBlob',
  query: `mutation addContextBlob($options: AddContextBlobInput!) {
  addContextBlob(options: $options) {
    id
    createdAt
  }
}`,
};

export const removeContextBlobMutation = {
  id: 'removeContextBlobMutation' as const,
  op: 'removeContextBlob',
  query: `mutation removeContextBlob($options: RemoveContextBlobInput!) {
  removeContextBlob(options: $options)
}`,
};

export const addContextCategoryMutation = {
  id: 'addContextCategoryMutation' as const,
  op: 'addContextCategory',
  query: `mutation addContextCategory($options: AddContextCategoryInput!) {
  addContextCategory(options: $options) {
    id
    createdAt
    type
    docs {
      id
      createdAt
    }
  }
}`,
};

export const removeContextCategoryMutation = {
  id: 'removeContextCategoryMutation' as const,
  op: 'removeContextCategory',
  query: `mutation removeContextCategory($options: RemoveContextCategoryInput!) {
  removeContextCategory(options: $options)
}`,
};

export const createCopilotContextMutation = {
  id: 'createCopilotContextMutation' as const,
  op: 'createCopilotContext',
  query: `mutation createCopilotContext($workspaceId: String!, $sessionId: String!) {
  createCopilotContext(workspaceId: $workspaceId, sessionId: $sessionId)
}`,
};

export const addContextDocMutation = {
  id: 'addContextDocMutation' as const,
  op: 'addContextDoc',
  query: `mutation addContextDoc($options: AddContextDocInput!) {
  addContextDoc(options: $options) {
    id
    createdAt
  }
}`,
};

export const removeContextDocMutation = {
  id: 'removeContextDocMutation' as const,
  op: 'removeContextDoc',
  query: `mutation removeContextDoc($options: RemoveContextDocInput!) {
  removeContextDoc(options: $options)
}`,
};

export const addContextFileMutation = {
  id: 'addContextFileMutation' as const,
  op: 'addContextFile',
  query: `mutation addContextFile($content: Upload!, $options: AddContextFileInput!) {
  addContextFile(content: $content, options: $options) {
    id
    createdAt
    name
    mimeType
    chunkSize
    blobId
  }
}`,
  file: true,
  uploads: {"variables":{"content":{"type":"Upload","listDepth":0}},"inputs":{}},
};

export const removeContextFileMutation = {
  id: 'removeContextFileMutation' as const,
  op: 'removeContextFile',
  query: `mutation removeContextFile($options: RemoveContextFileInput!) {
  removeContextFile(options: $options)
}`,
};

export const listContextObjectQuery = {
  id: 'listContextObjectQuery' as const,
  op: 'listContextObject',
  query: `query listContextObject($workspaceId: String!, $sessionId: String!, $contextId: String!) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      contexts(sessionId: $sessionId, contextId: $contextId) {
        blobs {
          id
          createdAt
        }
        docs {
          id
          createdAt
        }
        files {
          id
          name
          mimeType
          blobId
          chunkSize
          createdAt
        }
        tags {
          type
          id
          docs {
            id
            createdAt
          }
          createdAt
        }
        collections {
          type
          id
          docs {
            id
            createdAt
          }
          createdAt
        }
      }
    }
  }
}`,
};

export const listContextQuery = {
  id: 'listContextQuery' as const,
  op: 'listContext',
  query: `query listContext($workspaceId: String!, $sessionId: String!) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      contexts(sessionId: $sessionId) {
        id
        workspaceId
      }
    }
  }
}`,
};

export const matchContextQuery = {
  id: 'matchContextQuery' as const,
  op: 'matchContext',
  query: `query matchContext($contextId: String, $workspaceId: String, $content: String!, $limit: SafeInt, $scopedThreshold: Float, $threshold: Float) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      contexts(contextId: $contextId) {
        matchFiles(
          content: $content
          limit: $limit
          scopedThreshold: $scopedThreshold
          threshold: $threshold
        ) {
          fileId
          blobId
          name
          mimeType
          chunk
          content
          distance
        }
        matchWorkspaceDocs(
          content: $content
          limit: $limit
          scopedThreshold: $scopedThreshold
          threshold: $threshold
        ) {
          docId
          chunk
          content
          distance
        }
      }
    }
  }
}`,
};

export const matchWorkspaceDocsQuery = {
  id: 'matchWorkspaceDocsQuery' as const,
  op: 'matchWorkspaceDocs',
  query: `query matchWorkspaceDocs($contextId: String, $workspaceId: String, $content: String!, $limit: SafeInt, $scopedThreshold: Float, $threshold: Float) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      contexts(contextId: $contextId) {
        matchWorkspaceDocs(
          content: $content
          limit: $limit
          scopedThreshold: $scopedThreshold
          threshold: $threshold
        ) {
          docId
          chunk
          content
          distance
        }
      }
    }
  }
}`,
};

export const matchFilesQuery = {
  id: 'matchFilesQuery' as const,
  op: 'matchFiles',
  query: `query matchFiles($contextId: String, $workspaceId: String, $content: String!, $limit: SafeInt, $scopedThreshold: Float, $threshold: Float) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      contexts(contextId: $contextId) {
        matchFiles(
          content: $content
          limit: $limit
          scopedThreshold: $scopedThreshold
          threshold: $threshold
        ) {
          fileId
          blobId
          chunk
          content
          distance
        }
      }
    }
  }
}`,
};

export const getWorkspaceEmbeddingStatusQuery = {
  id: 'getWorkspaceEmbeddingStatusQuery' as const,
  op: 'getWorkspaceEmbeddingStatus',
  query: `query getWorkspaceEmbeddingStatus($workspaceId: String!) {
  queryWorkspaceEmbeddingStatus(workspaceId: $workspaceId) {
    total
    embedded
  }
}`,
};

export const queueWorkspaceEmbeddingMutation = {
  id: 'queueWorkspaceEmbeddingMutation' as const,
  op: 'queueWorkspaceEmbedding',
  query: `mutation queueWorkspaceEmbedding($workspaceId: String!, $docId: [String!]!) {
  queueWorkspaceEmbedding(workspaceId: $workspaceId, docId: $docId)
}`,
};

export const getCopilotHistoryIdsQuery = {
  id: 'getCopilotHistoryIdsQuery' as const,
  op: 'getCopilotHistoryIds',
  query: `query getCopilotHistoryIds($workspaceId: String!, $pagination: PaginationInput!, $docId: String, $options: QueryChatHistoriesInput) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: $pagination, docId: $docId, options: $options) {
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          cursor
          node {
            sessionId
            pinned
            messages {
              id
              role
              createdAt
            }
          }
        }
      }
    }
  }
}`,
};

export const getCopilotDocSessionsQuery = {
  id: 'getCopilotDocSessionsQuery' as const,
  op: 'getCopilotDocSessions',
  query: `query getCopilotDocSessions($workspaceId: String!, $docId: String!, $pagination: PaginationInput!, $options: QueryChatHistoriesInput) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: $pagination, docId: $docId, options: $options) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const getCopilotPinnedSessionsQuery = {
  id: 'getCopilotPinnedSessionsQuery' as const,
  op: 'getCopilotPinnedSessions',
  query: `query getCopilotPinnedSessions($workspaceId: String!, $docId: String, $messageOrder: ChatHistoryOrder, $withPrompt: Boolean) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(
        pagination: {first: 1}
        docId: $docId
        options: {pinned: true, messageOrder: $messageOrder, withPrompt: $withPrompt}
      ) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const getCopilotWorkspaceSessionsQuery = {
  id: 'getCopilotWorkspaceSessionsQuery' as const,
  op: 'getCopilotWorkspaceSessions',
  query: `query getCopilotWorkspaceSessions($workspaceId: String!, $pagination: PaginationInput!, $options: QueryChatHistoriesInput) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: $pagination, docId: null, options: $options) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const getCopilotHistoriesQuery = {
  id: 'getCopilotHistoriesQuery' as const,
  op: 'getCopilotHistories',
  query: `query getCopilotHistories($workspaceId: String!, $pagination: PaginationInput!, $docId: String, $options: QueryChatHistoriesInput) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: $pagination, docId: $docId, options: $options) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const submitAudioTranscriptionMutation = {
  id: 'submitAudioTranscriptionMutation' as const,
  op: 'submitAudioTranscription',
  query: `mutation submitAudioTranscription($workspaceId: String!, $blobId: String!, $blob: Upload, $blobs: [Upload!]) {
  submitAudioTranscription(
    blob: $blob
    blobs: $blobs
    blobId: $blobId
    workspaceId: $workspaceId
  ) {
    id
    status
  }
}`,
  file: true,
  uploads: {"variables":{"blob":{"type":"Upload","listDepth":0},"blobs":{"type":"Upload","listDepth":1}},"inputs":{}},
};

export const claimAudioTranscriptionMutation = {
  id: 'claimAudioTranscriptionMutation' as const,
  op: 'claimAudioTranscription',
  query: `mutation claimAudioTranscription($jobId: String!, $workspaceId: String!) {
  claimAudioTranscription(jobId: $jobId, workspaceId: $workspaceId) {
    id
    status
    title
    summary
    actions
    transcription {
      speaker
      start
      end
      transcription
    }
  }
}`,
};

export const getAudioTranscriptionQuery = {
  id: 'getAudioTranscriptionQuery' as const,
  op: 'getAudioTranscription',
  query: `query getAudioTranscription($workspaceId: String!, $jobId: String, $blobId: String) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      audioTranscription(jobId: $jobId, blobId: $blobId) {
        id
        status
        title
        summary
        transcription {
          speaker
          start
          end
          transcription
        }
      }
    }
  }
}`,
};

export const retryAudioTranscriptionMutation = {
  id: 'retryAudioTranscriptionMutation' as const,
  op: 'retryAudioTranscription',
  query: `mutation retryAudioTranscription($workspaceId: String!, $jobId: String!) {
  retryAudioTranscription(workspaceId: $workspaceId, jobId: $jobId) {
    id
    status
  }
}`,
};

export const createCopilotMessageMutation = {
  id: 'createCopilotMessageMutation' as const,
  op: 'createCopilotMessage',
  query: `mutation createCopilotMessage($options: CreateChatMessageInput!) {
  createCopilotMessage(options: $options)
}`,
  file: true,
  uploads: {"variables":{"options":{"type":"CreateChatMessageInput","listDepth":0}},"inputs":{"CreateChatMessageInput":{"blob":{"type":"Upload","listDepth":0},"blobs":{"type":"Upload","listDepth":1}}}},
};

export const getPromptModelsQuery = {
  id: 'getPromptModelsQuery' as const,
  op: 'getPromptModels',
  query: `query getPromptModels($promptName: String!) {
  currentUser {
    copilot {
      models(promptName: $promptName) {
        defaultModel
        optionalModels {
          id
          name
          providerId
          providerName
          tier
          costMultiplier
        }
        proModels {
          id
          name
        }
      }
    }
  }
}`,
};

export const copilotQuotaQuery = {
  id: 'copilotQuotaQuery' as const,
  op: 'copilotQuota',
  query: `query copilotQuota {
  currentUser {
    copilot {
      quota {
        limit
        used
      }
    }
  }
}`,
};

export const cleanupCopilotSessionMutation = {
  id: 'cleanupCopilotSessionMutation' as const,
  op: 'cleanupCopilotSession',
  query: `mutation cleanupCopilotSession($input: DeleteSessionInput!) {
  cleanupCopilotSession(options: $input)
}`,
};

export const createCopilotSessionMutation = {
  id: 'createCopilotSessionMutation' as const,
  op: 'createCopilotSession',
  query: `mutation createCopilotSession($options: CreateChatSessionInput!) {
  createCopilotSession(options: $options)
}`,
};

export const forkCopilotSessionMutation = {
  id: 'forkCopilotSessionMutation' as const,
  op: 'forkCopilotSession',
  query: `mutation forkCopilotSession($options: ForkChatSessionInput!) {
  forkCopilotSession(options: $options)
}`,
};

export const getCopilotLatestDocSessionQuery = {
  id: 'getCopilotLatestDocSessionQuery' as const,
  op: 'getCopilotLatestDocSession',
  query: `query getCopilotLatestDocSession($workspaceId: String!, $docId: String!) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(
        pagination: {first: 1}
        docId: $docId
        options: {sessionOrder: desc, action: false, fork: false, withMessages: true}
      ) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const getCopilotSessionQuery = {
  id: 'getCopilotSessionQuery' as const,
  op: 'getCopilotSession',
  query: `query getCopilotSession($workspaceId: String!, $sessionId: String!) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: {first: 1}, options: {sessionId: $sessionId}) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const getCopilotRecentSessionsQuery = {
  id: 'getCopilotRecentSessionsQuery' as const,
  op: 'getCopilotRecentSessions',
  query: `query getCopilotRecentSessions($workspaceId: String!, $limit: Int = 10, $offset: Int = 0) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(
        pagination: {first: $limit, offset: $offset}
        options: {action: false, fork: false, excludePromptNames: ["streamlain-shared-canvas-chat-v1"], sessionOrder: desc, withMessages: false}
      ) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const updateCopilotSessionMutation = {
  id: 'updateCopilotSessionMutation' as const,
  op: 'updateCopilotSession',
  query: `mutation updateCopilotSession($options: UpdateChatSessionInput!) {
  updateCopilotSession(options: $options)
}`,
};

export const getCopilotSessionsQuery = {
  id: 'getCopilotSessionsQuery' as const,
  op: 'getCopilotSessions',
  query: `query getCopilotSessions($workspaceId: String!, $pagination: PaginationInput!, $docId: String, $options: QueryChatHistoriesInput) {
  currentUser {
    copilot(workspaceId: $workspaceId) {
      chats(pagination: $pagination, docId: $docId, options: $options) {
        ...PaginatedCopilotChats
      }
    }
  }
}
${copilotChatMessageFragment}
${copilotChatHistoryFragment}
${paginatedCopilotChatsFragment}`,
};

export const addWorkspaceEmbeddingIgnoredDocsMutation = {
  id: 'addWorkspaceEmbeddingIgnoredDocsMutation' as const,
  op: 'addWorkspaceEmbeddingIgnoredDocs',
  query: `mutation addWorkspaceEmbeddingIgnoredDocs($workspaceId: String!, $add: [String!]!) {
  updateWorkspaceEmbeddingIgnoredDocs(workspaceId: $workspaceId, add: $add)
}`,
};

export const getAllWorkspaceEmbeddingIgnoredDocsQuery = {
  id: 'getAllWorkspaceEmbeddingIgnoredDocsQuery' as const,
  op: 'getAllWorkspaceEmbeddingIgnoredDocs',
  query: `query getAllWorkspaceEmbeddingIgnoredDocs($workspaceId: String!) {
  workspace(id: $workspaceId) {
    embedding {
      allIgnoredDocs {
        docId
        createdAt
      }
    }
  }
}`,
};

export const getWorkspaceEmbeddingIgnoredDocsQuery = {
  id: 'getWorkspaceEmbeddingIgnoredDocsQuery' as const,
  op: 'getWorkspaceEmbeddingIgnoredDocs',
  query: `query getWorkspaceEmbeddingIgnoredDocs($workspaceId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    embedding {
      ignoredDocs(pagination: $pagination) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            docId
            createdAt
            docCreatedAt
            docUpdatedAt
            title
            createdBy
            createdByAvatar
            updatedBy
          }
        }
      }
    }
  }
}`,
};

export const removeWorkspaceEmbeddingIgnoredDocsMutation = {
  id: 'removeWorkspaceEmbeddingIgnoredDocsMutation' as const,
  op: 'removeWorkspaceEmbeddingIgnoredDocs',
  query: `mutation removeWorkspaceEmbeddingIgnoredDocs($workspaceId: String!, $remove: [String!]!) {
  updateWorkspaceEmbeddingIgnoredDocs(workspaceId: $workspaceId, remove: $remove)
}`,
};

export const workspaceCorruptionReportsQuery = {
  id: 'workspaceCorruptionReportsQuery' as const,
  op: 'workspaceCorruptionReports',
  query: `query workspaceCorruptionReports($input: GetCorruptionReportsInput!) {
  workspaceCorruptionReports(input: $input) {
    items {
      id
      workspaceId
      docId
      userId
      createdAt
      clientTimestamp
      clientTimezone
      errorType
      errorMessage
      operation
      status
      browser
      os
      appVersion
      wasOnline
      binLength
      binPreview
      docTitleSnapshot
    }
    total
    hasMore
  }
}`,
};

export const workspaceCorruptionStatsQuery = {
  id: 'workspaceCorruptionStatsQuery' as const,
  op: 'workspaceCorruptionStats',
  query: `query workspaceCorruptionStats($workspaceId: String!) {
  workspaceCorruptionStats(workspaceId: $workspaceId) {
    total
    pending
    recovered
    unrecoverable
    affectedUsers
    affectedDocs
  }
}`,
};

export const corruptionReportForAiQuery = {
  id: 'corruptionReportForAiQuery' as const,
  op: 'corruptionReportForAi',
  query: `query corruptionReportForAi($id: String!) {
  corruptionReportForAI(id: $id) {
    reportId
    exportedAt
    workspaceId
    docId
    docTitle
    userId
    serverTimestamp
    clientTimestamp
    clientTimezone
    errorType
    errorMessage
    operation
    status
    browser
    os
    appVersion
    wasOnline
    corruptedDataSize
    corruptedDataPreviewHex
    markdownPrompt
  }
}`,
};

export const updateCorruptionReportStatusMutation = {
  id: 'updateCorruptionReportStatusMutation' as const,
  op: 'updateCorruptionReportStatus',
  query: `mutation updateCorruptionReportStatus($id: String!, $status: CorruptionReportStatus!) {
  updateCorruptionReportStatus(id: $id, status: $status) {
    id
    workspaceId
    docId
    userId
    createdAt
    clientTimestamp
    clientTimezone
    errorType
    errorMessage
    operation
    status
    browser
    os
    appVersion
    wasOnline
    binLength
    binPreview
    docTitleSnapshot
  }
}`,
};

export const docHistoryTimelineQuery = {
  id: 'docHistoryTimelineQuery' as const,
  op: 'docHistoryTimeline',
  query: `query docHistoryTimeline($input: GetDocHistoryTimelineInput!) {
  docHistoryTimeline(input: $input) {
    docId
    entries {
      id
      timestamp
      size
      editorId
      isValid
      validationError
      entryType
      cumulativeSize
      dataFingerprint
    }
    totalEntries
    validEntries
    corruptEntries
    suggestedRestorePoint
  }
}`,
};

export const rebuildDocFromHistoryMutation = {
  id: 'rebuildDocFromHistoryMutation' as const,
  op: 'rebuildDocFromHistory',
  query: `mutation rebuildDocFromHistory($input: RebuildDocFromHistoryInput!) {
  rebuildDocFromHistory(input: $input) {
    success
    newSnapshotSize
    restoredToTimestamp
    validEntriesApplied
    corruptEntriesSkipped
    error
  }
}`,
};

export const docSizeInfoQuery = {
  id: 'docSizeInfoQuery' as const,
  op: 'docSizeInfo',
  query: `query docSizeInfo($workspaceId: String!, $docId: String!) {
  docSizeInfo(workspaceId: $workspaceId, docId: $docId) {
    docId
    snapshotSizeBytes
    snapshotSizeMB
    historyCount
    lastModified
    exceedsAutoThreshold
    exceedsDocAlertThreshold
    exceedsWsAlertThreshold
  }
}`,
};

export const compactDocMutation = {
  id: 'compactDocMutation' as const,
  op: 'compactDoc',
  query: `mutation compactDoc($input: CompactDocInput!) {
  compactDoc(input: $input) {
    success
    originalSizeBytes
    newSizeBytes
    reductionPercent
    error
  }
}`,
};

export const createAppBaseDraftMutation = {
  id: 'createAppBaseDraftMutation' as const,
  op: 'createAppBaseDraft',
  query: `mutation createAppBaseDraft($workspaceId: String!, $appBaseId: String!, $input: CreateAppBaseDraftInput!) {
  createAppBaseDraft(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    input: $input
  ) {
    id
    version
    role
    active
    viewConfig
    publishedAt
  }
}`,
};

export const createAppBaseMutation = {
  id: 'createAppBaseMutation' as const,
  op: 'createAppBase',
  query: `mutation createAppBase($workspaceId: String!, $input: CreateAppBaseInput!) {
  createAppBase(workspaceId: $workspaceId, input: $input) {
    id
    workspaceId
    docId
    slug
    title
    config
    isPublished
    publishedAt
    createdAt
    updatedAt
  }
}`,
};

export const createCheckoutSessionMutation = {
  id: 'createCheckoutSessionMutation' as const,
  op: 'createCheckoutSession',
  query: `mutation createCheckoutSession($input: CreateCheckoutSessionInput!) {
  createCheckoutSession(input: $input)
}`,
};

export const createCreditCheckoutMutation = {
  id: 'createCreditCheckoutMutation' as const,
  op: 'createCreditCheckout',
  query: `mutation createCreditCheckout($workspaceId: String!, $packId: String!, $successUrl: String!, $cancelUrl: String!, $withdrawalConsent: Boolean!) {
  createCreditCheckout(
    workspaceId: $workspaceId
    packId: $packId
    successUrl: $successUrl
    cancelUrl: $cancelUrl
    withdrawalConsent: $withdrawalConsent
  )
}`,
};

export const createCustomerPortalMutation = {
  id: 'createCustomerPortalMutation' as const,
  op: 'createCustomerPortal',
  query: `mutation createCustomerPortal {
  createCustomerPortal
}`,
};

export const createSelfhostCustomerPortalMutation = {
  id: 'createSelfhostCustomerPortalMutation' as const,
  op: 'createSelfhostCustomerPortal',
  query: `mutation createSelfhostCustomerPortal($workspaceId: String!) {
  createSelfhostWorkspaceCustomerPortal(workspaceId: $workspaceId)
}`,
};

export const createTagMutation = {
  id: 'createTagMutation' as const,
  op: 'createTag',
  query: `mutation createTag($workspaceId: String!, $id: String!, $name: String!, $color: String!) {
  createTag(workspaceId: $workspaceId, id: $id, name: $name, color: $color) {
    id
    workspaceId
    name
    color
    createdAt
    updatedAt
  }
}`,
};

export const createWorkspaceMutation = {
  id: 'createWorkspaceMutation' as const,
  op: 'createWorkspace',
  query: `mutation createWorkspace($idempotencyKey: String!, $name: String, $locale: String) {
  createWorkspace(idempotencyKey: $idempotencyKey, name: $name, locale: $locale) {
    id
    public
    createdAt
  }
}`,
};

export const creditBalanceQuery = {
  id: 'creditBalanceQuery' as const,
  op: 'creditBalance',
  query: `query creditBalance($workspaceId: String!) {
  creditBalance(workspaceId: $workspaceId) {
    balanceMicro
    displayBalance
    monthlyGrantMicro
    displayMonthlyGrant
    monthlyUsedMicro
    displayMonthlyUsed
    subscriptionGrantMicro
    subscriptionUsedMicro
    subscriptionRemainingMicro
    displaySubscriptionRemaining
    purchasedRemainingMicro
    periodStart
    periodEnd
    aiUsedMicro
    displayAiUsed
    workflowUsedMicro
    displayWorkflowUsed
    otherUsedMicro
    displayOtherUsed
    remainingPercent
    lowBalance
    lowBalancePercent
  }
}`,
};

export const creditPacksQuery = {
  id: 'creditPacksQuery' as const,
  op: 'creditPacks',
  query: `query creditPacks {
  creditPacks {
    id
    name
    credits
    stripeProductId
    packType
    unitAmount
    currency
    interval
  }
}`,
};

export const creditTransactionsQuery = {
  id: 'creditTransactionsQuery' as const,
  op: 'creditTransactions',
  query: `query creditTransactions($workspaceId: String!, $first: Int, $after: String, $type: CreditTransactionTypeEnum) {
  creditTransactions(
    workspaceId: $workspaceId
    first: $first
    after: $after
    type: $type
  ) {
    edges {
      cursor
      node {
        id
        amountMicro
        displayAmount
        type
        description
        balanceAfterMicro
        displayBalanceAfter
        createdAt
      }
    }
    pageInfo {
      hasNextPage
      endCursor
      totalCount
    }
  }
}`,
};

export const currentBandwidthUsageQuery = {
  id: 'currentBandwidthUsageQuery' as const,
  op: 'currentBandwidthUsage',
  query: `query currentBandwidthUsage($workspaceId: String!, $appBaseId: String!) {
  currentBandwidthUsage(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    appBaseId
    usedGB
    remainingGB
    creditBalanceMicro
    budgetSet
    budgetMonthlyLimitMicro
    budgetUsedPct
    budgetBehavior
    currentTier
    asOfDate
    resetDate
  }
}`,
};

export const appBaseViewerStateQuery = {
  id: 'appBaseViewerStateQuery' as const,
  op: 'appBaseViewerState',
  query: `query appBaseViewerState($workspaceId: String!, $appBaseId: String!) {
  appBaseViewerState(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    viewConfig
    source
    isDirty
    publishedVersion
    draftVersion
    selectedVersion
    isLive
  }
}`,
};

export const databaseAddPropertyMutation = {
  id: 'databaseAddPropertyMutation' as const,
  op: 'databaseAddProperty',
  query: `mutation databaseAddProperty($input: DatabaseAddPropertyInput!) {
  databaseAddProperty(input: $input) {
    columnId
  }
}`,
};

export const databaseAddRowMutation = {
  id: 'databaseAddRowMutation' as const,
  op: 'databaseAddRow',
  query: `mutation databaseAddRow($input: DatabaseAddRowInput!) {
  databaseAddRow(input: $input) {
    rowId
  }
}`,
};

export const databaseBulkMutateMutation = {
  id: 'databaseBulkMutateMutation' as const,
  op: 'databaseBulkMutate',
  query: `mutation databaseBulkMutate($input: DatabaseBulkMutateInput!) {
  databaseBulkMutate(input: $input) {
    operationId
    databaseId
    kind
    iteratorExhausted
    acknowledgement
    confirmedCount
    skippedChanged
    skippedMissing
    status
    receipt {
      workspaceId
      groupId
      operationId
      mutationId
      state
      receiptDigest
      deadlineAt
      sourceRowIds
    }
    error {
      code
      message
      retryable
    }
  }
}`,
};

export const databaseBulkReceiptStatusQuery = {
  id: 'databaseBulkReceiptStatusQuery' as const,
  op: 'databaseBulkReceiptStatus',
  query: `query databaseBulkReceiptStatus($input: DatabaseBulkReceiptStatusInput!) {
  databaseBulkReceiptStatus(input: $input) {
    workspaceId
    groupId
    operationId
    state
    receiptDigest
    failureClass
  }
}`,
};

export const databaseDeleteDependenciesQuery = {
  id: 'databaseDeleteDependenciesQuery' as const,
  op: 'databaseDeleteDependencies',
  query: `query databaseDeleteDependencies($input: DatabasePermanentlyDeleteInput!) {
  databaseDeleteDependencies(input: $input) {
    databaseId
    docId
    databaseTitle
    docTitle
    writable
    columns {
      id
      name
    }
  }
}`,
};

export const databaseDeletePropertyMutation = {
  id: 'databaseDeletePropertyMutation' as const,
  op: 'databaseDeleteProperty',
  query: `mutation databaseDeleteProperty($input: DatabaseDeletePropertyInput!) {
  databaseDeleteProperty(input: $input) {
    success
    columnId
    error
  }
}`,
};

export const databaseDeleteRowMutation = {
  id: 'databaseDeleteRowMutation' as const,
  op: 'databaseDeleteRow',
  query: `mutation databaseDeleteRow($input: DatabaseDeleteRowInput!) {
  databaseDeleteRow(input: $input)
}`,
};

export const databaseDuplicatePropertyMutation = {
  id: 'databaseDuplicatePropertyMutation' as const,
  op: 'databaseDuplicateProperty',
  query: `mutation databaseDuplicateProperty($input: DatabaseDuplicatePropertyInput!) {
  databaseDuplicateProperty(input: $input) {
    columnId
  }
}`,
};

export const databaseTrashMutation = {
  id: 'databaseTrashMutation' as const,
  op: 'databaseTrash',
  query: `mutation databaseTrash($input: DatabaseLifecycleInput!) {
  databaseTrash(input: $input) {
    operationId
    state
  }
}`,
};

export const databaseRestoreMutation = {
  id: 'databaseRestoreMutation' as const,
  op: 'databaseRestore',
  query: `mutation databaseRestore($input: DatabaseLifecycleInput!) {
  databaseRestore(input: $input) {
    operationId
    state
  }
}`,
};

export const databaseMoveRowMutation = {
  id: 'databaseMoveRowMutation' as const,
  op: 'databaseMoveRow',
  query: `mutation databaseMoveRow($input: DatabaseMoveRowInput!) {
  databaseMoveRow(input: $input)
}`,
};

export const databaseMoveMutation = {
  id: 'databaseMoveMutation' as const,
  op: 'databaseMove',
  query: `mutation databaseMove($input: DatabaseMoveInput!) {
  databaseMove(input: $input) {
    operationId
    databaseId
    status
    docId
    blockId
    kept {
      code
      databaseId
      message
    }
  }
}`,
};

export const databasePermanentlyDeleteMutation = {
  id: 'databasePermanentlyDeleteMutation' as const,
  op: 'databasePermanentlyDelete',
  query: `mutation databasePermanentlyDelete($input: DatabasePermanentlyDeleteInput!) {
  databasePermanentlyDelete(input: $input) {
    deletedSystemDocCount
    systemDocIds
  }
}`,
};

export const databaseRenamePropertyMutation = {
  id: 'databaseRenamePropertyMutation' as const,
  op: 'databaseRenameProperty',
  query: `mutation databaseRenameProperty($input: DatabaseRenamePropertyInput!) {
  databaseRenameProperty(input: $input) {
    columnId
  }
}`,
};

export const databaseSetPropertyDataMutation = {
  id: 'databaseSetPropertyDataMutation' as const,
  op: 'databaseSetPropertyData',
  query: `mutation databaseSetPropertyData($input: DatabaseSetPropertyDataInput!) {
  databaseSetPropertyData(input: $input) {
    columnId
  }
}`,
};

export const databaseSetPropertyTypeMutation = {
  id: 'databaseSetPropertyTypeMutation' as const,
  op: 'databaseSetPropertyType',
  query: `mutation databaseSetPropertyType($input: DatabaseSetPropertyTypeInput!) {
  databaseSetPropertyType(input: $input) {
    columnId
  }
}`,
};

export const databaseUpdateCellsMutation = {
  id: 'databaseUpdateCellsMutation' as const,
  op: 'databaseUpdateCells',
  query: `mutation databaseUpdateCells($input: DatabaseUpdateCellsInput!) {
  databaseUpdateCells(input: $input) {
    ok
    affectedRowIds
  }
}`,
};

export const deleteAllNotificationsMutation = {
  id: 'deleteAllNotificationsMutation' as const,
  op: 'deleteAllNotifications',
  query: `mutation deleteAllNotifications {
  deleteAllNotifications
}`,
};

export const deleteAppBaseMutation = {
  id: 'deleteAppBaseMutation' as const,
  op: 'deleteAppBase',
  query: `mutation deleteAppBase($workspaceId: String!, $id: String!) {
  deleteAppBase(workspaceId: $workspaceId, id: $id)
}`,
};

export const restoreAppBaseMutation = {
  id: 'restoreAppBaseMutation' as const,
  op: 'restoreAppBase',
  query: `mutation restoreAppBase($workspaceId: String!, $id: String!) {
  restoreAppBase(workspaceId: $workspaceId, id: $id)
}`,
};

export const permanentlyDeleteAppBaseMutation = {
  id: 'permanentlyDeleteAppBaseMutation' as const,
  op: 'permanentlyDeleteAppBase',
  query: `mutation permanentlyDeleteAppBase($workspaceId: String!, $id: String!, $tables: [AppBaseTableDispositionInput!]!) {
  permanentlyDeleteAppBase(workspaceId: $workspaceId, id: $id, tables: $tables)
}`,
};

export const deleteNotificationMutation = {
  id: 'deleteNotificationMutation' as const,
  op: 'deleteNotification',
  query: `mutation deleteNotification($id: String!) {
  deleteNotification(id: $id)
}`,
};

export const deleteTagMutation = {
  id: 'deleteTagMutation' as const,
  op: 'deleteTag',
  query: `mutation deleteTag($workspaceId: String!, $tagId: String!) {
  deleteTag(workspaceId: $workspaceId, tagId: $tagId)
}`,
};

export const deleteWorkspaceMutation = {
  id: 'deleteWorkspaceMutation' as const,
  op: 'deleteWorkspace',
  query: `mutation deleteWorkspace($id: String!) {
  deleteWorkspace(id: $id)
}`,
};

export const detachAppBaseDatabaseMutation = {
  id: 'detachAppBaseDatabaseMutation' as const,
  op: 'detachAppBaseDatabase',
  query: `mutation detachAppBaseDatabase($workspaceId: String!, $id: String!, $input: DetachAppBaseDatabaseInput!) {
  detachAppBaseDatabase(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const disableManagedAiMutation = {
  id: 'disableManagedAiMutation' as const,
  op: 'disableManagedAi',
  query: `mutation disableManagedAi($workspaceId: String!) {
  disableManagedAI(workspaceId: $workspaceId)
}`,
};

export const discardAppBaseDraftMutation = {
  id: 'discardAppBaseDraftMutation' as const,
  op: 'discardAppBaseDraft',
  query: `mutation discardAppBaseDraft($workspaceId: String!, $appBaseId: String!) {
  discardAppBaseDraft(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const discardContentEntryChangesMutation = {
  id: 'discardContentEntryChangesMutation' as const,
  op: 'discardContentEntryChanges',
  query: `mutation discardContentEntryChanges($workspaceId: String!, $appBaseId: String!, $sourceKey: String!, $rowIds: [String!]!) {
  discardContentEntryChanges(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
    rowIds: $rowIds
  ) {
    rowId
    status
    liveVersion
    hasOpenChanges
  }
}`,
};

export const discoverAppBaseDatabasesQuery = {
  id: 'discoverAppBaseDatabasesQuery' as const,
  op: 'discoverAppBaseDatabases',
  query: `query discoverAppBaseDatabases($workspaceId: String!, $id: String!, $docId: String) {
  discoverAppBaseDatabases(workspaceId: $workspaceId, id: $id, docId: $docId)
}`,
};

export const getDocRolePermissionsQuery = {
  id: 'getDocRolePermissionsQuery' as const,
  op: 'getDocRolePermissions',
  query: `query getDocRolePermissions($workspaceId: String!, $docId: String!) {
  workspace(id: $workspaceId) {
    doc(docId: $docId) {
      permissions {
        Doc_Copy
        Doc_Delete
        Doc_Duplicate
        Doc_Properties_Read
        Doc_Properties_Update
        Doc_Publish
        Doc_Read
        Doc_Restore
        Doc_TransferOwner
        Doc_Trash
        Doc_Update
        Doc_Users_Manage
        Doc_Users_Read
        Doc_Comments_Create
        Doc_Comments_Delete
        Doc_Comments_Read
        Doc_Comments_Resolve
      }
    }
  }
}`,
};

export const docTagIdsQuery = {
  id: 'docTagIdsQuery' as const,
  op: 'docTagIds',
  query: `query docTagIds($workspaceId: String!) {
  docTagIds(workspaceId: $workspaceId) {
    docId
    tagIds
  }
}`,
};

export const enableManagedAiMutation = {
  id: 'enableManagedAiMutation' as const,
  op: 'enableManagedAi',
  query: `mutation enableManagedAi($workspaceId: String!) {
  enableManagedAI(workspaceId: $workspaceId) {
    enabled
    consentGiven
    consentAt
    suspended
    suspendReason
    maxRequestsPerMinute
  }
}`,
};

export const ensureAppBaseDataSourcesMutation = {
  id: 'ensureAppBaseDataSourcesMutation' as const,
  op: 'ensureAppBaseDataSources',
  query: `mutation ensureAppBaseDataSources($workspaceId: String!, $id: String!, $input: EnsureAppBaseDataSourcesInput!) {
  ensureAppBaseDataSources(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const finalizeCheckoutSessionMutation = {
  id: 'finalizeCheckoutSessionMutation' as const,
  op: 'finalizeCheckoutSession',
  query: `mutation finalizeCheckoutSession($sessionId: String!) {
  finalizeCheckoutSession(sessionId: $sessionId)
}`,
};

export const addDocToFolderMutation = {
  id: 'addDocToFolderMutation' as const,
  op: 'addDocToFolder',
  query: `mutation addDocToFolder($workspaceId: String!, $folderId: String!, $docId: String!) {
  addDocToFolder(workspaceId: $workspaceId, folderId: $folderId, docId: $docId)
}`,
};

export const checkDeletedFolderIdsQuery = {
  id: 'checkDeletedFolderIdsQuery' as const,
  op: 'checkDeletedFolderIds',
  query: `query checkDeletedFolderIds($workspaceId: String!, $folderIds: [String!]!) {
  workspace(id: $workspaceId) {
    deletedFolderIds(folderIds: $folderIds)
  }
}`,
};

export const createFolderMutation = {
  id: 'createFolderMutation' as const,
  op: 'createFolder',
  query: `mutation createFolder($input: CreateFolderInput!) {
  createFolder(input: $input) {
    id
    workspaceId
    parentId
    name
    visibility
    inheritPermissions
    createdAt
    updatedAt
    docCount
  }
}`,
};

export const getFolderUsersQuery = {
  id: 'getFolderUsersQuery' as const,
  op: 'getFolderUsers',
  query: `query getFolderUsers($workspaceId: String!, $folderId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    folder(folderId: $folderId) {
      id
      name
      grantedUsers(pagination: $pagination) {
        totalCount
        items {
          role
          workspaceRole
          membershipKind
          isAreaBlocked
          areaBoundaryKind
          areaBoundaryRootFolderId
          areaBoundaryRootName
          user {
            id
            name
            email
            avatarUrl
          }
        }
      }
    }
  }
}`,
};

export const getFolderQuery = {
  id: 'getFolderQuery' as const,
  op: 'getFolder',
  query: `query getFolder($workspaceId: String!, $folderId: String!) {
  workspace(id: $workspaceId) {
    folder(folderId: $folderId) {
      id
      workspaceId
      parentId
      name
      visibility
      inheritPermissions
      createdAt
      updatedAt
      docCount
      permissionGraphVersion
      effectiveAccess {
        principalType
        principalId
        displayName
        email
        avatarUrl
        effectiveRole
        workspaceRole
        membershipKind
        sourceType
        sourceId
        sourceName
        isDirectOverride
        isInherited
        isInheritanceBlocked
        isAreaBlocked
        areaBoundaryKind
        areaBoundaryRootFolderId
        areaBoundaryRootName
        permissionGraphVersion
      }
      children {
        id
        workspaceId
        parentId
        name
        visibility
        inheritPermissions
        createdAt
        updatedAt
        docCount
      }
      permissions {
        Folder_Read
        Folder_Update
        Folder_Delete
        Folder_Trash
        Folder_Restore
        Folder_Move
        Folder_AddDoc
        Folder_RemoveDoc
        Folder_CreateSubfolder
        Folder_Users_Manage
        Folder_Users_Read
      }
    }
  }
}`,
};

export const getFoldersWithOwnersQuery = {
  id: 'getFoldersWithOwnersQuery' as const,
  op: 'getFoldersWithOwners',
  query: `query getFoldersWithOwners($workspaceId: String!, $parentId: String) {
  workspace(id: $workspaceId) {
    folders(parentId: $parentId) {
      id
      workspaceId
      parentId
      name
      visibility
      inheritPermissions
      createdAt
      updatedAt
      docCount
      owner {
        id
        name
        email
        avatarUrl
      }
    }
  }
}`,
};

export const getFoldersQuery = {
  id: 'getFoldersQuery' as const,
  op: 'getFolders',
  query: `query getFolders($workspaceId: String!, $parentId: String) {
  workspace(id: $workspaceId) {
    folders(parentId: $parentId) {
      id
      workspaceId
      parentId
      name
      visibility
      inheritPermissions
      createdAt
      updatedAt
      docCount
    }
  }
}`,
};

export const getTrashedFoldersQuery = {
  id: 'getTrashedFoldersQuery' as const,
  op: 'getTrashedFolders',
  query: `query getTrashedFolders($workspaceId: String!) {
  workspace(id: $workspaceId) {
    trashedFolders {
      id
      workspaceId
      parentId
      name
      systemKind
      trashed
      trashedAt
      trashedBy
      createdAt
      updatedAt
      docIds
      permissions {
        Folder_Read
        Folder_Update
        Folder_Delete
        Folder_Trash
        Folder_Restore
        Folder_Move
        Folder_AddDoc
        Folder_RemoveDoc
        Folder_CreateSubfolder
        Folder_Users_Read
        Folder_Users_Manage
      }
      owner {
        id
        name
        email
        avatarUrl
      }
    }
  }
}`,
};

export const grantFolderRoleMutation = {
  id: 'grantFolderRoleMutation' as const,
  op: 'grantFolderRole',
  query: `mutation grantFolderRole($input: GrantFolderRoleInput!) {
  grantFolderRole(input: $input)
}`,
};

export const moveFolderMutation = {
  id: 'moveFolderMutation' as const,
  op: 'moveFolder',
  query: `mutation moveFolder($workspaceId: String!, $folderId: String!, $newParentId: String) {
  moveFolder(
    workspaceId: $workspaceId
    folderId: $folderId
    newParentId: $newParentId
  ) {
    id
    workspaceId
    parentId
    name
    visibility
    inheritPermissions
    createdAt
    updatedAt
    docCount
  }
}`,
};

export const myAccessibleFoldersQuery = {
  id: 'myAccessibleFoldersQuery' as const,
  op: 'myAccessibleFolders',
  query: `query myAccessibleFolders($workspaceId: String!) {
  workspace(id: $workspaceId) {
    myAccessibleFolders {
      id
      name
      parentId
      ownerUserId
      systemKind
      visibility
      inheritPermissions
      isWorkspaceRoot
      immutable
      storageLimitBytes
      storageUsedBytes
      metadataOnly
      docIds
      permissionGraphVersion
    }
  }
}`,
};

export const permanentlyDeleteFolderMutation = {
  id: 'permanentlyDeleteFolderMutation' as const,
  op: 'permanentlyDeleteFolder',
  query: `mutation permanentlyDeleteFolder($workspaceId: String!, $folderId: String!) {
  permanentlyDeleteFolder(workspaceId: $workspaceId, folderId: $folderId)
}`,
};

export const preflightPermissionLifecycleQuery = {
  id: 'preflightPermissionLifecycleQuery' as const,
  op: 'preflightPermissionLifecycle',
  query: `query preflightPermissionLifecycle($input: PermissionLifecyclePreflightInput!) {
  preflightPermissionLifecycle(input: $input) {
    allowed
    operation
    totalBlockedCount
    blockedTargetIds
    affectedFolderIds
    affectedDocIds
    areaImpacts {
      targetKind
      targetId
      targetName
      principalType
      principalId
      role
      sourceType
      sourceId
      sourceName
      impact
      fromAreaKind
      fromAreaRootFolderId
      fromAreaRootName
      fromAreaBlocked
      toAreaKind
      toAreaRootFolderId
      toAreaRootName
      toAreaBlocked
    }
    reasonCounts {
      reason
      count
    }
    blockedObjects {
      kind
      id
      targetId
      name
      reason
    }
  }
}`,
};

export const removeDocFromFolderMutation = {
  id: 'removeDocFromFolderMutation' as const,
  op: 'removeDocFromFolder',
  query: `mutation removeDocFromFolder($workspaceId: String!, $docId: String!) {
  removeDocFromFolder(workspaceId: $workspaceId, docId: $docId)
}`,
};

export const restoreFolderMutation = {
  id: 'restoreFolderMutation' as const,
  op: 'restoreFolder',
  query: `mutation restoreFolder($workspaceId: String!, $folderId: String!) {
  restoreFolder(workspaceId: $workspaceId, folderId: $folderId) {
    id
    name
    trashed
    trashedAt
    trashedBy
  }
}`,
};

export const revokeFolderRoleMutation = {
  id: 'revokeFolderRoleMutation' as const,
  op: 'revokeFolderRole',
  query: `mutation revokeFolderRole($workspaceId: String!, $folderId: String!, $userId: String!) {
  revokeFolderRole(
    workspaceId: $workspaceId
    folderId: $folderId
    userId: $userId
  )
}`,
};

export const rootFolderDocIdsQuery = {
  id: 'rootFolderDocIdsQuery' as const,
  op: 'rootFolderDocIds',
  query: `query rootFolderDocIds($workspaceId: String!) {
  workspace(id: $workspaceId) {
    rootFolder {
      id
      docIds
    }
  }
}`,
};

export const trashFolderMutation = {
  id: 'trashFolderMutation' as const,
  op: 'trashFolder',
  query: `mutation trashFolder($workspaceId: String!, $folderId: String!) {
  trashFolder(workspaceId: $workspaceId, folderId: $folderId) {
    id
    name
    trashed
    trashedAt
    trashedBy
  }
}`,
};

export const updateFolderMutation = {
  id: 'updateFolderMutation' as const,
  op: 'updateFolder',
  query: `mutation updateFolder($input: UpdateFolderInput!) {
  updateFolder(input: $input) {
    id
    workspaceId
    parentId
    name
    visibility
    inheritPermissions
    createdAt
    updatedAt
    docCount
  }
}`,
};

export const getCurrentUserFeaturesQuery = {
  id: 'getCurrentUserFeaturesQuery' as const,
  op: 'getCurrentUserFeatures',
  query: `query getCurrentUserFeatures {
  currentUser {
    id
    name
    email
    emailVerified
    avatarUrl
    features
  }
}`,
};

export const getCurrentUserQuery = {
  id: 'getCurrentUserQuery' as const,
  op: 'getCurrentUser',
  query: `query getCurrentUser {
  currentUser {
    id
    name
    email
    emailVerified
    avatarUrl
    token {
      sessionToken
    }
  }
}`,
  deprecations: ["'token' is deprecated: use [/api/auth/sign-in?native=true] instead"],
};

export const getDocCreatedByUpdatedByListQuery = {
  id: 'getDocCreatedByUpdatedByListQuery' as const,
  op: 'getDocCreatedByUpdatedByList',
  query: `query getDocCreatedByUpdatedByList($workspaceId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    docs(pagination: $pagination) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          creatorId
          lastUpdaterId
        }
      }
    }
  }
}`,
};

export const getDocDefaultRoleQuery = {
  id: 'getDocDefaultRoleQuery' as const,
  op: 'getDocDefaultRole',
  query: `query getDocDefaultRole($workspaceId: String!, $docId: String!) {
  workspace(id: $workspaceId) {
    doc(docId: $docId) {
      defaultRole
    }
  }
}`,
};

export const getDocInheritedAccessQuery = {
  id: 'getDocInheritedAccessQuery' as const,
  op: 'getDocInheritedAccess',
  query: `query getDocInheritedAccess($workspaceId: String!, $docId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    doc(docId: $docId) {
      inheritedAccess(pagination: $pagination) {
        edges {
          cursor
          node {
            principalType
            principalId
            displayName
            email
            avatarUrl
            effectiveRole
            sourceType
            sourceId
            sourceName
            memberCount
            isDirectOverride
            isInherited
            isInheritanceBlocked
            isAreaBlocked
            areaBoundaryKind
            areaBoundaryRootFolderId
            areaBoundaryRootName
            permissionGraphVersion
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
      }
    }
  }
}`,
};

export const getDocSummaryQuery = {
  id: 'getDocSummaryQuery' as const,
  op: 'getDocSummary',
  query: `query getDocSummary($workspaceId: String!, $docId: String!) {
  workspace(id: $workspaceId) {
    doc(docId: $docId) {
      summary
    }
  }
}`,
};

export const getFolderGrantedUsersListQuery = {
  id: 'getFolderGrantedUsersListQuery' as const,
  op: 'getFolderGrantedUsersList',
  query: `query getFolderGrantedUsersList($workspaceId: String!, $folderId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    folder(folderId: $folderId) {
      id
      grantedUsers(pagination: $pagination) {
        totalCount
        items {
          role
          workspaceRole
          isAreaBlocked
          areaBoundaryKind
          areaBoundaryRootFolderId
          areaBoundaryRootName
          user {
            id
            name
            email
            avatarUrl
          }
        }
      }
    }
  }
}`,
};

export const getInviteInfoQuery = {
  id: 'getInviteInfoQuery' as const,
  op: 'getInviteInfo',
  query: `query getInviteInfo($inviteId: String!) {
  getInviteInfo(inviteId: $inviteId) {
    workspace {
      id
      name
      avatar
    }
    user {
      id
      name
      avatarUrl
    }
    status
    invitee {
      id
      name
      email
      avatarUrl
    }
  }
}`,
};

export const getMemberCountByWorkspaceIdQuery = {
  id: 'getMemberCountByWorkspaceIdQuery' as const,
  op: 'getMemberCountByWorkspaceId',
  query: `query getMemberCountByWorkspaceId($workspaceId: String!) {
  workspace(id: $workspaceId) {
    memberCount
  }
}`,
};

export const getMembersByWorkspaceIdQuery = {
  id: 'getMembersByWorkspaceIdQuery' as const,
  op: 'getMembersByWorkspaceId',
  query: `query getMembersByWorkspaceId($workspaceId: String!, $skip: Int, $take: Int, $query: String) {
  workspace(id: $workspaceId) {
    memberCount
    guestCount
    members(skip: $skip, take: $take, query: $query) {
      id
      name
      email
      avatarUrl
      permission
      inviteId
      emailVerified
      status
      membershipKind
      houseGuestExpiresAt
    }
  }
}`,
  deprecations: ["'permission' is deprecated: Use role instead"],
};

export const oauthProvidersQuery = {
  id: 'oauthProvidersQuery' as const,
  op: 'oauthProviders',
  query: `query oauthProviders {
  serverConfig {
    oauthProviders
  }
}`,
};

export const getPageGrantedUsersListQuery = {
  id: 'getPageGrantedUsersListQuery' as const,
  op: 'getPageGrantedUsersList',
  query: `query getPageGrantedUsersList($pagination: PaginationInput!, $docId: String!, $workspaceId: String!, $search: String, $includeCandidates: Boolean, $guestsOnly: Boolean) {
  workspace(id: $workspaceId) {
    doc(docId: $docId) {
      grantedUsersList(
        pagination: $pagination
        search: $search
        includeCandidates: $includeCandidates
        guestsOnly: $guestsOnly
      ) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            hasDirectGrant
            role
            workspaceRole
            membershipKind
            houseGuestExpiresAt
            isAreaBlocked
            areaBoundaryKind
            areaBoundaryRootFolderId
            areaBoundaryRootName
            user {
              id
              name
              email
              avatarUrl
            }
          }
        }
      }
    }
  }
}`,
};

export const getPendingDecisionCountQuery = {
  id: 'getPendingDecisionCountQuery' as const,
  op: 'getPendingDecisionCount',
  query: `query getPendingDecisionCount($workspaceId: String!) {
  workspace(id: $workspaceId) {
    pendingDecisionCount
  }
}`,
};

export const getPendingDecisionsQuery = {
  id: 'getPendingDecisionsQuery' as const,
  op: 'getPendingDecisions',
  query: `query getPendingDecisions($workspaceId: String!) {
  workspace(id: $workspaceId) {
    pendingDecisions {
      id
      configId
      sourceRowId
      targetColumn
      searchValue
      matchCount
      candidates {
        id
        title
        context
      }
      status
      createdAt
    }
  }
}`,
};

export const getPublicUserByIdQuery = {
  id: 'getPublicUserByIdQuery' as const,
  op: 'getPublicUserById',
  query: `query getPublicUserById($id: String!) {
  publicUserById(id: $id) {
    id
    avatarUrl
    name
  }
}`,
};

export const getRecentlyUpdatedDocsQuery = {
  id: 'getRecentlyUpdatedDocsQuery' as const,
  op: 'getRecentlyUpdatedDocs',
  query: `query getRecentlyUpdatedDocs($workspaceId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    recentlyUpdatedDocs(pagination: $pagination) {
      totalCount
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          title
          createdAt
          updatedAt
          creatorId
          lastUpdaterId
        }
      }
    }
  }
}`,
};

export const getScheduledWorkspacesQuery = {
  id: 'getScheduledWorkspacesQuery' as const,
  op: 'getScheduledWorkspaces',
  query: `query getScheduledWorkspaces {
  scheduledWorkspaces {
    id
    name
    deletionScheduledAt
  }
  workspaceDeletionGraceDays
}`,
};

export const getUserDocGrantsQuery = {
  id: 'getUserDocGrantsQuery' as const,
  op: 'getUserDocGrants',
  query: `query getUserDocGrants($workspaceId: String!, $userId: String!) {
  workspace(id: $workspaceId) {
    userDocGrants(userId: $userId) {
      docId
      role
      isAreaBlocked
      areaBoundaryKind
      areaBoundaryRootFolderId
      areaBoundaryRootName
    }
  }
}`,
};

export const getUserFeaturesQuery = {
  id: 'getUserFeaturesQuery' as const,
  op: 'getUserFeatures',
  query: `query getUserFeatures {
  currentUser {
    id
    features
  }
}`,
};

export const getUserFolderGrantsQuery = {
  id: 'getUserFolderGrantsQuery' as const,
  op: 'getUserFolderGrants',
  query: `query getUserFolderGrants($workspaceId: String!, $userId: String!) {
  workspace(id: $workspaceId) {
    userFolderGrants(userId: $userId) {
      folderId
      role
      isAreaBlocked
      areaBoundaryKind
      areaBoundaryRootFolderId
      areaBoundaryRootName
    }
  }
}`,
};

export const getUserSettingsQuery = {
  id: 'getUserSettingsQuery' as const,
  op: 'getUserSettings',
  query: `query getUserSettings {
  currentUser {
    settings {
      receiveInvitationEmail
      receiveMentionEmail
      receiveCommentEmail
      aiRelationMatchingEnabled
      pdfSearchCoverageCoachmarkDismissed
      timeZone
    }
  }
}`,
};

export const getUserQuery = {
  id: 'getUserQuery' as const,
  op: 'getUser',
  query: `query getUser($email: String!) {
  user(email: $email) {
    __typename
    ... on UserType {
      id
      name
      avatarUrl
      email
      hasPassword
    }
    ... on LimitedUserType {
      email
      hasPassword
    }
  }
}`,
};

export const getWorkspaceBillingStateQuery = {
  id: 'getWorkspaceBillingStateQuery' as const,
  op: 'getWorkspaceBillingState',
  query: `query getWorkspaceBillingState($workspaceId: String!) {
  workspace(id: $workspaceId) {
    billingState {
      effectivePlan
      fallbackPlanAfterOverride
      adminOverride {
        plan
        expiresAt
      }
      billingSubscription {
        id
        status
        plan
        isBasePlan
        recurring
        start
        end
        trialStart
        trialEnd
        nextBillAt
        canceledAt
        variant
        provider
        canWithdraw
        withdrawalEligibleUntil
        withdrawalUnavailableReason
      }
    }
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const getWorkspaceDocsWithOwnersQuery = {
  id: 'getWorkspaceDocsWithOwnersQuery' as const,
  op: 'getWorkspaceDocsWithOwners',
  query: `query getWorkspaceDocsWithOwners($workspaceId: String!, $pagination: PaginationInput!) {
  workspace(id: $workspaceId) {
    docs(pagination: $pagination) {
      edges {
        node {
          id
          title
          createdAt
          updatedAt
          createdBy {
            id
            name
            avatarUrl
          }
        }
      }
    }
  }
}`,
};

export const getWorkspaceInfoQuery = {
  id: 'getWorkspaceInfoQuery' as const,
  op: 'getWorkspaceInfo',
  query: `query getWorkspaceInfo($workspaceId: String!) {
  workspace(id: $workspaceId) {
    name
    avatarUrl
    role
    team
    membershipKind
    recoveryVersion
  }
}`,
};

export const getWorkspacePageByIdQuery = {
  id: 'getWorkspacePageByIdQuery' as const,
  op: 'getWorkspacePageById',
  query: `query getWorkspacePageById($workspaceId: String!, $pageId: String!) {
  workspace(id: $workspaceId) {
    doc(docId: $pageId) {
      id
      mode
      defaultRole
      defaultRoleDirect
      public
      publicSharing {
        public
        manual
        otherSourcesCount
        canEnable
        canRevoke
      }
      title
      summary
      permissionGraphVersion
    }
  }
}`,
};

export const getWorkspacePageMetaByIdQuery = {
  id: 'getWorkspacePageMetaByIdQuery' as const,
  op: 'getWorkspacePageMetaById',
  query: `query getWorkspacePageMetaById($id: String!, $pageId: String!) {
  workspace(id: $id) {
    pageMeta(pageId: $pageId) {
      createdAt
      updatedAt
      createdBy {
        name
        avatarUrl
      }
      updatedBy {
        name
        avatarUrl
      }
    }
  }
}`,
  deprecations: ["'pageMeta' is deprecated: use [WorkspaceType.doc] instead"],
};

export const getWorkspacePublicByIdQuery = {
  id: 'getWorkspacePublicByIdQuery' as const,
  op: 'getWorkspacePublicById',
  query: `query getWorkspacePublicById($id: String!) {
  workspace(id: $id) {
    public
  }
}`,
};

export const getWorkspacePublicPagesQuery = {
  id: 'getWorkspacePublicPagesQuery' as const,
  op: 'getWorkspacePublicPages',
  query: `query getWorkspacePublicPages($workspaceId: String!) {
  workspace(id: $workspaceId) {
    publicDocs {
      id
      mode
    }
  }
}`,
};

export const getWorkspaceSubscriptionQuery = {
  id: 'getWorkspaceSubscriptionQuery' as const,
  op: 'getWorkspaceSubscription',
  query: `query getWorkspaceSubscription($workspaceId: String!) {
  workspace(id: $workspaceId) {
    subscription {
      id
      status
      plan
      isBasePlan
      recurring
      start
      end
      trialStart
      trialEnd
      nextBillAt
      canceledAt
      variant
      provider
      canWithdraw
      withdrawalEligibleUntil
      withdrawalUnavailableReason
    }
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const getWorkspaceQuery = {
  id: 'getWorkspaceQuery' as const,
  op: 'getWorkspace',
  query: `query getWorkspace($id: String!) {
  workspace(id: $id) {
    id
  }
}`,
};

export const getWorkspacesQuery = {
  id: 'getWorkspacesQuery' as const,
  op: 'getWorkspaces',
  query: `query getWorkspaces {
  workspaces {
    id
    initialized
    role
    deletionScheduledAt
    team
    owner {
      id
    }
  }
}`,
};

export const grantDocUserRolesMutation = {
  id: 'grantDocUserRolesMutation' as const,
  op: 'grantDocUserRoles',
  query: `mutation grantDocUserRoles($input: GrantDocUserRolesInput!) {
  grantDocUserRoles(input: $input)
}`,
};

export const addGroupMemberMutation = {
  id: 'addGroupMemberMutation' as const,
  op: 'addGroupMember',
  query: `mutation addGroupMember($workspaceId: String!, $groupId: String!, $userId: String!) {
  addGroupMember(workspaceId: $workspaceId, groupId: $groupId, userId: $userId) {
    id
    groupId
    userId
  }
}`,
};

export const createGroupMutation = {
  id: 'createGroupMutation' as const,
  op: 'createGroup',
  query: `mutation createGroup($workspaceId: String!, $name: String!, $description: String) {
  createGroup(workspaceId: $workspaceId, name: $name, description: $description) {
    id
    workspaceId
    name
    description
    isAutoSync
    createdAt
  }
}`,
};

export const deleteGroupMutation = {
  id: 'deleteGroupMutation' as const,
  op: 'deleteGroup',
  query: `mutation deleteGroup($workspaceId: String!, $groupId: String!) {
  deleteGroup(workspaceId: $workspaceId, groupId: $groupId)
}`,
};

export const getDocGroupRolesQuery = {
  id: 'getDocGroupRolesQuery' as const,
  op: 'getDocGroupRoles',
  query: `query getDocGroupRoles($workspaceId: String!, $docId: String!) {
  docGroupRoles(workspaceId: $workspaceId, docId: $docId) {
    id
    docId
    groupId
    role
    createdAt
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
  }
}`,
};

export const getFolderGroupRolesQuery = {
  id: 'getFolderGroupRolesQuery' as const,
  op: 'getFolderGroupRoles',
  query: `query getFolderGroupRoles($workspaceId: String!, $folderId: String!) {
  folderGroupRoles(workspaceId: $workspaceId, folderId: $folderId) {
    id
    folderId
    groupId
    role
    createdAt
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
  }
}`,
};

export const getGroupDocRolesQuery = {
  id: 'getGroupDocRolesQuery' as const,
  op: 'getGroupDocRoles',
  query: `query getGroupDocRoles($workspaceId: String!, $groupId: String!) {
  groupDocRoles(workspaceId: $workspaceId, groupId: $groupId) {
    id
    docId
    groupId
    role
    createdAt
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
  }
}`,
};

export const getGroupFolderRolesQuery = {
  id: 'getGroupFolderRolesQuery' as const,
  op: 'getGroupFolderRoles',
  query: `query getGroupFolderRoles($workspaceId: String!, $groupId: String!) {
  groupFolderRoles(workspaceId: $workspaceId, groupId: $groupId) {
    id
    folderId
    groupId
    role
    createdAt
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
  }
}`,
};

export const getGroupMembersQuery = {
  id: 'getGroupMembersQuery' as const,
  op: 'getGroupMembers',
  query: `query getGroupMembers($workspaceId: String!, $groupId: String!) {
  groupMembers(workspaceId: $workspaceId, groupId: $groupId) {
    id
    groupId
    userId
  }
}`,
};

export const getGroupQuery = {
  id: 'getGroupQuery' as const,
  op: 'getGroup',
  query: `query getGroup($workspaceId: String!, $groupId: String!) {
  group(workspaceId: $workspaceId, groupId: $groupId) {
    id
    workspaceId
    name
    description
    isAutoSync
    createdAt
  }
}`,
};

export const getGroupsQuery = {
  id: 'getGroupsQuery' as const,
  op: 'getGroups',
  query: `query getGroups($workspaceId: String!) {
  groups(workspaceId: $workspaceId) {
    id
    workspaceId
    name
    description
    isAutoSync
    createdAt
    memberWorkspaceRoles
  }
}`,
};

export const grantDocGroupRoleMutation = {
  id: 'grantDocGroupRoleMutation' as const,
  op: 'grantDocGroupRole',
  query: `mutation grantDocGroupRole($workspaceId: String!, $docId: String!, $groupId: String!, $role: DocRole!) {
  grantDocGroupRole(
    workspaceId: $workspaceId
    docId: $docId
    groupId: $groupId
    role: $role
  ) {
    id
    docId
    groupId
    role
    createdAt
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
  }
}`,
};

export const grantFolderGroupRoleMutation = {
  id: 'grantFolderGroupRoleMutation' as const,
  op: 'grantFolderGroupRole',
  query: `mutation grantFolderGroupRole($workspaceId: String!, $folderId: String!, $groupId: String!, $role: FolderRole!) {
  grantFolderGroupRole(
    workspaceId: $workspaceId
    folderId: $folderId
    groupId: $groupId
    role: $role
  ) {
    id
    folderId
    groupId
    role
    isAreaBlocked
    areaBoundaryKind
    areaBoundaryRootFolderId
    areaBoundaryRootName
    createdAt
  }
}`,
};

export const removeGroupMemberMutation = {
  id: 'removeGroupMemberMutation' as const,
  op: 'removeGroupMember',
  query: `mutation removeGroupMember($workspaceId: String!, $groupId: String!, $userId: String!) {
  removeGroupMember(workspaceId: $workspaceId, groupId: $groupId, userId: $userId)
}`,
};

export const revokeDocGroupRoleMutation = {
  id: 'revokeDocGroupRoleMutation' as const,
  op: 'revokeDocGroupRole',
  query: `mutation revokeDocGroupRole($workspaceId: String!, $docId: String!, $groupId: String!) {
  revokeDocGroupRole(workspaceId: $workspaceId, docId: $docId, groupId: $groupId)
}`,
};

export const revokeFolderGroupRoleMutation = {
  id: 'revokeFolderGroupRoleMutation' as const,
  op: 'revokeFolderGroupRole',
  query: `mutation revokeFolderGroupRole($workspaceId: String!, $folderId: String!, $groupId: String!) {
  revokeFolderGroupRole(
    workspaceId: $workspaceId
    folderId: $folderId
    groupId: $groupId
  )
}`,
};

export const updateGroupMutation = {
  id: 'updateGroupMutation' as const,
  op: 'updateGroup',
  query: `mutation updateGroup($workspaceId: String!, $groupId: String!, $name: String, $description: String) {
  updateGroup(
    workspaceId: $workspaceId
    groupId: $groupId
    name: $name
    description: $description
  ) {
    id
    workspaceId
    name
    description
    isAutoSync
    createdAt
  }
}`,
};

export const listHistoryQuery = {
  id: 'listHistoryQuery' as const,
  op: 'listHistory',
  query: `query listHistory($workspaceId: String!, $pageDocId: String!, $take: Int, $before: DateTime) {
  workspace(id: $workspaceId) {
    histories(guid: $pageDocId, take: $take, before: $before) {
      id
      timestamp
      editor {
        name
        avatarUrl
      }
    }
  }
}`,
};

export const indexerAggregateQuery = {
  id: 'indexerAggregateQuery' as const,
  op: 'indexerAggregate',
  query: `query indexerAggregate($id: String!, $input: AggregateInput!) {
  workspace(id: $id) {
    aggregate(input: $input) {
      buckets {
        key
        count
        hits {
          nodes {
            fields
            highlights
          }
        }
      }
      pagination {
        count
        hasMore
        nextCursor
      }
    }
  }
}`,
};

export const indexerSearchDocsQuery = {
  id: 'indexerSearchDocsQuery' as const,
  op: 'indexerSearchDocs',
  query: `query indexerSearchDocs($id: String!, $input: SearchDocsInput!) {
  workspace(id: $id) {
    searchDocs(input: $input) {
      docId
      title
      blockId
      blobId
      flavour
      sourceType
      highlight
      databaseId
      rowId
      rowShardDocId
      rowShardVersion
      columnId
      databaseTitle
      rowLabel
      columnName
      visibility
      technicalSourceKind
      createdAt
      updatedAt
      createdByUser {
        id
        name
        avatarUrl
      }
      updatedByUser {
        id
        name
        avatarUrl
      }
    }
  }
}`,
};

export const indexerSearchQuery = {
  id: 'indexerSearchQuery' as const,
  op: 'indexerSearch',
  query: `query indexerSearch($id: String!, $input: SearchInput!) {
  workspace(id: $id) {
    search(input: $input) {
      nodes {
        fields
        highlights
      }
      pagination {
        count
        hasMore
        nextCursor
      }
    }
  }
}`,
};

export const installedTemplatesQuery = {
  id: 'installedTemplatesQuery' as const,
  op: 'installedTemplates',
  query: `query installedTemplates($workspaceId: String!) {
  installedTemplates(workspaceId: $workspaceId) {
    installId
    templateId
    templateTitle
    templateType
    stepInsertType
    versionNumber
    latestVersion
    installedDocIds
    workflowId
    installedAt
    isLegacyInstall
  }
}`,
};

export const isWorkspaceTwoFactorCompliantQuery = {
  id: 'isWorkspaceTwoFactorCompliantQuery' as const,
  op: 'isWorkspaceTwoFactorCompliant',
  query: `query isWorkspaceTwoFactorCompliant($workspaceId: String!) {
  isWorkspaceTwoFactorCompliant(workspaceId: $workspaceId)
}`,
};

export const knownAiCrawlerBotsQuery = {
  id: 'knownAiCrawlerBotsQuery' as const,
  op: 'knownAiCrawlerBots',
  query: `query knownAiCrawlerBots {
  knownAICrawlerBots {
    id
    userAgent
    label
  }
}`,
};

export const leaveWorkspaceMutation = {
  id: 'leaveWorkspaceMutation' as const,
  op: 'leaveWorkspace',
  query: `mutation leaveWorkspace($workspaceId: String!, $sendLeaveMail: Boolean) {
  leaveWorkspace(workspaceId: $workspaceId, sendLeaveMail: $sendLeaveMail)
}`,
};

export const listCustomDomainsQuery = {
  id: 'listCustomDomainsQuery' as const,
  op: 'listCustomDomains',
  query: `query listCustomDomains($workspaceId: String!) {
  customDomains(workspaceId: $workspaceId) {
    id
    domain
    url
    type
    status
    verificationToken
    verifiedAt
    sslStatus
    appBaseId
    createdAt
  }
}`,
};

export const listNotificationsQuery = {
  id: 'listNotificationsQuery' as const,
  op: 'listNotifications',
  query: `query listNotifications($pagination: PaginationInput!) {
  currentUser {
    notifications(pagination: $pagination) {
      totalCount
      edges {
        cursor
        node {
          id
          type
          level
          read
          createdAt
          updatedAt
          body
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
}`,
};

export const localeSettingsQuery = {
  id: 'localeSettingsQuery' as const,
  op: 'localeSettings',
  query: `query localeSettings {
  localeSettings {
    availableLocales
    defaultLocale
  }
}`,
};

export const mailAccountsQuery = {
  id: 'mailAccountsQuery' as const,
  op: 'mailAccounts',
  query: `query mailAccounts($workspaceId: String!) {
  mailAccounts(workspaceId: $workspaceId) {
    id
    workspaceId
    userId
    accessDocId
    resourceId
    lifecycleState
    trashedAt
    deletedAt
    effectiveDocRole
    capabilities
    area
    scope
    showInWorkspace
    mailboxRootId
    credentialId
    providerKind
    emailAddress
    displayName
    status
    syncEnabled
    syncMode
    syncAuthorityUserId
    activationStatus
    providerProfileId
    mailboxGroupId
    lastSyncedAt
    lastErrorAt
    lastError
    createdAt
    updatedAt
  }
}`,
};

export const mailboxRootDocIdsQuery = {
  id: 'mailboxRootDocIdsQuery' as const,
  op: 'mailboxRootDocIds',
  query: `query mailboxRootDocIds($workspaceId: String!) {
  mailboxRootDocIds(workspaceId: $workspaceId)
}`,
};

export const mailboxRootQuery = {
  id: 'mailboxRootQuery' as const,
  op: 'mailboxRoot',
  query: `query mailboxRoot($workspaceId: String!) {
  mailboxRoot(workspaceId: $workspaceId) {
    id
    workspaceId
    userId
    docId
    messagesDatabaseId
    inboxDatabaseId
    sentDatabaseId
    draftsDatabaseId
    archiveDatabaseId
    trashDatabaseId
    attachmentsDatabaseId
    foldersDatabaseId
    rulesDatabaseId
    accountStatusDatabaseId
    createdAt
    updatedAt
  }
}`,
};

export const mailCredentialShareProposalsQuery = {
  id: 'mailCredentialShareProposalsQuery' as const,
  op: 'mailCredentialShareProposals',
  query: `query mailCredentialShareProposals($input: MailCredentialShareProposalInput!) {
  mailCredentialShareProposals(input: $input) {
    credentialId
    currentRole
    managedRole
    proposedRole
    targetId
    targetType
  }
}`,
};

export const mailMessagesQuery = {
  id: 'mailMessagesQuery' as const,
  op: 'mailMessages',
  query: `query mailMessages($workspaceId: String!, $accountId: String!, $folder: MailFolder, $folderId: String, $virtualFolder: MailVirtualFolder, $limit: Int, $cursor: String, $search: String) {
  mailMessages(
    workspaceId: $workspaceId
    accountId: $accountId
    folder: $folder
    folderId: $folderId
    virtualFolder: $virtualFolder
    limit: $limit
    cursor: $cursor
    search: $search
  ) {
    id
    workspaceId
    accountId
    userId
    mailboxRootId
    folderId
    folder
    direction
    providerUid
    messageId
    threadId
    compositionKind
    sourceMessageId
    inReplyToHeader
    referencesHeader
    rowId
    subject
    fromAddress
    fromName
    toAddresses
    ccAddresses
    bccAddresses
    sentAt
    receivedAt
    internalDate
    snippet
    bodyStatus
    bodyHydratedAt
    bodyLastAccessedAt
    cachePinned
    isRead
    isFlagged
    isStarred
    hasAttachments
    sizeBytes
    createdAt
    updatedAt
  }
}`,
};

export const mailMessagesLegacyQuery = {
  id: 'mailMessagesLegacyQuery' as const,
  op: 'mailMessagesLegacy',
  query: `query mailMessagesLegacy($workspaceId: String!, $accountId: String!, $folder: MailFolder, $folderId: String, $limit: Int, $cursor: String, $search: String) {
  mailMessages(
    workspaceId: $workspaceId
    accountId: $accountId
    folder: $folder
    folderId: $folderId
    limit: $limit
    cursor: $cursor
    search: $search
  ) {
    id
    workspaceId
    accountId
    userId
    mailboxRootId
    folderId
    folder
    direction
    providerUid
    messageId
    threadId
    rowId
    subject
    fromAddress
    fromName
    toAddresses
    ccAddresses
    bccAddresses
    sentAt
    receivedAt
    internalDate
    snippet
    bodyStatus
    bodyHydratedAt
    bodyLastAccessedAt
    cachePinned
    isRead
    isFlagged
    isStarred
    hasAttachments
    sizeBytes
    createdAt
    updatedAt
  }
}`,
};

export const mailVirtualFoldersQuery = {
  id: 'mailVirtualFoldersQuery' as const,
  op: 'mailVirtualFolders',
  query: `query mailVirtualFolders($workspaceId: String!, $accountId: String!) {
  mailVirtualFolders(workspaceId: $workspaceId, accountId: $accountId) {
    id
    accountId
    displayName
    messageCount
    unreadCount
  }
}`,
};

export const mailFoldersQuery = {
  id: 'mailFoldersQuery' as const,
  op: 'mailFolders',
  query: `query mailFolders($workspaceId: String!, $accountId: String) {
  mailFolders(workspaceId: $workspaceId, accountId: $accountId) {
    id
    workspaceId
    accountId
    userId
    rowId
    providerFolderId
    path
    displayName
    role
    isSystem
    delimiter
    selectable
    canCreateChild
    canRename
    canDelete
    canMoveMessages
    messageCount
    unreadCount
    sortOrder
    createdAt
    updatedAt
  }
}`,
};

export const mailSyncStatesQuery = {
  id: 'mailSyncStatesQuery' as const,
  op: 'mailSyncStates',
  query: `query mailSyncStates($workspaceId: String!, $accountId: String) {
  mailSyncStates(workspaceId: $workspaceId, accountId: $accountId) {
    accountId
    lastUid
    lastModSeq
    lockedAt
    lockedBy
    phase
    syncReason
    currentFolderPath
    currentFolderIndex
    folderTotal
    fetchedCount
    persistedCount
    duplicateCount
    startedAt
    finishedAt
    lastSuccessfulSyncAt
    lastErrorAt
    lastError
    updatedAt
  }
}`,
};

export const mailTrustedSendersQuery = {
  id: 'mailTrustedSendersQuery' as const,
  op: 'mailTrustedSenders',
  query: `query mailTrustedSenders($workspaceId: String!) {
  mailTrustedSenders(workspaceId: $workspaceId) {
    workspaceId
    userId
    senderAddress
    createdAt
    updatedAt
  }
}`,
};

export const mailSignaturesQuery = {
  id: 'mailSignaturesQuery' as const,
  op: 'mailSignatures',
  query: `query mailSignatures($workspaceId: String!) {
  mailSignatures(workspaceId: $workspaceId) {
    id
    workspaceId
    userId
    name
    bodyHtml
    bodyText
    isEnabled
    createdAt
    updatedAt
  }
}`,
};

export const mailSignatureAssignmentsQuery = {
  id: 'mailSignatureAssignmentsQuery' as const,
  op: 'mailSignatureAssignments',
  query: `query mailSignatureAssignments($workspaceId: String!, $accountId: String) {
  mailSignatureAssignments(workspaceId: $workspaceId, accountId: $accountId) {
    id
    workspaceId
    userId
    accountId
    signatureId
    scope
    createdAt
    updatedAt
  }
}`,
};

export const mailAutorespondersQuery = {
  id: 'mailAutorespondersQuery' as const,
  op: 'mailAutoresponders',
  query: `query mailAutoresponders($workspaceId: String!, $accountId: String) {
  mailAutoresponders(workspaceId: $workspaceId, accountId: $accountId) {
    id
    workspaceId
    userId
    accountId
    signatureId
    enabled
    startsAt
    endsAt
    subject
    bodyText
    bodyHtml
    createdAt
    updatedAt
  }
}`,
};

export const prepareMailCompositionQuery = {
  id: 'prepareMailCompositionQuery' as const,
  op: 'prepareMailComposition',
  query: `query prepareMailComposition($input: PrepareMailCompositionInput!) {
  prepareMailComposition(input: $input) {
    accountId
    composeKind
    sourceMessageId
    inReplyToHeader
    referencesHeader
    to
    cc
    bcc
    subject
    quoteText
    quoteHtml
    attachments {
      blobKey
      filename
      mimeType
    }
  }
}`,
};

export const mailComposeSignatureQuery = {
  id: 'mailComposeSignatureQuery' as const,
  op: 'mailComposeSignature',
  query: `query mailComposeSignature($workspaceId: String!, $accountId: String!, $composeKind: MailComposeKind!) {
  mailComposeSignature(
    workspaceId: $workspaceId
    accountId: $accountId
    composeKind: $composeKind
  ) {
    id
    bodyHtml
    bodyText
  }
}`,
};

export const mailRulesQuery = {
  id: 'mailRulesQuery' as const,
  op: 'mailRules',
  query: `query mailRules($workspaceId: String!, $accountId: String) {
  mailRules(workspaceId: $workspaceId, accountId: $accountId) {
    id
    workspaceId
    accountId
    userId
    rowId
    name
    requestedEnabled
    effectiveEnabled
    validationStatus
    validationError
    priority
    conditions
    actions
    lastRunAt
    createdAt
    updatedAt
  }
}`,
};

export const mailMessageBodyQuery = {
  id: 'mailMessageBodyQuery' as const,
  op: 'mailMessageBody',
  query: `query mailMessageBody($workspaceId: String!, $messageId: String!) {
  mailMessageBody(workspaceId: $workspaceId, messageId: $messageId) {
    id
    workspaceId
    accountId
    userId
    subject
    bodyText
    bodyHtml
    bodyStatus
    bodyHydratedAt
    bodyLastAccessedAt
    bodyEvictedAt
    cachePinned
    sizeBytes
    attachments {
      id
      workspaceId
      accountId
      messageId
      userId
      blobKey
      filename
      mimeType
      sizeBytes
      contentId
      disposition
      createdAt
    }
  }
}`,
};

export const mailAdminOverviewQuery = {
  id: 'mailAdminOverviewQuery' as const,
  op: 'mailAdminOverview',
  query: `query mailAdminOverview($workspaceId: String!) {
  mailAdminOverview(workspaceId: $workspaceId) {
    workspaceId
    policy {
      workspaceId
      mailWindowDays
      availableMailWindowDays
      effectiveMailWindowDays
      retentionMode
      retentionDays
      searchIndexEnabled
      searchIndexAttachmentsEnabled
    }
    accounts {
      id
      userId
      userEmail
      userName
      providerKind
      emailAddress
      status
      syncEnabled
      syncMode
      syncAuthorityUserId
      activationStatus
      providerProfileId
      mailboxGroupId
      lastSyncedAt
      lastErrorAt
      lastError
      createdAt
    }
    usage {
      accountCount
      messageCount
      attachmentCount
      searchIndexBytes
      bodyCacheBytes
      attachmentCacheBytes
      searchIndexedMessageCount
      searchPendingMessageCount
      searchFailedMessageCount
      searchSkippedMessageCount
      calculatedAt
    }
    searchIndexStatus {
      status
      backfillStatus
      backfillRunId
      backfillJobId
      backfillScannedCount
      backfillQueuedCount
      backfillIndexedCount
      backfillSkippedCount
      backfillFailedCount
      backfillPurgedCount
      backfillPageCount
      backfillPageSize
      backfillCursorDate
      backfillStartedAt
      backfillPausedAt
      backfillCompletedAt
      backfillCancelledAt
      backfillLastError
      eligibleMessageCount
      unindexedMessageCount
      coveragePercent
      avgIndexedBytesPerMessage
      oldestEligibleMessageAt
      newestEligibleMessageAt
      oldestUnindexedMessageAt
      newestUnindexedMessageAt
      degradedReasons
    }
    quotaUsers {
      userId
      userName
      userEmail
      mailWindowDays
      worstFactorKind
      worstPercent
      factors {
        kind
        usedBytes
        limitBytes
        usedCount
        limitCount
        percent
        status
        label
        detail
        action
        limitSources {
          kind
          label
          limitBytes
          limitCount
        }
      }
      accounts {
        accountId
        emailAddress
        providerKind
        worstFactorKind
        worstPercent
        factors {
          kind
          usedBytes
          limitBytes
          usedCount
          limitCount
          percent
          status
          label
          detail
          action
          limitSources {
            kind
            label
            limitBytes
            limitCount
          }
        }
      }
    }
  }
}`,
};

export const mailCurrentUserQuotaQuery = {
  id: 'mailCurrentUserQuotaQuery' as const,
  op: 'mailCurrentUserQuota',
  query: `query mailCurrentUserQuota($workspaceId: String!) {
  mailCurrentUserQuota(workspaceId: $workspaceId) {
    userId
    userName
    userEmail
    mailWindowDays
    worstFactorKind
    worstPercent
    factors {
      kind
      usedBytes
      limitBytes
      usedCount
      limitCount
      percent
      status
      label
      detail
      action
      limitSources {
        kind
        label
        limitBytes
        limitCount
      }
    }
    accounts {
      accountId
      emailAddress
      providerKind
      worstFactorKind
      worstPercent
      factors {
        kind
        usedBytes
        limitBytes
        usedCount
        limitCount
        percent
        status
        label
        detail
        action
        limitSources {
          kind
          label
          limitBytes
          limitCount
        }
      }
    }
  }
}`,
};

export const connectMailAccountMutation = {
  id: 'connectMailAccountMutation' as const,
  op: 'connectMailAccount',
  query: `mutation connectMailAccount($input: ConnectMailAccountInput!) {
  connectMailAccount(input: $input) {
    id
    workspaceId
    userId
    accessDocId
    resourceId
    lifecycleState
    trashedAt
    deletedAt
    effectiveDocRole
    capabilities
    area
    scope
    showInWorkspace
    mailboxRootId
    credentialId
    providerKind
    emailAddress
    displayName
    status
    syncEnabled
    syncMode
    syncAuthorityUserId
    activationStatus
    providerProfileId
    mailboxGroupId
    lastSyncedAt
    lastErrorAt
    lastError
    createdAt
    updatedAt
  }
}`,
};

export const updateMailAccountMutation = {
  id: 'updateMailAccountMutation' as const,
  op: 'updateMailAccount',
  query: `mutation updateMailAccount($input: UpdateMailAccountInput!) {
  updateMailAccount(input: $input) {
    id
    workspaceId
    userId
    accessDocId
    resourceId
    lifecycleState
    trashedAt
    deletedAt
    effectiveDocRole
    capabilities
    area
    scope
    showInWorkspace
    mailboxRootId
    credentialId
    providerKind
    emailAddress
    displayName
    status
    syncEnabled
    syncMode
    syncAuthorityUserId
    activationStatus
    providerProfileId
    mailboxGroupId
    lastSyncedAt
    lastErrorAt
    lastError
    createdAt
    updatedAt
  }
}`,
};

export const deleteMailAccountMutation = {
  id: 'deleteMailAccountMutation' as const,
  op: 'deleteMailAccount',
  query: `mutation deleteMailAccount($workspaceId: String!, $accountId: String!) {
  deleteMailAccount(workspaceId: $workspaceId, accountId: $accountId)
}`,
};

export const syncMailAccountMutation = {
  id: 'syncMailAccountMutation' as const,
  op: 'syncMailAccount',
  query: `mutation syncMailAccount($workspaceId: String!, $accountId: String!) {
  syncMailAccount(workspaceId: $workspaceId, accountId: $accountId)
}`,
};

export const setMailWatchInterestMutation = {
  id: 'setMailWatchInterestMutation' as const,
  op: 'setMailWatchInterest',
  query: `mutation setMailWatchInterest($workspaceId: String!, $accountId: String!, $interest: MailWatchInterest!) {
  setMailWatchInterest(
    workspaceId: $workspaceId
    accountId: $accountId
    interest: $interest
  )
}`,
};

export const sendMailMutation = {
  id: 'sendMailMutation' as const,
  op: 'sendMail',
  query: `mutation sendMail($input: SendMailInput!) {
  sendMail(input: $input) {
    providerMessageId
    persistedMessageId
    rowId
    accepted
    rejected
  }
}`,
};

export const saveMailDraftMutation = {
  id: 'saveMailDraftMutation' as const,
  op: 'saveMailDraft',
  query: `mutation saveMailDraft($input: SaveMailDraftInput!) {
  saveMailDraft(input: $input) {
    accountId
    draftMessageId
    rowId
    providerDraftUid
    savedAt
  }
}`,
};

export const sendSavedMailDraftMutation = {
  id: 'sendSavedMailDraftMutation' as const,
  op: 'sendSavedMailDraft',
  query: `mutation sendSavedMailDraft($input: SendSavedMailDraftInput!, $files: [Upload!]) {
  sendSavedMailDraft(input: $input, files: $files) {
    providerMessageId
    persistedMessageId
    rowId
    accepted
    rejected
  }
}`,
  file: true,
  uploads: {"variables":{"files":{"type":"Upload","listDepth":1}},"inputs":{}},
};

export const upsertMailSignatureMutation = {
  id: 'upsertMailSignatureMutation' as const,
  op: 'upsertMailSignature',
  query: `mutation upsertMailSignature($input: UpsertMailSignatureInput!) {
  upsertMailSignature(input: $input) {
    id
    workspaceId
    userId
    name
    bodyHtml
    bodyText
    isEnabled
    createdAt
    updatedAt
  }
}`,
};

export const deleteMailSignatureMutation = {
  id: 'deleteMailSignatureMutation' as const,
  op: 'deleteMailSignature',
  query: `mutation deleteMailSignature($workspaceId: String!, $signatureId: String!) {
  deleteMailSignature(workspaceId: $workspaceId, signatureId: $signatureId)
}`,
};

export const assignMailSignatureMutation = {
  id: 'assignMailSignatureMutation' as const,
  op: 'assignMailSignature',
  query: `mutation assignMailSignature($input: AssignMailSignatureInput!) {
  assignMailSignature(input: $input) {
    id
    workspaceId
    userId
    accountId
    signatureId
    scope
    createdAt
    updatedAt
  }
}`,
};

export const deleteMailSignatureAssignmentMutation = {
  id: 'deleteMailSignatureAssignmentMutation' as const,
  op: 'deleteMailSignatureAssignment',
  query: `mutation deleteMailSignatureAssignment($workspaceId: String!, $accountId: String!, $scope: MailSignatureScope!) {
  deleteMailSignatureAssignment(
    workspaceId: $workspaceId
    accountId: $accountId
    scope: $scope
  )
}`,
};

export const upsertMailAutoresponderMutation = {
  id: 'upsertMailAutoresponderMutation' as const,
  op: 'upsertMailAutoresponder',
  query: `mutation upsertMailAutoresponder($input: UpsertMailAutoresponderInput!) {
  upsertMailAutoresponder(input: $input) {
    id
    workspaceId
    userId
    accountId
    signatureId
    enabled
    startsAt
    endsAt
    subject
    bodyText
    bodyHtml
    createdAt
    updatedAt
  }
}`,
};

export const createMailFolderMutation = {
  id: 'createMailFolderMutation' as const,
  op: 'createMailFolder',
  query: `mutation createMailFolder($input: CreateMailFolderInput!) {
  createMailFolder(input: $input) {
    id
    accountId
    path
    displayName
    role
    isSystem
    canRename
    canDelete
    messageCount
    unreadCount
  }
}`,
};

export const updateMailFolderMutation = {
  id: 'updateMailFolderMutation' as const,
  op: 'updateMailFolder',
  query: `mutation updateMailFolder($input: UpdateMailFolderInput!) {
  updateMailFolder(input: $input) {
    id
    accountId
    path
    displayName
    role
    isSystem
    canRename
    canDelete
    messageCount
    unreadCount
  }
}`,
};

export const deleteMailFolderMutation = {
  id: 'deleteMailFolderMutation' as const,
  op: 'deleteMailFolder',
  query: `mutation deleteMailFolder($workspaceId: String!, $folderId: String!) {
  deleteMailFolder(workspaceId: $workspaceId, folderId: $folderId)
}`,
};

export const updateMailMessageStateMutation = {
  id: 'updateMailMessageStateMutation' as const,
  op: 'updateMailMessageState',
  query: `mutation updateMailMessageState($input: UpdateMailMessageStateInput!) {
  updateMailMessageState(input: $input) {
    id
    folderId
    folder
    isRead
    isFlagged
    isStarred
    updatedAt
  }
}`,
};

export const moveMailMessageMutation = {
  id: 'moveMailMessageMutation' as const,
  op: 'moveMailMessage',
  query: `mutation moveMailMessage($input: MoveMailMessageInput!) {
  moveMailMessage(input: $input) {
    id
    folderId
    folder
    isRead
    isFlagged
    isStarred
    updatedAt
  }
}`,
};

export const deleteMailAutoresponderOverrideMutation = {
  id: 'deleteMailAutoresponderOverrideMutation' as const,
  op: 'deleteMailAutoresponderOverride',
  query: `mutation deleteMailAutoresponderOverride($workspaceId: String!, $accountId: String!) {
  deleteMailAutoresponderOverride(
    workspaceId: $workspaceId
    accountId: $accountId
  )
}`,
};

export const upsertMailRuleMutation = {
  id: 'upsertMailRuleMutation' as const,
  op: 'upsertMailRule',
  query: `mutation upsertMailRule($input: UpsertMailRuleInput!) {
  upsertMailRule(input: $input) {
    id
    accountId
    name
    requestedEnabled
    effectiveEnabled
    validationStatus
    validationError
    priority
    conditions
    actions
    updatedAt
  }
}`,
};

export const deleteMailRuleMutation = {
  id: 'deleteMailRuleMutation' as const,
  op: 'deleteMailRule',
  query: `mutation deleteMailRule($workspaceId: String!, $ruleId: String!) {
  deleteMailRule(workspaceId: $workspaceId, ruleId: $ruleId)
}`,
};

export const updateMailPolicyMutation = {
  id: 'updateMailPolicyMutation' as const,
  op: 'updateMailPolicy',
  query: `mutation updateMailPolicy($workspaceId: String!, $input: UpdateMailPolicyInput!) {
  updateMailPolicy(workspaceId: $workspaceId, input: $input) {
    workspaceId
    mailWindowDays
    availableMailWindowDays
    effectiveMailWindowDays
    retentionMode
    retentionDays
    searchIndexEnabled
    searchIndexAttachmentsEnabled
  }
}`,
};

export const rebuildMailSearchIndexMutation = {
  id: 'rebuildMailSearchIndexMutation' as const,
  op: 'rebuildMailSearchIndex',
  query: `mutation rebuildMailSearchIndex($input: RebuildMailSearchIndexInput!) {
  rebuildMailSearchIndex(input: $input) {
    jobId
    status
    queuedMessages
    skippedMessages
  }
}`,
};

export const trustMailSenderMutation = {
  id: 'trustMailSenderMutation' as const,
  op: 'trustMailSender',
  query: `mutation trustMailSender($input: TrustMailSenderInput!) {
  trustMailSender(input: $input) {
    workspaceId
    userId
    senderAddress
    createdAt
    updatedAt
  }
}`,
};

export const managedAiStatusQuery = {
  id: 'managedAiStatusQuery' as const,
  op: 'managedAiStatus',
  query: `query managedAiStatus($workspaceId: String!) {
  managedAIStatus(workspaceId: $workspaceId) {
    enabled
    consentGiven
    consentAt
    suspended
    suspendReason
    maxRequestsPerMinute
  }
}`,
};

export const addGroupMateMutation = {
  id: 'addGroupMateMutation' as const,
  op: 'addGroupMate',
  query: `mutation addGroupMate($workspaceId: String!, $groupId: String!, $mateId: String!) {
  addGroupMate(workspaceId: $workspaceId, groupId: $groupId, mateId: $mateId)
}`,
};

export const createSkillMutation = {
  id: 'createSkillMutation' as const,
  op: 'createSkill',
  query: `mutation createSkill($input: CreateSkillInput!) {
  createSkill(input: $input) {
    id
  }
}`,
};

export const createWorkspaceMateMutation = {
  id: 'createWorkspaceMateMutation' as const,
  op: 'createWorkspaceMate',
  query: `mutation createWorkspaceMate($input: CreateWorkspaceMateInput!) {
  createWorkspaceMate(input: $input) {
    id
  }
}`,
};

export const deleteMateMutation = {
  id: 'deleteMateMutation' as const,
  op: 'deleteMate',
  query: `mutation deleteMate($workspaceId: String!, $mateId: String!) {
  deleteMate(workspaceId: $workspaceId, mateId: $mateId)
}`,
};

export const deleteSkillMutation = {
  id: 'deleteSkillMutation' as const,
  op: 'deleteSkill',
  query: `mutation deleteSkill($workspaceId: String!, $skillId: String!) {
  deleteSkill(workspaceId: $workspaceId, skillId: $skillId)
}`,
};

export const getCanManageWorkspaceMatesQuery = {
  id: 'getCanManageWorkspaceMatesQuery' as const,
  op: 'getCanManageWorkspaceMates',
  query: `query getCanManageWorkspaceMates($workspaceId: String!) {
  canManageWorkspaceMates(workspaceId: $workspaceId)
}`,
};

export const getDocMateRolesQuery = {
  id: 'getDocMateRolesQuery' as const,
  op: 'getDocMateRoles',
  query: `query getDocMateRoles($workspaceId: String!, $docId: String!) {
  docMateRoles(workspaceId: $workspaceId, docId: $docId) {
    mateId
    name
    role
  }
}`,
};

export const getFolderMateRolesQuery = {
  id: 'getFolderMateRolesQuery' as const,
  op: 'getFolderMateRoles',
  query: `query getFolderMateRoles($workspaceId: String!, $folderId: String!) {
  folderMateRoles(workspaceId: $workspaceId, folderId: $folderId) {
    mateId
    name
    role
  }
}`,
};

export const getGroupMatesQuery = {
  id: 'getGroupMatesQuery' as const,
  op: 'getGroupMates',
  query: `query getGroupMates($workspaceId: String!, $groupId: String!) {
  groupMates(workspaceId: $workspaceId, groupId: $groupId) {
    mateId
    name
  }
}`,
};

export const getMateGrantTargetsQuery = {
  id: 'getMateGrantTargetsQuery' as const,
  op: 'getMateGrantTargets',
  query: `query getMateGrantTargets($workspaceId: String!) {
  mateGrantTargets(workspaceId: $workspaceId) {
    mateId
    name
  }
}`,
};

export const getMateInviteFoldersQuery = {
  id: 'getMateInviteFoldersQuery' as const,
  op: 'getMateInviteFolders',
  query: `query getMateInviteFolders($workspaceId: String!) {
  mateInviteFolders(workspaceId: $workspaceId) {
    folderId
    name
  }
}`,
};

export const getMateRulesQuery = {
  id: 'getMateRulesQuery' as const,
  op: 'getMateRules',
  query: `query getMateRules($workspaceId: String!) {
  workspace(id: $workspaceId) {
    matePersonalReadMode
    mateWorkspaceReadMode
    mateMemoryEnabled
  }
}`,
};

export const getMateShareEffectQuery = {
  id: 'getMateShareEffectQuery' as const,
  op: 'getMateShareEffect',
  query: `query getMateShareEffect($workspaceId: String!, $mateId: String!, $principalKind: MateSharePrincipalKind!, $principalId: String!) {
  mateShareEffect(
    workspaceId: $workspaceId
    mateId: $mateId
    principalKind: $principalKind
    principalId: $principalId
  ) {
    folderId
    name
    mateRole
    rights {
      read
      create
      update
      delete
    }
    read {
      allowed
      ask
      limitedBy
    }
    create {
      allowed
      ask
      limitedBy
    }
    update {
      allowed
      ask
      limitedBy
    }
    delete {
      allowed
      ask
      limitedBy
    }
  }
}`,
};

export const getMateSharesQuery = {
  id: 'getMateSharesQuery' as const,
  op: 'getMateShares',
  query: `query getMateShares($workspaceId: String!, $mateId: String!) {
  mateShares(workspaceId: $workspaceId, mateId: $mateId) {
    principalKind
    principalId
    principalName
    mode
    shareMemory
  }
}`,
};

export const getMateSkillsQuery = {
  id: 'getMateSkillsQuery' as const,
  op: 'getMateSkills',
  query: `query getMateSkills($workspaceId: String!, $mateId: String!) {
  mateSkills(workspaceId: $workspaceId, mateId: $mateId)
}`,
};

export const getSkillToolCatalogQuery = {
  id: 'getSkillToolCatalogQuery' as const,
  op: 'getSkillToolCatalog',
  query: `query getSkillToolCatalog($workspaceId: String!) {
  skillToolCatalog(workspaceId: $workspaceId) {
    category
    tools {
      name
      description
      source
    }
  }
}`,
};

export const getUsableMatesQuery = {
  id: 'getUsableMatesQuery' as const,
  op: 'getUsableMates',
  query: `query getUsableMates($workspaceId: String!) {
  usableMates(workspaceId: $workspaceId) {
    id
    kind
    name
    description
    memoryEnabled
    instructions
  }
}`,
};

export const getWorkspaceMatesQuery = {
  id: 'getWorkspaceMatesQuery' as const,
  op: 'getWorkspaceMates',
  query: `query getWorkspaceMates($workspaceId: String!) {
  workspaceMates(workspaceId: $workspaceId) {
    id
    name
    description
    instructions
    memoryEnabled
  }
}`,
};

export const getWorkspaceSkillsQuery = {
  id: 'getWorkspaceSkillsQuery' as const,
  op: 'getWorkspaceSkills',
  query: `query getWorkspaceSkills($workspaceId: String!) {
  workspaceSkills(workspaceId: $workspaceId) {
    id
    name
    description
    instructions
    toolNames
  }
}`,
};

export const grantDocMateRoleMutation = {
  id: 'grantDocMateRoleMutation' as const,
  op: 'grantDocMateRole',
  query: `mutation grantDocMateRole($workspaceId: String!, $docId: String!, $mateId: String!, $role: DocRole!) {
  grantDocMateRole(
    workspaceId: $workspaceId
    docId: $docId
    mateId: $mateId
    role: $role
  )
}`,
};

export const grantFolderMateRoleMutation = {
  id: 'grantFolderMateRoleMutation' as const,
  op: 'grantFolderMateRole',
  query: `mutation grantFolderMateRole($workspaceId: String!, $folderId: String!, $mateId: String!, $role: FolderRole!) {
  grantFolderMateRole(
    workspaceId: $workspaceId
    folderId: $folderId
    mateId: $mateId
    role: $role
  )
}`,
};

export const removeGroupMateMutation = {
  id: 'removeGroupMateMutation' as const,
  op: 'removeGroupMate',
  query: `mutation removeGroupMate($workspaceId: String!, $groupId: String!, $mateId: String!) {
  removeGroupMate(workspaceId: $workspaceId, groupId: $groupId, mateId: $mateId)
}`,
};

export const revokeDocMateRoleMutation = {
  id: 'revokeDocMateRoleMutation' as const,
  op: 'revokeDocMateRole',
  query: `mutation revokeDocMateRole($workspaceId: String!, $docId: String!, $mateId: String!) {
  revokeDocMateRole(workspaceId: $workspaceId, docId: $docId, mateId: $mateId)
}`,
};

export const revokeFolderMateRoleMutation = {
  id: 'revokeFolderMateRoleMutation' as const,
  op: 'revokeFolderMateRole',
  query: `mutation revokeFolderMateRole($workspaceId: String!, $folderId: String!, $mateId: String!) {
  revokeFolderMateRole(
    workspaceId: $workspaceId
    folderId: $folderId
    mateId: $mateId
  )
}`,
};

export const setMateSkillsMutation = {
  id: 'setMateSkillsMutation' as const,
  op: 'setMateSkills',
  query: `mutation setMateSkills($workspaceId: String!, $mateId: String!, $skillIds: [String!]!) {
  setMateSkills(workspaceId: $workspaceId, mateId: $mateId, skillIds: $skillIds)
}`,
};

export const shareMateMutation = {
  id: 'shareMateMutation' as const,
  op: 'shareMate',
  query: `mutation shareMate($input: ShareMateInput!) {
  shareMate(input: $input) {
    principalId
  }
}`,
};

export const unshareMateMutation = {
  id: 'unshareMateMutation' as const,
  op: 'unshareMate',
  query: `mutation unshareMate($workspaceId: String!, $mateId: String!, $principalKind: MateSharePrincipalKind!, $principalId: String!) {
  unshareMate(
    workspaceId: $workspaceId
    mateId: $mateId
    principalKind: $principalKind
    principalId: $principalId
  )
}`,
};

export const updateMateRulesMutation = {
  id: 'updateMateRulesMutation' as const,
  op: 'updateMateRules',
  query: `mutation updateMateRules($input: UpdateWorkspaceInput!) {
  updateWorkspace(input: $input) {
    id
  }
}`,
};

export const updateMateMutation = {
  id: 'updateMateMutation' as const,
  op: 'updateMate',
  query: `mutation updateMate($workspaceId: String!, $mateId: String!, $input: UpdateMateInput!) {
  updateMate(workspaceId: $workspaceId, mateId: $mateId, input: $input) {
    id
  }
}`,
};

export const updateSkillMutation = {
  id: 'updateSkillMutation' as const,
  op: 'updateSkill',
  query: `mutation updateSkill($input: UpdateSkillInput!) {
  updateSkill(input: $input) {
    id
  }
}`,
};

export const generateWorkspaceMcpTokenMutation = {
  id: 'generateWorkspaceMcpTokenMutation' as const,
  op: 'generateWorkspaceMcpToken',
  query: `mutation generateWorkspaceMcpToken($input: GenerateWorkspaceMcpTokenInput!) {
  generateWorkspaceMcpToken(input: $input) {
    token {
      id
      userId
      userName
      userEmail
      userRole
      workspaceId
      createdBy
      createdByName
      createdAt
      expiresAt
      maskedToken
      connectedApp
      toolPolicy {
        disabledCategories
        disabledTools
      }
    }
    config
  }
}`,
};

export const grantWorkspaceMcpAccessToGroupMutation = {
  id: 'grantWorkspaceMcpAccessToGroupMutation' as const,
  op: 'grantWorkspaceMcpAccessToGroup',
  query: `mutation grantWorkspaceMcpAccessToGroup($input: GrantWorkspaceMcpGroupInput!) {
  grantWorkspaceMcpAccessToGroup(input: $input) {
    granted
    skipped
    failed
  }
}`,
};

export const listWorkspaceMcpTokensQuery = {
  id: 'listWorkspaceMcpTokensQuery' as const,
  op: 'listWorkspaceMcpTokens',
  query: `query listWorkspaceMcpTokens($workspaceId: String!) {
  workspaceMcpTokens(workspaceId: $workspaceId) {
    id
    userId
    userName
    userEmail
    userRole
    workspaceId
    createdBy
    createdByName
    createdAt
    expiresAt
    maskedToken
    connectedApp
    toolPolicy {
      disabledCategories
      disabledTools
    }
  }
}`,
};

export const myWorkspaceMcpTokensQuery = {
  id: 'myWorkspaceMcpTokensQuery' as const,
  op: 'myWorkspaceMcpTokens',
  query: `query myWorkspaceMcpTokens($workspaceId: String!) {
  myWorkspaceMcpTokens(workspaceId: $workspaceId) {
    id
    userId
    userName
    userEmail
    userRole
    workspaceId
    createdBy
    createdByName
    createdAt
    expiresAt
    maskedToken
    connectedApp
    toolPolicy {
      disabledCategories
      disabledTools
    }
  }
}`,
};

export const requestWorkspaceMcpAccessMutation = {
  id: 'requestWorkspaceMcpAccessMutation' as const,
  op: 'requestWorkspaceMcpAccess',
  query: `mutation requestWorkspaceMcpAccess($workspaceId: String!) {
  requestWorkspaceMcpAccess(workspaceId: $workspaceId)
}`,
};

export const revealWorkspaceMcpGrantMutation = {
  id: 'revealWorkspaceMcpGrantMutation' as const,
  op: 'revealWorkspaceMcpGrant',
  query: `mutation revealWorkspaceMcpGrant($input: RevealWorkspaceMcpGrantInput!) {
  revealWorkspaceMcpGrant(input: $input) {
    tokenId
    workspaceId
    config
    expiresAt
    createdAt
  }
}`,
};

export const revokeWorkspaceMcpTokenMutation = {
  id: 'revokeWorkspaceMcpTokenMutation' as const,
  op: 'revokeWorkspaceMcpToken',
  query: `mutation revokeWorkspaceMcpToken($workspaceId: String!, $tokenId: String!) {
  revokeWorkspaceMcpToken(workspaceId: $workspaceId, tokenId: $tokenId)
}`,
};

export const memberQuotaInfoQuery = {
  id: 'memberQuotaInfoQuery' as const,
  op: 'memberQuotaInfo',
  query: `query memberQuotaInfo($workspaceId: ID!) {
  memberQuotaInfo(workspaceId: $workspaceId) {
    baseLimit
    extraSeats
    maxExtraSeats
    effectiveLimit
    currentUsage
    canInvite
    seatTiers
    storagePerSeat
  }
}`,
};

export const mentionUserMutation = {
  id: 'mentionUserMutation' as const,
  op: 'mentionUser',
  query: `mutation mentionUser($input: MentionInput!) {
  mentionUser(input: $input)
}`,
};

export const mineCopyQuery = {
  id: 'mineCopyQuery' as const,
  op: 'mineCopy',
  query: `query mineCopy($workspaceId: String!, $sourceKind: MineCopySourceKind!, $sourceId: String!) {
  mineCopy(
    workspaceId: $workspaceId
    sourceKind: $sourceKind
    sourceId: $sourceId
  ) {
    sourceExists
    copy {
      templateId
      versionNumber
      updatedAt
    }
  }
}`,
};

export const moveAppBaseDatabaseMutation = {
  id: 'moveAppBaseDatabaseMutation' as const,
  op: 'moveAppBaseDatabase',
  query: `mutation moveAppBaseDatabase($workspaceId: String!, $id: String!, $input: MoveAppBaseDatabaseInput!) {
  moveAppBaseDatabase(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const moveAppBaseMountMutation = {
  id: 'moveAppBaseMountMutation' as const,
  op: 'moveAppBaseMount',
  query: `mutation moveAppBaseMount($workspaceId: String!, $id: String!, $mountId: String!, $mountPoint: String!, $swap: Boolean, $change: AppBaseDraftChangeInput) {
  moveAppBaseMount(
    workspaceId: $workspaceId
    id: $id
    mountId: $mountId
    mountPoint: $mountPoint
    swap: $swap
    change: $change
  )
}`,
};

export const myAccessibleDocsQuery = {
  id: 'myAccessibleDocsQuery' as const,
  op: 'myAccessibleDocs',
  query: `query myAccessibleDocs($workspaceId: String!, $first: Int, $offset: Int, $docIds: [String!]) {
  workspace(id: $workspaceId) {
    docs(pagination: {first: $first, offset: $offset}, docIds: $docIds) {
      edges {
        node {
          id
          title
          trashed
          createdAt
          updatedAt
          permissionGraphVersion
          permissions {
            Doc_Copy
            Doc_Delete
            Doc_Duplicate
            Doc_Properties_Read
            Doc_Properties_Update
            Doc_Publish
            Doc_Read
            Doc_Restore
            Doc_TransferOwner
            Doc_Trash
            Doc_Update
            Doc_Users_Manage
            Doc_Users_Read
            Doc_Comments_Create
            Doc_Comments_Delete
            Doc_Comments_Read
            Doc_Comments_Resolve
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
      }
    }
  }
}`,
};

export const myNotificationPreferencesInWorkspaceQuery = {
  id: 'myNotificationPreferencesInWorkspaceQuery' as const,
  op: 'myNotificationPreferencesInWorkspace',
  query: `query myNotificationPreferencesInWorkspace($workspaceId: String!) {
  myNotificationPreferencesInWorkspace(workspaceId: $workspaceId) {
    triggerType
    category
    i18nKey
    descriptionI18nKey
    requiredRole
    enabled
    channelMail
    channelInApp
    channelWebPush
    supportsMail
    supportsInApp
    supportsWebPush
    accountId
    folderKey
    quietHours
    thresholds
    defaultThresholds
    stored
  }
}`,
};

export const myTenantsQuery = {
  id: 'myTenantsQuery' as const,
  op: 'myTenants',
  query: `query myTenants {
  myTenants {
    id
    name
    slug
    billingEmail
    stripeCustomerId
    createdAt
    updatedAt
    members {
      id
      userId
      role
      createdAt
    }
  }
}`,
};

export const notificationCountQuery = {
  id: 'notificationCountQuery' as const,
  op: 'notificationCount',
  query: `query notificationCount {
  currentUser {
    notificationCount
  }
}`,
};

export const onboardingStatusQuery = {
  id: 'onboardingStatusQuery' as const,
  op: 'onboardingStatus',
  query: `query onboardingStatus {
  onboardingStatus {
    needsOnboarding
    reason
    invitedWorkspaceId
    inviteId
    revokedWorkspaceName
    revocationNotificationId
  }
}`,
};

export const pdfPageSearchQuery = {
  id: 'pdfPageSearchQuery' as const,
  op: 'pdfPageSearch',
  query: `query pdfPageSearch($id: String!, $input: PdfPageSearchInput!) {
  workspace(id: $id) {
    pdfSearch(input: $input) {
      docId
      attachmentBlockId
      pageIndex
      source
      coverageStatus
      visibility
      technicalSourceKind
      snippet
      score
      rects {
        x
        y
        width
        height
      }
    }
  }
}`,
};

export const pendingIntervalChangesQuery = {
  id: 'pendingIntervalChangesQuery' as const,
  op: 'pendingIntervalChanges',
  query: `query pendingIntervalChanges($workspaceId: String!) {
  pendingIntervalChanges(workspaceId: $workspaceId) {
    type
    newInterval
    effectiveDate
    creditPackId
  }
}`,
};

export const permanentlyDeleteAppMutation = {
  id: 'permanentlyDeleteAppMutation' as const,
  op: 'permanentlyDeleteApp',
  query: `mutation permanentlyDeleteApp($workspaceId: String!, $id: String!, $appId: String!, $tables: [AppBaseTableDispositionInput!]!) {
  permanentlyDeleteApp(
    workspaceId: $workspaceId
    id: $id
    appId: $appId
    tables: $tables
  )
}`,
};

export const permanentlyDeleteDocMutation = {
  id: 'permanentlyDeleteDocMutation' as const,
  op: 'permanentlyDeleteDoc',
  query: `mutation permanentlyDeleteDoc($workspaceId: String!, $docId: String!) {
  permanentlyDeleteDoc(workspaceId: $workspaceId, docId: $docId)
}`,
};

export const planAppBaseDevelopmentMutation = {
  id: 'planAppBaseDevelopmentMutation' as const,
  op: 'planAppBaseDevelopment',
  query: `mutation planAppBaseDevelopment($workspaceId: String!, $input: JSONObject!) {
  planAppBaseDevelopment(workspaceId: $workspaceId, input: $input)
}`,
};

export const planChangePriceQuery = {
  id: 'planChangePriceQuery' as const,
  op: 'planChangePrice',
  query: `query planChangePrice($plan: String!, $recurring: SubscriptionRecurring!, $targetPlan: String) {
  planChangePrice(plan: $plan, recurring: $recurring, targetPlan: $targetPlan) {
    priceId
    amount
    currency
    interval
    kind
  }
}`,
};

export const planRolesQuery = {
  id: 'planRolesQuery' as const,
  op: 'planRoles',
  query: `query planRoles {
  planRoles {
    basePlan
    demoPlan
  }
}`,
};

export const preflightDocCreateQuery = {
  id: 'preflightDocCreateQuery' as const,
  op: 'preflightDocCreate',
  query: `query preflightDocCreate($input: DocCreatePreflightInput!) {
  preflightDocCreate(input: $input) {
    allowed
    reason
    targetFolderId
  }
}`,
};

export const prepareDocStripQuery = {
  id: 'prepareDocStripQuery' as const,
  op: 'prepareDocStrip',
  query: `query prepareDocStrip($input: StripDocHistoryInput!) {
  prepareDocStrip(input: $input) {
    snapshotSizeBytes
    historySizeBytes
    historyCount
    mergedSizeBytes
    reductionPercent
  }
}`,
};

export const pricesQuery = {
  id: 'pricesQuery' as const,
  op: 'prices',
  query: `query prices {
  prices {
    type
    plan
    currency
    amount
    yearlyAmount
    lifetimeAmount
    launchAmount
    launchYearlyAmount
  }
}`,
};

export const pricingPlansQuery = {
  id: 'pricingPlansQuery' as const,
  op: 'pricingPlans',
  query: `query pricingPlans($language: String = "de") {
  pricingPlans(language: $language) {
    productName
    isFreePlan
    isBasePlan
    trialDays
    description
    features
    ctaType
    badge
    featured
    displayOrder
    prices {
      priceId
      interval
      amount
      currency
      kind
    }
    launchOffer {
      endsAt
      paidMonths
      seatTiers
    }
    quotas {
      storage
      storagePerMember
      storagePerSeat
      blobLimit
      memberLimit
      guestLimit
      externalAccessPerDocLimit
      historyDays
      auditTrailRetentionDays
      monthlyCreditGrant
      maxExtraSeats
      seatTiers
    }
  }
}`,
};

export const publishPageMutation = {
  id: 'publishPageMutation' as const,
  op: 'publishPage',
  query: `mutation publishPage($workspaceId: String!, $pageId: String!, $mode: PublicDocMode = Page) {
  publishDoc(workspaceId: $workspaceId, docId: $pageId, mode: $mode) {
    id
    mode
  }
}`,
};

export const publishAppBaseDraftMutation = {
  id: 'publishAppBaseDraftMutation' as const,
  op: 'publishAppBaseDraft',
  query: `mutation publishAppBaseDraft($workspaceId: String!, $appBaseId: String!, $version: Int!) {
  publishAppBaseDraft(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    version: $version
  ) {
    id
    version
    role
    active
    publishedAt
  }
}`,
};

export const publishAppContainerToMineMutation = {
  id: 'publishAppContainerToMineMutation' as const,
  op: 'publishAppContainerToMine',
  query: `mutation publishAppContainerToMine($workspaceId: String!, $appBaseId: String!, $fromTrash: Boolean = false) {
  publishAppContainerToMine(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    fromTrash: $fromTrash
  ) {
    templateId
    versionNumber
  }
}`,
};

export const publishAutomationToMineMutation = {
  id: 'publishAutomationToMineMutation' as const,
  op: 'publishAutomationToMine',
  query: `mutation publishAutomationToMine($workspaceId: String!, $automationBlockId: String!) {
  publishAutomationToMine(
    workspaceId: $workspaceId
    automationBlockId: $automationBlockId
  ) {
    templateId
    versionNumber
  }
}`,
};

export const publishContentEntriesMutation = {
  id: 'publishContentEntriesMutation' as const,
  op: 'publishContentEntries',
  query: `mutation publishContentEntries($workspaceId: String!, $appBaseId: String!, $sourceKey: String!, $rowIds: [String!]!) {
  publishContentEntries(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
    rowIds: $rowIds
  ) {
    rowId
    status
    liveVersion
    hasOpenChanges
  }
}`,
};

export const publishTrashedAppToMineMutation = {
  id: 'publishTrashedAppToMineMutation' as const,
  op: 'publishTrashedAppToMine',
  query: `mutation publishTrashedAppToMine($workspaceId: String!, $id: String!, $appId: String!) {
  publishTrashedAppToMine(workspaceId: $workspaceId, id: $id, appId: $appId) {
    templateId
    versionNumber
  }
}`,
};

export const publishWorkflowToMineMutation = {
  id: 'publishWorkflowToMineMutation' as const,
  op: 'publishWorkflowToMine',
  query: `mutation publishWorkflowToMine($workspaceId: String!, $workflowId: String!) {
  publishWorkflowToMine(workspaceId: $workspaceId, workflowId: $workflowId) {
    templateId
    versionNumber
  }
}`,
};

export const purchaseExtraSeatsMutation = {
  id: 'purchaseExtraSeatsMutation' as const,
  op: 'purchaseExtraSeats',
  query: `mutation purchaseExtraSeats($workspaceId: ID!, $quantity: Int!, $withdrawalConsent: Boolean!) {
  purchaseExtraSeats(
    workspaceId: $workspaceId
    quantity: $quantity
    withdrawalConsent: $withdrawalConsent
  ) {
    success
    addon {
      quantity
      status
      pricingTiersMonthly
      pricingTiersYearly
      totalMonthly
    }
  }
}`,
};

export const quotaQuery = {
  id: 'quotaQuery' as const,
  op: 'quota',
  query: `query quota {
  currentUser {
    id
    quota {
      name
      blobLimit
      storageQuota
      historyPeriod
      memberLimit
      humanReadable {
        name
        blobLimit
        storageQuota
        historyPeriod
        memberLimit
      }
    }
    quotaUsage {
      storageQuota
    }
  }
}`,
  deprecations: ["'storageQuota' is deprecated: use `UserQuotaType['usedStorageQuota']` instead"],
};

export const reactivateWorkspaceMutation = {
  id: 'reactivateWorkspaceMutation' as const,
  op: 'reactivateWorkspace',
  query: `mutation reactivateWorkspace($id: String!) {
  reactivateWorkspace(id: $id) {
    id
  }
}`,
};

export const readAllNotificationsMutation = {
  id: 'readAllNotificationsMutation' as const,
  op: 'readAllNotifications',
  query: `mutation readAllNotifications {
  readAllNotifications
}`,
};

export const readNotificationMutation = {
  id: 'readNotificationMutation' as const,
  op: 'readNotification',
  query: `mutation readNotification($id: String!) {
  readNotification(id: $id)
}`,
};

export const reassignAppBaseRowOwnersMutation = {
  id: 'reassignAppBaseRowOwnersMutation' as const,
  op: 'reassignAppBaseRowOwners',
  query: `mutation reassignAppBaseRowOwners($input: ReassignAppBaseRowOwnersInput!) {
  reassignAppBaseRowOwners(input: $input) {
    reassignedRows
    remainingRows
  }
}`,
};

export const rebindAppBaseDatabaseMutation = {
  id: 'rebindAppBaseDatabaseMutation' as const,
  op: 'rebindAppBaseDatabase',
  query: `mutation rebindAppBaseDatabase($workspaceId: String!, $id: String!, $input: RebindAppBaseDatabaseInput!) {
  rebindAppBaseDatabase(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const recoverDocMutation = {
  id: 'recoverDocMutation' as const,
  op: 'recoverDoc',
  query: `mutation recoverDoc($workspaceId: String!, $docId: String!, $timestamp: DateTime!) {
  recoverDoc(workspaceId: $workspaceId, guid: $docId, timestamp: $timestamp)
}`,
};

export const refreshAppBasePublishedContentMutation = {
  id: 'refreshAppBasePublishedContentMutation' as const,
  op: 'refreshAppBasePublishedContent',
  query: `mutation refreshAppBasePublishedContent($workspaceId: String!, $appBaseId: String!) {
  refreshAppBasePublishedContent(workspaceId: $workspaceId, appBaseId: $appBaseId)
}`,
};

export const registerCustomDomainMutation = {
  id: 'registerCustomDomainMutation' as const,
  op: 'registerCustomDomain',
  query: `mutation registerCustomDomain($workspaceId: String!, $domain: String!, $appBaseId: String!) {
  registerCustomDomain(
    workspaceId: $workspaceId
    domain: $domain
    appBaseId: $appBaseId
  ) {
    id
    domain
    type
    status
    verificationToken
    verifiedAt
    sslStatus
    appBaseId
    createdAt
  }
}`,
};

export const registerSubdomainMutation = {
  id: 'registerSubdomainMutation' as const,
  op: 'registerSubdomain',
  query: `mutation registerSubdomain($workspaceId: String!, $subdomain: String!, $appBaseId: String!) {
  registerSubdomain(
    workspaceId: $workspaceId
    subdomain: $subdomain
    appBaseId: $appBaseId
  ) {
    id
    domain
    type
    status
    sslStatus
    appBaseId
    createdAt
  }
}`,
};

export const reimportOnboardingDocMutation = {
  id: 'reimportOnboardingDocMutation' as const,
  op: 'reimportOnboardingDoc',
  query: `mutation reimportOnboardingDoc($workspaceId: String!, $locale: String) {
  reimportOnboardingDoc(workspaceId: $workspaceId, locale: $locale)
}`,
};

export const reindexWorkspaceEmbeddingsMutation = {
  id: 'reindexWorkspaceEmbeddingsMutation' as const,
  op: 'reindexWorkspaceEmbeddings',
  query: `mutation reindexWorkspaceEmbeddings($workspaceId: String!, $docIds: [String!]) {
  reindexWorkspaceEmbeddings(workspaceId: $workspaceId, docIds: $docIds) {
    deletedChunks
    queuedDocs
  }
}`,
};

export const releaseBrandedSubdomainMutation = {
  id: 'releaseBrandedSubdomainMutation' as const,
  op: 'releaseBrandedSubdomain',
  query: `mutation releaseBrandedSubdomain($workspaceId: String!) {
  releaseBrandedSubdomain(workspaceId: $workspaceId) {
    effectiveHost
  }
}`,
};

export const remindWorkspaceTwoFactorMembersMutation = {
  id: 'remindWorkspaceTwoFactorMembersMutation' as const,
  op: 'remindWorkspaceTwoFactorMembers',
  query: `mutation remindWorkspaceTwoFactorMembers($workspaceId: String!) {
  remindWorkspaceTwoFactorMembers(workspaceId: $workspaceId)
}`,
};

export const removeAvatarMutation = {
  id: 'removeAvatarMutation' as const,
  op: 'removeAvatar',
  query: `mutation removeAvatar {
  removeAvatar {
    success
  }
}`,
};

export const removeCustomDomainMutation = {
  id: 'removeCustomDomainMutation' as const,
  op: 'removeCustomDomain',
  query: `mutation removeCustomDomain($workspaceId: String!, $domainId: String!) {
  removeCustomDomain(workspaceId: $workspaceId, domainId: $domainId)
}`,
};

export const removeSeatAddonMutation = {
  id: 'removeSeatAddonMutation' as const,
  op: 'removeSeatAddon',
  query: `mutation removeSeatAddon($workspaceId: ID!) {
  removeSeatAddon(workspaceId: $workspaceId)
}`,
};

export const removeTenantMemberMutation = {
  id: 'removeTenantMemberMutation' as const,
  op: 'removeTenantMember',
  query: `mutation removeTenantMember($tenantId: ID!, $userId: String!) {
  removeTenantMember(tenantId: $tenantId, userId: $userId)
}`,
};

export const renameBrandedSubdomainMutation = {
  id: 'renameBrandedSubdomainMutation' as const,
  op: 'renameBrandedSubdomain',
  query: `mutation renameBrandedSubdomain($workspaceId: String!, $newLabel: String!) {
  renameBrandedSubdomain(workspaceId: $workspaceId, newLabel: $newLabel) {
    effectiveHost
  }
}`,
};

export const reorderAppBaseMountsMutation = {
  id: 'reorderAppBaseMountsMutation' as const,
  op: 'reorderAppBaseMounts',
  query: `mutation reorderAppBaseMounts($workspaceId: String!, $id: String!, $mountIds: [String!]!, $change: AppBaseDraftChangeInput) {
  reorderAppBaseMounts(
    workspaceId: $workspaceId
    id: $id
    mountIds: $mountIds
    change: $change
  )
}`,
};

export const reportCorruptionMutation = {
  id: 'reportCorruptionMutation' as const,
  op: 'reportCorruption',
  query: `mutation reportCorruption($input: ReportCorruptionInput!) {
  reportCorruption(input: $input) {
    id
    workspaceId
    docId
    status
    createdAt
  }
}`,
};

export const resolvePendingDecisionMutation = {
  id: 'resolvePendingDecisionMutation' as const,
  op: 'resolvePendingDecision',
  query: `mutation resolvePendingDecision($workspaceId: String!, $id: String!, $resolvedWith: String!) {
  resolvePendingDecision(
    workspaceId: $workspaceId
    id: $id
    resolvedWith: $resolvedWith
  ) {
    id
    configId
    sourceRowId
    targetColumn
    searchValue
    matchCount
    candidates {
      id
      title
      context
    }
    status
    createdAt
  }
}`,
};

export const restoreAppBaseModuleMutation = {
  id: 'restoreAppBaseModuleMutation' as const,
  op: 'restoreAppBaseModule',
  query: `mutation restoreAppBaseModule($workspaceId: String!, $appBaseId: String!, $version: Int!, $mountId: String!) {
  restoreAppBaseModule(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    version: $version
    mountId: $mountId
  ) {
    id
    version
    role
    active
    viewConfig
  }
}`,
};

export const restoreAppBaseVersionMutation = {
  id: 'restoreAppBaseVersionMutation' as const,
  op: 'restoreAppBaseVersion',
  query: `mutation restoreAppBaseVersion($workspaceId: String!, $appBaseId: String!, $version: Int!) {
  restoreAppBaseVersion(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    version: $version
  ) {
    id
    version
    role
    active
    viewConfig
  }
}`,
};

export const restoreAppMutation = {
  id: 'restoreAppMutation' as const,
  op: 'restoreApp',
  query: `mutation restoreApp($workspaceId: String!, $id: String!, $appId: String!, $change: AppBaseDraftChangeInput) {
  restoreApp(workspaceId: $workspaceId, id: $id, appId: $appId, change: $change)
}`,
};

export const restoreContentEntryVersionMutation = {
  id: 'restoreContentEntryVersionMutation' as const,
  op: 'restoreContentEntryVersion',
  query: `mutation restoreContentEntryVersion($workspaceId: String!, $appBaseId: String!, $sourceKey: String!, $rowId: String!, $version: Int!) {
  restoreContentEntryVersion(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    sourceKey: $sourceKey
    rowId: $rowId
    version: $version
  ) {
    rowId
    status
    liveVersion
    hasOpenChanges
  }
}`,
};

export const restoreDocMutation = {
  id: 'restoreDocMutation' as const,
  op: 'restoreDoc',
  query: `mutation restoreDoc($workspaceId: String!, $docId: String!) {
  restoreDoc(workspaceId: $workspaceId, docId: $docId) {
    id
    trashed
  }
}`,
};

export const resumeSubscriptionMutation = {
  id: 'resumeSubscriptionMutation' as const,
  op: 'resumeSubscription',
  query: `mutation resumeSubscription($plan: String!, $workspaceId: String) {
  resumeSubscription(plan: $plan, workspaceId: $workspaceId) {
    id
    status
    nextBillAt
    start
    end
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const revokeDocUserRolesMutation = {
  id: 'revokeDocUserRolesMutation' as const,
  op: 'revokeDocUserRoles',
  query: `mutation revokeDocUserRoles($input: RevokeDocUserRoleInput!) {
  revokeDocUserRoles(input: $input)
}`,
};

export const revokeMemberPermissionMutation = {
  id: 'revokeMemberPermissionMutation' as const,
  op: 'revokeMemberPermission',
  query: `mutation revokeMemberPermission($workspaceId: String!, $userId: String!) {
  revokeMember(workspaceId: $workspaceId, userId: $userId)
}`,
};

export const revokePublicPageMutation = {
  id: 'revokePublicPageMutation' as const,
  op: 'revokePublicPage',
  query: `mutation revokePublicPage($workspaceId: String!, $pageId: String!) {
  revokePublicDoc(workspaceId: $workspaceId, docId: $pageId) {
    id
    mode
    public
  }
}`,
};

export const scheduleAccountDeletionMutation = {
  id: 'scheduleAccountDeletionMutation' as const,
  op: 'scheduleAccountDeletion',
  query: `mutation scheduleAccountDeletion($reason: String) {
  scheduleAccountDeletion(reason: $reason) {
    scheduled
    scheduledAt
    finalizesAt
    reactivatable
    gracePeriodDays
  }
}`,
};

export const sendBlockInviteMutation = {
  id: 'sendBlockInviteMutation' as const,
  op: 'sendBlockInvite',
  query: `mutation sendBlockInvite($input: BlockInviteInput!) {
  sendBlockInvite(input: $input) {
    createdCount
    skippedCount
    userIds
    ineligible {
      id
      type
      reason
      detail
    }
  }
}`,
};

export const sendChangeEmailMutation = {
  id: 'sendChangeEmailMutation' as const,
  op: 'sendChangeEmail',
  query: `mutation sendChangeEmail($callbackUrl: String!) {
  sendChangeEmail(callbackUrl: $callbackUrl)
}`,
};

export const sendChangePasswordEmailMutation = {
  id: 'sendChangePasswordEmailMutation' as const,
  op: 'sendChangePasswordEmail',
  query: `mutation sendChangePasswordEmail($callbackUrl: String!) {
  sendChangePasswordEmail(callbackUrl: $callbackUrl)
}`,
};

export const sendSetPasswordEmailMutation = {
  id: 'sendSetPasswordEmailMutation' as const,
  op: 'sendSetPasswordEmail',
  query: `mutation sendSetPasswordEmail($callbackUrl: String!) {
  sendSetPasswordEmail(callbackUrl: $callbackUrl)
}`,
};

export const sendVerifyChangeEmailMutation = {
  id: 'sendVerifyChangeEmailMutation' as const,
  op: 'sendVerifyChangeEmail',
  query: `mutation sendVerifyChangeEmail($token: String!, $email: String!, $callbackUrl: String!) {
  sendVerifyChangeEmail(token: $token, email: $email, callbackUrl: $callbackUrl)
}`,
};

export const sendVerifyEmailMutation = {
  id: 'sendVerifyEmailMutation' as const,
  op: 'sendVerifyEmail',
  query: `mutation sendVerifyEmail($callbackUrl: String!) {
  sendVerifyEmail(callbackUrl: $callbackUrl)
}`,
};

export const serverConfigQuery = {
  id: 'serverConfigQuery' as const,
  op: 'serverConfig',
  query: `query serverConfig {
  serverConfig {
    version
    baseUrl
    name
    features
    type
    initialized
    syncUrl
    credentialsRequirement {
      ...CredentialsRequirements
    }
  }
}
${passwordLimitsFragment}
${credentialsRequirementsFragment}`,
};

export const setAppBaseAccessMutation = {
  id: 'setAppBaseAccessMutation' as const,
  op: 'setAppBaseAccess',
  query: `mutation setAppBaseAccess($workspaceId: String!, $id: String!, $edit: JSONObject!, $change: AppBaseDraftChangeInput) {
  setAppBaseAccess(
    workspaceId: $workspaceId
    id: $id
    edit: $edit
    change: $change
  )
}`,
};

export const setAppBaseBandwidthBudgetMutation = {
  id: 'setAppBaseBandwidthBudgetMutation' as const,
  op: 'setAppBaseBandwidthBudget',
  query: `mutation setAppBaseBandwidthBudget($input: SetAppBaseBandwidthBudgetInput!) {
  setAppBaseBandwidthBudget(input: $input) {
    id
    workspaceId
    appBaseId
    monthlyLimitMicro
    monthToDateUsedMicro
    alertThresholds
    behavior
    createdAt
    updatedAt
  }
}`,
};

export const setAppBaseFrameMountMutation = {
  id: 'setAppBaseFrameMountMutation' as const,
  op: 'setAppBaseFrameMount',
  query: `mutation setAppBaseFrameMount($workspaceId: String!, $id: String!, $mountId: String, $change: AppBaseDraftChangeInput) {
  setAppBaseFrameMount(
    workspaceId: $workspaceId
    id: $id
    mountId: $mountId
    change: $change
  )
}`,
};

export const setAppBaseMountNavigationMutation = {
  id: 'setAppBaseMountNavigationMutation' as const,
  op: 'setAppBaseMountNavigation',
  query: `mutation setAppBaseMountNavigation($workspaceId: String!, $id: String!, $mountId: String!, $label: String, $placement: String!, $design: String, $change: AppBaseDraftChangeInput) {
  setAppBaseMountNavigation(
    workspaceId: $workspaceId
    id: $id
    mountId: $mountId
    label: $label
    placement: $placement
    design: $design
    change: $change
  )
}`,
};

export const setInitialPasswordMutation = {
  id: 'setInitialPasswordMutation' as const,
  op: 'setInitialPassword',
  query: `mutation setInitialPassword($password: String!) {
  setInitialPassword(password: $password)
}`,
};

export const setMyNotificationPreferenceMutation = {
  id: 'setMyNotificationPreferenceMutation' as const,
  op: 'setMyNotificationPreference',
  query: `mutation setMyNotificationPreference($input: SetMyNotificationPreferenceInput!) {
  setMyNotificationPreference(input: $input) {
    triggerType
    category
    i18nKey
    descriptionI18nKey
    requiredRole
    enabled
    channelMail
    channelInApp
    channelWebPush
    supportsMail
    supportsInApp
    supportsWebPush
    accountId
    folderKey
    quietHours
    thresholds
    defaultThresholds
    stored
  }
}`,
};

export const setWorkspacePublicByIdMutation = {
  id: 'setWorkspacePublicByIdMutation' as const,
  op: 'setWorkspacePublicById',
  query: `mutation setWorkspacePublicById($id: ID!, $public: Boolean!) {
  updateWorkspace(input: {id: $id, public: $public}) {
    id
  }
}`,
};

export const skipPendingDecisionMutation = {
  id: 'skipPendingDecisionMutation' as const,
  op: 'skipPendingDecision',
  query: `mutation skipPendingDecision($workspaceId: String!, $id: String!) {
  skipPendingDecision(workspaceId: $workspaceId, id: $id) {
    id
    configId
    sourceRowId
    targetColumn
    searchValue
    matchCount
    candidates {
      id
      title
      context
    }
    status
    createdAt
  }
}`,
};

export const stepAppBaseVersionMutation = {
  id: 'stepAppBaseVersionMutation' as const,
  op: 'stepAppBaseVersion',
  query: `mutation stepAppBaseVersion($workspaceId: String!, $appBaseId: String!, $step: AppBaseVersionStep!) {
  stepAppBaseVersion(
    workspaceId: $workspaceId
    appBaseId: $appBaseId
    step: $step
  ) {
    id
    version
    role
    active
  }
}`,
};

export const stripDocHistoryMutation = {
  id: 'stripDocHistoryMutation' as const,
  op: 'stripDocHistory',
  query: `mutation stripDocHistory($input: StripDocHistoryInput!) {
  stripDocHistory(input: $input) {
    success
    originalTotalBytes
    newSizeBytes
    historiesDeleted
    reductionPercent
    error
  }
}`,
};

export const stripeUserSubscriptionsQuery = {
  id: 'stripeUserSubscriptionsQuery' as const,
  op: 'stripeUserSubscriptions',
  query: `query stripeUserSubscriptions {
  stripeUserSubscriptions {
    subscriptionId
    productName
    status
    interval
    unitAmount
    currency
    currentPeriodStart
    currentPeriodEnd
    cancelAtPeriodEnd
  }
}`,
};

export const refreshSubscriptionMutation = {
  id: 'refreshSubscriptionMutation' as const,
  op: 'refreshSubscription',
  query: `mutation refreshSubscription {
  refreshUserSubscriptions {
    id
    status
    plan
    recurring
    start
    end
    nextBillAt
    canceledAt
    variant
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const subscriptionQuery = {
  id: 'subscriptionQuery' as const,
  op: 'subscription',
  query: `query subscription {
  currentUser {
    id
    subscriptions {
      id
      status
      plan
      isBasePlan
      recurring
      start
      end
      trialStart
      trialEnd
      nextBillAt
      canceledAt
      variant
      canWithdraw
      withdrawalEligibleUntil
      withdrawalUnavailableReason
    }
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const switchPlanIntervalMutation = {
  id: 'switchPlanIntervalMutation' as const,
  op: 'switchPlanInterval',
  query: `mutation switchPlanInterval($plan: String!, $recurring: SubscriptionRecurring!, $targetPlan: String, $withdrawalConsent: Boolean) {
  switchPlanInterval(
    plan: $plan
    recurring: $recurring
    targetPlan: $targetPlan
    withdrawalConsent: $withdrawalConsent
  ) {
    id
    status
    plan
    recurring
    start
    end
    nextBillAt
  }
}`,
  deprecations: ["'id' is deprecated: removed"],
};

export const tagDocMutation = {
  id: 'tagDocMutation' as const,
  op: 'tagDoc',
  query: `mutation tagDoc($workspaceId: String!, $docId: String!, $tagId: String!) {
  tagDoc(workspaceId: $workspaceId, docId: $docId, tagId: $tagId)
}`,
};

export const tenantQuery = {
  id: 'tenantQuery' as const,
  op: 'tenant',
  query: `query tenant($id: ID!) {
  tenant(id: $id) {
    id
    name
    slug
    billingEmail
    stripeCustomerId
    createdAt
    updatedAt
    members {
      id
      userId
      role
      createdAt
    }
  }
}`,
};

export const adminDisableTotpMutation = {
  id: 'adminDisableTotpMutation' as const,
  op: 'adminDisableTotp',
  query: `mutation adminDisableTotp($userId: String!) {
  adminDisableTOTP(userId: $userId)
}`,
};

export const disableTotpMutation = {
  id: 'disableTotpMutation' as const,
  op: 'disableTotp',
  query: `mutation disableTotp($password: String!, $code: String!) {
  disableTOTP(password: $password, code: $code)
}`,
};

export const enableTotpMutation = {
  id: 'enableTotpMutation' as const,
  op: 'enableTotp',
  query: `mutation enableTotp($password: String) {
  enableTOTP(password: $password) {
    qrCodeDataUrl
    secretKey
    otpauthUri
  }
}`,
};

export const regenerateBackupCodesMutation = {
  id: 'regenerateBackupCodesMutation' as const,
  op: 'regenerateBackupCodes',
  query: `mutation regenerateBackupCodes($password: String!) {
  regenerateBackupCodes(password: $password)
}`,
};

export const revokeAllDeviceTrustsMutation = {
  id: 'revokeAllDeviceTrustsMutation' as const,
  op: 'revokeAllDeviceTrusts',
  query: `mutation revokeAllDeviceTrusts {
  revokeAllDeviceTrusts
}`,
};

export const revokeDeviceTrustMutation = {
  id: 'revokeDeviceTrustMutation' as const,
  op: 'revokeDeviceTrust',
  query: `mutation revokeDeviceTrust($trustId: String!) {
  revokeDeviceTrust(trustId: $trustId)
}`,
};

export const sendEmailOtpMutation = {
  id: 'sendEmailOtpMutation' as const,
  op: 'sendEmailOtp',
  query: `mutation sendEmailOtp {
  sendEmailOTP
}`,
};

export const setPreferredTwoFactorMethodMutation = {
  id: 'setPreferredTwoFactorMethodMutation' as const,
  op: 'setPreferredTwoFactorMethod',
  query: `mutation setPreferredTwoFactorMethod($method: String!) {
  setPreferredTwoFactorMethod(method: $method)
}`,
};

export const totpStatusQuery = {
  id: 'totpStatusQuery' as const,
  op: 'totpStatus',
  query: `query totpStatus {
  totpStatus {
    enabled
    preferredMethod
    verifiedAt
    hasBackupCodes
    backupCodesCount
  }
}`,
};

export const trustedDevicesQuery = {
  id: 'trustedDevicesQuery' as const,
  op: 'trustedDevices',
  query: `query trustedDevices {
  trustedDevices {
    id
    label
    createdAt
    expiresAt
  }
}`,
};

export const verifyBackupCodeLoginMutation = {
  id: 'verifyBackupCodeLoginMutation' as const,
  op: 'verifyBackupCodeLogin',
  query: `mutation verifyBackupCodeLogin($code: String!, $trustDevice: Boolean) {
  verifyBackupCodeLogin(code: $code, trustDevice: $trustDevice) {
    success
    localDataLoginProof
    localDataAccountId
    backupCodesRemaining
  }
}`,
};

export const verifyEmailOtpLoginMutation = {
  id: 'verifyEmailOtpLoginMutation' as const,
  op: 'verifyEmailOtpLogin',
  query: `mutation verifyEmailOtpLogin($code: String!, $trustDevice: Boolean) {
  verifyEmailOTPLogin(code: $code, trustDevice: $trustDevice) {
    success
    localDataLoginProof
    localDataAccountId
    backupCodesRemaining
  }
}`,
};

export const verifyTotpLoginMutation = {
  id: 'verifyTotpLoginMutation' as const,
  op: 'verifyTotpLogin',
  query: `mutation verifyTotpLogin($code: String!, $trustDevice: Boolean) {
  verifyTOTPLogin(code: $code, trustDevice: $trustDevice) {
    success
    localDataLoginProof
    localDataAccountId
    backupCodesRemaining
  }
}`,
};

export const verifyTotpSetupMutation = {
  id: 'verifyTotpSetupMutation' as const,
  op: 'verifyTotpSetup',
  query: `mutation verifyTotpSetup($code: String!) {
  verifyTOTPSetup(code: $code)
}`,
};

export const translateAppBaseMetadataMutation = {
  id: 'translateAppBaseMetadataMutation' as const,
  op: 'translateAppBaseMetadata',
  query: `mutation translateAppBaseMetadata($input: TranslateAppBaseMetadataInput!) {
  translateAppBaseMetadata(input: $input) {
    appBaseId
    workspaceId
    translatedLocales
    skippedLocales
    jobId
    microCreditsCharged
  }
}`,
};

export const trashDocMutation = {
  id: 'trashDocMutation' as const,
  op: 'trashDoc',
  query: `mutation trashDoc($workspaceId: String!, $docId: String!) {
  trashDoc(workspaceId: $workspaceId, docId: $docId) {
    id
    trashed
  }
}`,
};

export const trashedAppsQuery = {
  id: 'trashedAppsQuery' as const,
  op: 'trashedApps',
  query: `query trashedApps($workspaceId: String!) {
  trashedApps(workspaceId: $workspaceId) {
    appBaseId
    appId
    title
    trashedAt
    mountPoint
  }
}`,
};

export const trialEligibleQuery = {
  id: 'trialEligibleQuery' as const,
  op: 'trialEligible',
  query: `query trialEligible {
  trialEligible
}`,
};

export const unmountAppMutation = {
  id: 'unmountAppMutation' as const,
  op: 'unmountApp',
  query: `mutation unmountApp($workspaceId: String!, $id: String!, $mountId: String!, $change: AppBaseDraftChangeInput) {
  unmountApp(
    workspaceId: $workspaceId
    id: $id
    mountId: $mountId
    change: $change
  )
}`,
};

export const unpublishAppBaseMutation = {
  id: 'unpublishAppBaseMutation' as const,
  op: 'unpublishAppBase',
  query: `mutation unpublishAppBase($workspaceId: String!, $appBaseId: String!) {
  unpublishAppBase(workspaceId: $workspaceId, appBaseId: $appBaseId) {
    id
    version
    role
    active
    publishedAt
  }
}`,
};

export const untagDocMutation = {
  id: 'untagDocMutation' as const,
  op: 'untagDoc',
  query: `mutation untagDoc($workspaceId: String!, $docId: String!, $tagId: String!) {
  untagDoc(workspaceId: $workspaceId, docId: $docId, tagId: $tagId)
}`,
};

export const updateAiConsentMutation = {
  id: 'updateAiConsentMutation' as const,
  op: 'updateAiConsent',
  query: `mutation updateAiConsent($workspaceId: String!, $consent: Boolean!) {
  updateAIConsent(workspaceId: $workspaceId, consent: $consent) {
    enabled
    consentGiven
    consentAt
    suspended
    suspendReason
    maxRequestsPerMinute
  }
}`,
};

export const updateAppBaseAieoMutation = {
  id: 'updateAppBaseAieoMutation' as const,
  op: 'updateAppBaseAieo',
  query: `mutation updateAppBaseAieo($input: UpdateAppBaseAIEOInput!) {
  updateAppBaseAIEO(input: $input) {
    appBaseId
    workspaceId
    crawlerPolicy
    crawlerPolicyDetails
    declaration
    trainingDataConsent
    attributionUrl
    llmsTextOverride
    updatedAt
  }
}`,
};

export const updateAppBaseBriefMutation = {
  id: 'updateAppBaseBriefMutation' as const,
  op: 'updateAppBaseBrief',
  query: `mutation updateAppBaseBrief($workspaceId: String!, $id: String!, $input: UpdateAppBaseBriefInput!) {
  updateAppBaseBrief(workspaceId: $workspaceId, id: $id, input: $input)
}`,
};

export const updateAppBaseSeoMutation = {
  id: 'updateAppBaseSeoMutation' as const,
  op: 'updateAppBaseSeo',
  query: `mutation updateAppBaseSeo($input: UpdateAppBaseSEOInput!) {
  updateAppBaseSEO(input: $input) {
    appBaseId
    workspaceId
    titlesI18n
    descriptionsI18n
    slugsI18n
    ogImagesI18n
    indexing
    canonicalUrlOverride
    jsonLdType
    twitterCardType
    robotsDirectives
    structuredData
    updatedAt
  }
}`,
};

export const updateAppBaseMutation = {
  id: 'updateAppBaseMutation' as const,
  op: 'updateAppBase',
  query: `mutation updateAppBase($workspaceId: String!, $id: String!, $input: UpdateAppBaseInput!) {
  updateAppBase(workspaceId: $workspaceId, id: $id, input: $input) {
    id
    slug
    title
    config
    isPublished
    updatedAt
  }
}`,
};

export const updateDocDefaultRoleMutation = {
  id: 'updateDocDefaultRoleMutation' as const,
  op: 'updateDocDefaultRole',
  query: `mutation updateDocDefaultRole($input: UpdateDocDefaultRoleInput!) {
  updateDocDefaultRole(input: $input)
}`,
};

export const updateDocTitleMutation = {
  id: 'updateDocTitleMutation' as const,
  op: 'updateDocTitle',
  query: `mutation updateDocTitle($input: UpdateDocTitleInput!) {
  updateDocTitle(input: $input) {
    id
    workspaceId
    title
    updatedAt
  }
}`,
};

export const updateDocUserRoleMutation = {
  id: 'updateDocUserRoleMutation' as const,
  op: 'updateDocUserRole',
  query: `mutation updateDocUserRole($input: UpdateDocUserRoleInput!) {
  updateDocUserRole(input: $input)
}`,
};

export const updateLocaleSettingsMutation = {
  id: 'updateLocaleSettingsMutation' as const,
  op: 'updateLocaleSettings',
  query: `mutation updateLocaleSettings($input: UpdateLocaleSettingsInput!) {
  updateLocaleSettings(input: $input) {
    availableLocales
    defaultLocale
  }
}`,
};

export const updateSeatCountMutation = {
  id: 'updateSeatCountMutation' as const,
  op: 'updateSeatCount',
  query: `mutation updateSeatCount($workspaceId: ID!, $quantity: Int!, $withdrawalConsent: Boolean!) {
  updateSeatCount(
    workspaceId: $workspaceId
    quantity: $quantity
    withdrawalConsent: $withdrawalConsent
  ) {
    success
    addon {
      quantity
      status
      pricingTiersMonthly
      pricingTiersYearly
      totalMonthly
    }
  }
}`,
};

export const updateTagMutation = {
  id: 'updateTagMutation' as const,
  op: 'updateTag',
  query: `mutation updateTag($workspaceId: String!, $tagId: String!, $name: String, $color: String) {
  updateTag(workspaceId: $workspaceId, tagId: $tagId, name: $name, color: $color) {
    id
    workspaceId
    name
    color
    createdAt
    updatedAt
  }
}`,
};

export const updateTenantMemberRoleMutation = {
  id: 'updateTenantMemberRoleMutation' as const,
  op: 'updateTenantMemberRole',
  query: `mutation updateTenantMemberRole($tenantId: ID!, $userId: String!, $role: TenantRoleEnum!) {
  updateTenantMemberRole(tenantId: $tenantId, userId: $userId, role: $role) {
    id
    tenantId
    userId
    role
    createdAt
  }
}`,
};

export const updateTenantMutation = {
  id: 'updateTenantMutation' as const,
  op: 'updateTenant',
  query: `mutation updateTenant($id: ID!, $input: UpdateTenantInput!) {
  updateTenant(id: $id, input: $input) {
    id
    name
    slug
    billingEmail
    updatedAt
  }
}`,
};

export const updateUserProfileMutation = {
  id: 'updateUserProfileMutation' as const,
  op: 'updateUserProfile',
  query: `mutation updateUserProfile($input: UpdateUserInput!) {
  updateProfile(input: $input) {
    id
    name
  }
}`,
};

export const updateUserSettingsMutation = {
  id: 'updateUserSettingsMutation' as const,
  op: 'updateUserSettings',
  query: `mutation updateUserSettings($input: UpdateUserSettingsInput!) {
  updateSettings(input: $input)
}`,
};

export const updateWorkspacePolicyMutation = {
  id: 'updateWorkspacePolicyMutation' as const,
  op: 'updateWorkspacePolicy',
  query: `mutation updateWorkspacePolicy($workspaceId: String!, $input: UpdateWorkspacePolicyInput!) {
  updateWorkspacePolicy(workspaceId: $workspaceId, input: $input) {
    requireTOTP
    allowEmailOTP
    enforcedAt
    gracePeriodDays
    gracePeriodActive
    gracePeriodRemainingDays
    offlineAccessTtlHours
  }
}`,
};

export const uploadAvatarMutation = {
  id: 'uploadAvatarMutation' as const,
  op: 'uploadAvatar',
  query: `mutation uploadAvatar($workspaceId: String!, $avatar: Upload!) {
  uploadAvatar(workspaceId: $workspaceId, avatar: $avatar) {
    id
    name
    avatarUrl
    email
  }
}`,
  file: true,
  uploads: {"variables":{"avatar":{"type":"Upload","listDepth":0}},"inputs":{}},
};

export const verifyEmailMutation = {
  id: 'verifyEmailMutation' as const,
  op: 'verifyEmail',
  query: `mutation verifyEmail($token: String!) {
  verifyEmail(token: $token)
}`,
};

export const workspaceBlobQuotaQuery = {
  id: 'workspaceBlobQuotaQuery' as const,
  op: 'workspaceBlobQuota',
  query: `query workspaceBlobQuota($id: String!) {
  workspace(id: $id) {
    quota {
      blobLimit
      tusUploadEnabled
      tusUploadPath
      humanReadable {
        blobLimit
      }
    }
  }
}`,
};

export const getWorkspaceConfigQuery = {
  id: 'getWorkspaceConfigQuery' as const,
  op: 'getWorkspaceConfig',
  query: `query getWorkspaceConfig($id: String!) {
  workspace(id: $id) {
    enableAi
    enableUrlPreview
    enableDocEmbedding
    appBasesEnabled
    presenceDiscoveryEnabled
  }
}`,
};

export const workspaceCreationQuotaQuery = {
  id: 'workspaceCreationQuotaQuery' as const,
  op: 'workspaceCreationQuota',
  query: `query workspaceCreationQuota {
  workspaceCreationQuota {
    canCreate
    ownedCount
    limit
    plan
    upgradeAvailable
  }
}`,
};

export const workspaceCreditSubscriptionsQuery = {
  id: 'workspaceCreditSubscriptionsQuery' as const,
  op: 'workspaceCreditSubscriptions',
  query: `query workspaceCreditSubscriptions($workspaceId: String!) {
  workspaceCreditSubscriptions(workspaceId: $workspaceId) {
    id
    creditPackId
    creditPackName
    credits
    status
    cancelAtPeriodEnd
    currentPeriodEnd
    billingInterval
  }
}`,
};

export const setEnableAiMutation = {
  id: 'setEnableAiMutation' as const,
  op: 'setEnableAi',
  query: `mutation setEnableAi($id: ID!, $enableAi: Boolean!) {
  updateWorkspace(input: {id: $id, enableAi: $enableAi}) {
    id
  }
}`,
};

export const setAppBasesEnabledMutation = {
  id: 'setAppBasesEnabledMutation' as const,
  op: 'setAppBasesEnabled',
  query: `mutation setAppBasesEnabled($id: ID!, $appBasesEnabled: Boolean!) {
  updateWorkspace(input: {id: $id, appBasesEnabled: $appBasesEnabled}) {
    id
  }
}`,
};

export const setEnableDocEmbeddingMutation = {
  id: 'setEnableDocEmbeddingMutation' as const,
  op: 'setEnableDocEmbedding',
  query: `mutation setEnableDocEmbedding($id: ID!, $enableDocEmbedding: Boolean!) {
  updateWorkspace(input: {id: $id, enableDocEmbedding: $enableDocEmbedding}) {
    id
  }
}`,
};

export const setEnableUrlPreviewMutation = {
  id: 'setEnableUrlPreviewMutation' as const,
  op: 'setEnableUrlPreview',
  query: `mutation setEnableUrlPreview($id: ID!, $enableUrlPreview: Boolean!) {
  updateWorkspace(input: {id: $id, enableUrlPreview: $enableUrlPreview}) {
    id
  }
}`,
};

export const inviteByEmailsMutation = {
  id: 'inviteByEmailsMutation' as const,
  op: 'inviteByEmails',
  query: `mutation inviteByEmails($workspaceId: String!, $emails: [String!]!, $permission: Permission) {
  inviteMembers(
    workspaceId: $workspaceId
    emails: $emails
    permission: $permission
  ) {
    email
    inviteId
    sentSuccess
    error
  }
}`,
  deprecations: ["'sentSuccess' is deprecated: Notification will be sent asynchronously"],
};

export const acceptInviteByInviteIdMutation = {
  id: 'acceptInviteByInviteIdMutation' as const,
  op: 'acceptInviteByInviteId',
  query: `mutation acceptInviteByInviteId($workspaceId: String!, $inviteId: String!) {
  acceptInviteById(workspaceId: $workspaceId, inviteId: $inviteId)
}`,
};

export const getWorkspaceInviteLinkQuery = {
  id: 'getWorkspaceInviteLinkQuery' as const,
  op: 'getWorkspaceInviteLink',
  query: `query getWorkspaceInviteLink($id: String!) {
  workspace(id: $id) {
    inviteLink {
      link
      expireTime
    }
  }
}`,
};

export const createInviteLinkMutation = {
  id: 'createInviteLinkMutation' as const,
  op: 'createInviteLink',
  query: `mutation createInviteLink($workspaceId: String!, $expireTime: WorkspaceInviteLinkExpireTime!) {
  createInviteLink(workspaceId: $workspaceId, expireTime: $expireTime) {
    link
    expireTime
  }
}`,
};

export const revokeInviteLinkMutation = {
  id: 'revokeInviteLinkMutation' as const,
  op: 'revokeInviteLink',
  query: `mutation revokeInviteLink($workspaceId: String!) {
  revokeInviteLink(workspaceId: $workspaceId)
}`,
};

export const workspaceInvoicesQuery = {
  id: 'workspaceInvoicesQuery' as const,
  op: 'workspaceInvoices',
  query: `query workspaceInvoices($take: Int!, $skip: Int!, $workspaceId: String!) {
  workspace(id: $workspaceId) {
    invoiceCount
    invoices(take: $take, skip: $skip) {
      id
      status
      currency
      amount
      reason
      lastPaymentError
      link
      pdfUrl
      createdAt
    }
  }
}`,
};

export const workspaceMemberComplianceQuery = {
  id: 'workspaceMemberComplianceQuery' as const,
  op: 'workspaceMemberCompliance',
  query: `query workspaceMemberCompliance($workspaceId: String!) {
  workspaceMemberCompliance(workspaceId: $workspaceId) {
    userId
    email
    name
    avatarUrl
    hasTOTP
    hasEmailOTP
    role
  }
}`,
};

export const workspacePolicyQuery = {
  id: 'workspacePolicyQuery' as const,
  op: 'workspacePolicy',
  query: `query workspacePolicy($workspaceId: String!) {
  workspacePolicy(workspaceId: $workspaceId) {
    requireTOTP
    allowEmailOTP
    enforcedAt
    gracePeriodDays
    gracePeriodActive
    gracePeriodRemainingDays
    offlineAccessTtlHours
  }
}`,
};

export const setPresenceDiscoveryEnabledMutation = {
  id: 'setPresenceDiscoveryEnabledMutation' as const,
  op: 'setPresenceDiscoveryEnabled',
  query: `mutation setPresenceDiscoveryEnabled($id: ID!, $enabled: Boolean!) {
  updateWorkspace(input: {id: $id, presenceDiscoveryEnabled: $enabled}) {
    id
    presenceDiscoveryEnabled
  }
}`,
};

export const getWorkspacePresenceQuery = {
  id: 'getWorkspacePresenceQuery' as const,
  op: 'getWorkspacePresence',
  query: `query getWorkspacePresence($workspaceId: String!) {
  workspace(id: $workspaceId) {
    presence {
      docId
      userIds
    }
  }
}`,
};

export const workspacePublicHostQuery = {
  id: 'workspacePublicHostQuery' as const,
  op: 'workspacePublicHost',
  query: `query workspacePublicHost($workspaceId: String!) {
  workspacePublicHost(workspaceId: $workspaceId) {
    defaultHost
    brandedHost
    customDomain
    effectiveHost
    defaultUrl
    brandedUrl
    customDomainUrl
    effectiveUrl
  }
}`,
};

export const workspaceQuotaQuery = {
  id: 'workspaceQuotaQuery' as const,
  op: 'workspaceQuota',
  query: `query workspaceQuota($id: String!) {
  workspace(id: $id) {
    quota {
      name
      blobLimit
      storageQuota
      usedStorageQuota
      historyPeriod
      memberLimit
      memberCount
      overcapacityMemberCount
      automationCount
      guestLimit
      externalAccessPerDocLimit
      guestCount
      guestMinTtlHours
      guestMaxTtlHours
      guestDefaultTtlHours
      sttBatch {
        maxMib
        maxMinutes
        upgradePlan
        upgradeMaxMib
      }
      humanReadable {
        name
        blobLimit
        storageQuota
        historyPeriod
        memberLimit
        memberCount
        overcapacityMemberCount
        automationCount
      }
    }
  }
}`,
};

export const getWorkspaceRolePermissionsQuery = {
  id: 'getWorkspaceRolePermissionsQuery' as const,
  op: 'getWorkspaceRolePermissions',
  query: `query getWorkspaceRolePermissions($id: String!) {
  workspaceRolePermissions(id: $id) {
    permissions {
      Workspace_Automation
      Workspace_Automation_Create
      Workspace_Automation_Delete
      Workspace_Automation_Edit
      Workspace_Automation_Execute
      Workspace_Automation_View
      Workspace_Administrators_Manage
      Workspace_Blobs_List
      Workspace_Blobs_Read
      Workspace_Blobs_Write
      Workspace_Copilot
      Workspace_CreateDoc
      Workspace_Delete
      Workspace_ExpertBots
      Workspace_KnowledgeGraph
      Workspace_Organize_Read
      Workspace_Payment_Manage
      Workspace_PersonalMemory
      Workspace_Properties_Create
      Workspace_Properties_Delete
      Workspace_Properties_Read
      Workspace_Properties_Update
      Workspace_Read
      Workspace_Settings_Read
      Workspace_Settings_Update
      Workspace_AppBases_Create
      Workspace_AppBases_Delete
      Workspace_AppBases_Edit
      Workspace_AppBases_Import
      Workspace_AppBases_Publish
      Workspace_AppBases_View
      Workspace_Sync
      Workspace_TransferOwner
      Workspace_Users_Manage
      Workspace_Users_Read
      Workspace_Workflow_Manage
    }
  }
}`,
  deprecations: ["'workspaceRolePermissions' is deprecated: use WorkspaceType[permissions] instead"],
};

export const workspaceRunningOperationsQuery = {
  id: 'workspaceRunningOperationsQuery' as const,
  op: 'workspaceRunningOperations',
  query: `query workspaceRunningOperations($workspaceId: String!) {
  workspaceRunningOperations(workspaceId: $workspaceId) {
    suspendedExecutionId
    workflowExecutionId
    executionLogId
    automationConfigId
    automationTitle
    docId
    docTitle
    blockId
    kind
    status
    jobId
    submittedAt
    expiresAt
  }
}`,
};

export const workspaceSearchQuery = {
  id: 'workspaceSearchQuery' as const,
  op: 'workspaceSearch',
  query: `query workspaceSearch($id: String!, $input: WorkspaceSearchInput!) {
  workspace(id: $id) {
    workspaceSearch(input: $input) {
      id
      kind
      visibility
      title
      subtitle
      snippet
      score
      createdAt
      updatedAt
      receivedAt
      mailMessageId
      mailAccountId
      mailFolderId
      mailFrom
      mailTo
      mailSubject
      attachmentHits {
        filename
        mimeType
        source
        snippet
        pageIndex
        coverageStatus
      }
      openTarget {
        kind
        workspaceId
        route
        mailMessageId
        mailAccountId
        mailFolderId
      }
    }
  }
}`,
};

export const mailSearchDiagnosticsQuery = {
  id: 'mailSearchDiagnosticsQuery' as const,
  op: 'mailSearchDiagnostics',
  query: `query mailSearchDiagnostics($id: String!, $input: MailSearchDiagnosticsInput) {
  workspace(id: $id) {
    mailSearchDiagnostics(input: $input) {
      scopeStatus
      missingPermissionTargets
      totalMessages
      messagesMissingBody
      totalAttachments
      totalPdfAttachments
      pdfAttachmentsIndexed
      pdfAttachmentsMissingOcr
      pdfAttachmentsStale
      pdfAttachmentsFailed
      pdfAttachmentsRepairable
      messages {
        messageId
        accountId
        folderId
        folder
        receivedAt
        bodyStatus
        attachmentStatus
        ocrStatus
        attachmentCount
        pdfAttachmentCount
        indexedPdfPageCount
        missingOcrAttachmentCount
        stalePdfAttachmentCount
        failedPdfAttachmentCount
      }
    }
  }
}`,
};

export const reindexMailSearchMutation = {
  id: 'reindexMailSearchMutation' as const,
  op: 'reindexMailSearch',
  query: `mutation reindexMailSearch($input: MailSearchReindexInput!) {
  reindexMailSearch(input: $input) {
    processedMessages
    processedAttachments
    processedTargets
    indexedPages
    failedTargets
    missingPermissionTargets
    fallbackDocReindexCount
    errors {
      messageId
      attachmentId
      blobKey
      status
      message
    }
  }
}`,
};

export const workspaceSeatAddonQuery = {
  id: 'workspaceSeatAddonQuery' as const,
  op: 'workspaceSeatAddon',
  query: `query workspaceSeatAddon($workspaceId: ID!) {
  workspaceSeatAddon(workspaceId: $workspaceId) {
    quantity
    status
    pricingTiersMonthly
    pricingTiersYearly
    totalMonthly
    totalYearly
    nextBillingDate
    recurring
  }
}`,
};

export const workspaceTagsQuery = {
  id: 'workspaceTagsQuery' as const,
  op: 'workspaceTags',
  query: `query workspaceTags($workspaceId: String!) {
  workspaceTags(workspaceId: $workspaceId) {
    id
    workspaceId
    name
    color
    createdAt
    updatedAt
  }
}`,
};

export const approveWorkspaceTeamMemberMutation = {
  id: 'approveWorkspaceTeamMemberMutation' as const,
  op: 'approveWorkspaceTeamMember',
  query: `mutation approveWorkspaceTeamMember($workspaceId: String!, $userId: String!) {
  approveMember(workspaceId: $workspaceId, userId: $userId)
}`,
};

export const grantWorkspaceTeamMemberMutation = {
  id: 'grantWorkspaceTeamMemberMutation' as const,
  op: 'grantWorkspaceTeamMember',
  query: `mutation grantWorkspaceTeamMember($workspaceId: String!, $userId: String!, $permission: Permission!) {
  grantMember(workspaceId: $workspaceId, userId: $userId, permission: $permission)
}`,
};

export const transferWorkspaceOwnershipMutation = {
  id: 'transferWorkspaceOwnershipMutation' as const,
  op: 'transferWorkspaceOwnership',
  query: `mutation transferWorkspaceOwnership($workspaceId: String!, $userId: String!) {
  transferWorkspaceOwnership(workspaceId: $workspaceId, userId: $userId)
}`,
};
